import {
  FC,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useCallback,
  useState,
} from 'react';
import { observer } from 'mobx-react';
import { useStore } from '~/stores';
import { playbackStore } from '~/stores/playback.store';
import {
  detectLoopPattern,
  computeLoopPosition,
  computeCampaignStats,
} from '~/services/playback/loopPattern';
import playbackStatusService from '~/services/playback-status/PlaybackStatusService';
import { resolveReportCadence } from '~/services/playback-status/reportCadence';
import { useTVRemote } from '~/utils/hooks/useTVRemote';
import {
  usePlayableUrl,
  resolvePlayableUrl,
  applyMediaCrossOrigin,
  mediaCrossOriginProp,
} from '~/utils/hooks/usePlayableUrl';
import { useDeviceInfo } from '~/utils/hooks/useDeviceInfo';
import { useOrientation } from '~/utils/hooks/useOrientation';
import { useEffectiveFitMode } from '~/utils/hooks/useEffectiveFitMode';
import { useRecycleBoot } from '~/utils/hooks/useRecycleBoot';
import { markBoot, flushBootTiming } from '~/utils/bootTiming';
import { ENV_FIT_MODE } from '~/utils/viewportFit';
import { useBatteryStatus } from '../../hooks/useBatteryStatus';
import { BrightnessService } from '~/services/brightness/BrightnessService';
import { VolumeService } from '~/services/volume/VolumeService';
// V059 — filter de janela por conteúdo (mesmo usado em scheduler.store).
import { isContentPlayable } from '~/services/scheduler/utils/contentScheduleFilter';
import { isGroupSchedulePlayable } from '~/services/scheduler/utils/groupScheduleFilter';
import NoContent, { NoContentReason } from '~/components/NoContent';
import VideoPreloader from '~/components/VideoPreloader';
import { getCurrentCacheType } from '~/services/media/cacheFactory';
import { usePlatform, ScreenOrientation } from '~/services/platform';
import analyticsService from '~/services/analytics/AnalyticsService';
import { otaLog } from '~/services/ota/OtaDebugLogger';
import {
  getRecentCrashes,
  clearCrashes,
  setHeartbeatContext,
  setHeartbeatVideoState,
} from '~/services/logging/crashCapture';
import InteractiveElementLayer from '~/components/InteractiveElementLayer';
import { interactiveElementsStore } from '~/stores/interactiveElements.store';
import { lastFrameCache } from '~/utils/lastFrameCache';
// last-frame WebCodecs (2026-07-16) — a própria TV gera o ÚLTIMO frame de cada
// vídeo via WebCodecs (software decode, escapa do HW overlay) pra cobrir a
// transição que SAI de um vídeo (paridade com o `lastFrameCache` nativo do
// Android): mostra o frame que já estava na tela → zero preto no fim + a troca
// vira corte de conteúdo → mascara o "soco" de qualidade canvas×vídeo.
import { lastFrameWcCache, ensureLastFrameWc } from './videoPoster';
import ZonePlayer from './ZonePlayer';
import PlaybackProgressBar from './PlaybackProgressBar';
// BACKEND AVPLAY (Fase 1, flag VITE_AVPLAY_BACKEND, default OFF) — só Tizen, slot
// fullscreen de vídeo. Fallback runtime pro <video> via onFallback. Doc: FEATURE-AVPLAY-TIZEN.md.
import { AvplayVideo, type AvplaySyncHandle } from './AvplayVideo';
import {
  AVPLAY_RECYCLE_ENABLED,
  shouldRecycleAvplay,
  noteAvplayRecycle,
  avplayOpensSinceRecycle,
} from '~/services/avplay/tizenAvplay';
import {
  getSourceLabel,
  getOrientationLabel,
  looksProvisionalWebViewDuration,
  resolveTrustedNaturalDuration,
  resolveOverrideLoopDecision,
  videoPlayedEnoughForCutShortAdvance,
  activeZonesForCampaign,
  zoneGroupMaxDurationSec,
  deveCapturarCapaNaHora,
  deveRepetirNativamente,
  deveSoltarStandbyNoGatilho,
  getFreezeCandidateName,
  shouldHideVideoForFreeze,
  shouldHoldVideoFrameForImage,
  shouldDeferNoContentForBoot,
  shouldEmitSlotStarted,
  slotExhibitionKey,
  computeAdvanceWatchdogTimeoutMs,
  shouldAdvanceWatchdogFire,
  isLoadKickPlatform,
  isAndroidWebView,
  shouldLoadKickFire,
  resolveZoneAwareFreezeCandidate,
  shouldSkipDomCoverOnVideoExit,
  computeStretchRect,
  isVideoReadbackAllowedForPlatform,
  shouldReuseDecodedImageOnLoop,
  shouldSkipHeldCanvasCapture,
  shouldRetryImageOnce,
  refreshCorsCacheEntry,
  resolveSyncPulseGate,
  shouldLetItemFinish,
  resolveSyncPlaybackRate,
  shownRatio,
  resolveSeekPlan,
  shouldPreloadNextVideo,
  shouldRetryVideoOpen,
  shouldRunStallRecovery,
} from './playbackHelpers';
import {
  isScheduledEngineEnabled,
  resolveNextSwitch,
  SCHEDULED_PREPARE_LEAD_MS,
  SCHEDULED_SWITCH_COARSE_LEAD_MS,
} from '~/services/playback/scheduledEngine';
import { useSegundoDecoder } from '~/services/playback/useSegundoDecoder';
import {
  criarZeladorDeDecoder,
  soltarDecoderDoVideo,
} from '~/services/playback/decoderJanitor';
import {
  captureVideoFrameJpeg,
  drawVideoFrameToCanvas,
} from '~/utils/captureVideoFrame';
import { serverNow } from '~/services/time/serverClock';
import {
  resolveLoopTarget,
  paintLeadMs,
  avplayEntryLeadMs,
} from '~/services/playback/loopSync';
import type { StreamMediaItem } from '~/stores/stream.store';
import type { PlaylistItem } from '~/services/scheduler/types';
import { resolveApiBaseUrl } from '~/services/embedded/hostConfig';
import {
  Container,
  TouchInterceptor,
  VideoPlayer,
  ImagePlayer,
  LoadingOverlay,
  LoadingSpinner,
  LoadingText,
  ServerUrlInfo,
  BlackTransition,
  ErrorOverlay,
  ErrorText,
  MediaInfo,
  LoopInfoPanel,
  CrashErrorBanner,
  OrientationButtons,
  OrientationButton,
  AutoBadge,
  SpeedControls,
  SpeedSliderContainer,
  SpeedSlider,
  SpeedValue,
  OfflineBadge,
  FullscreenButton,
  ContentViewport,
} from './styles';

/**
 * Landscape icon for orientation selector.
 */
const LandscapeIcon: FC = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M21 5H3c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm-2 12H5V7h14v10z" />
  </svg>
);

/**
 * Portrait icon for orientation selector.
 */
const PortraitIcon: FC = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M17 1H7c-1.1 0-2 .9-2 2v18c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2V3c0-1.1-.9-2-2-2zm-2 18H9V5h6v14z" />
  </svg>
);

/**
 * Fullscreen enter icon.
 */
const FullscreenEnterIcon: FC = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z" />
  </svg>
);

/**
 * Fullscreen exit icon.
 */
const FullscreenExitIcon: FC = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z" />
  </svg>
);

/**
 * Maps playback source to display label.
 */
const AUTO_HIDE_DELAY_MS = 4000;
const FULLSCREEN_BUTTON_HIDE_DELAY_MS = 3000;
/**
 * Tab A7 / Android WebView: OMX precisa de um beat entre liberar o decoder
 * do vídeo anterior e configurar o próximo (especialmente 1080p).
 * Sem isso, logcat mostra "Set Resolution failed" / error -12 → tela preta.
 *
 * v1.0.163 (Renato + Claude, 2026-06-08): aumentado 350ms → 600ms após
 * diagnóstico no Tab A7 SM-T505 com 180min de uso. Sintoma: gfxinfo mostrava
 * 10 frames de ~4950ms (freezes visíveis), Janky 1.81%, frames>100ms = 53.
 * Logcat revelou Android caindo do HW decoder (OMX.qcom.video.decoder.avc)
 * pro software (c2.android.avc.decoder) — release de 350ms colidia com
 * o readback Canvas Bridge (NdkImageReader). HW marcado como instável,
 * fallback pro software, CPU sobe 76%, thermal sustentado 54°C, freezes
 * acumulativos. 600ms dá folga suficiente. Combinado com await readback
 * (vide handleVideoEnded), elimina a race.
 *
 * v1.0.166 (DESCARTADO 2026-06-09 ~02:00): aumentado 600ms → 1200ms numa
 * tentativa de resolver MediaCodec error -12. NÃO RESOLVEU — o erro
 * permanece em qualquer delay porque a causa raiz é OUTRA: o servidor
 * FFmpeg (efflux-server/.../FFmpegService.java) força TODO vídeo pra
 * H.264 High Profile Level 4.1 + 2 B-frames, e o vídeo Samsung-Galaxy
 * com bitrate 4.16 Mbps tinha combinação não suportada pelo Adreno 610
 * (Snapdragon 662 / Tab A7). Provada via ffprobe: vídeo já vinha High@4.1
 * do server. Subir manualmente Main@4.0 não adiantava — server
 * re-encodava de volta. Fix real exige mudar FFmpegService server-side
 * (mudar pra `main` + `4.0` + `-bf 0` + reprocessar conteúdos), feito
 * em sessão separada (não nessa madrugada).
 *
 * v1.0.167 (Renato, 2026-06-09): rollback 1200ms → 600ms (igual v1.0.163).
 * Renato observou em campo que 1200ms gerava gap MUITO visível entre
 * vídeos. Como o delay maior não resolvia o problema de raiz (que é o
 * profile do server), o trade-off ficou ruim. Volta pra 600ms (ainda
 * resolve a race original com Canvas Bridge — vide v1.0.163).
 */
/** Tab A7: após surface reconnect (~7s pós-canPlay) o OMX libera decoder. */
const ANDROID_SURFACE_RECOVERY_MS = 1500;
const KEY_H = 72;
const CONFIG_KEY = 'efflux_player_config';

/**
 * Feature flag — Cache Diag panel (🔬 + 🗑️ Removidos).
 *
 * Desativado em v1.0.83 após estabilização do cache em produção
 * (Renato, 2026-05-30). Trocar pra `true` ativa o painel completo
 * de telemetria (backend, attempts, últimas ops, removidos) — útil
 * em troubleshooting de cache.
 *
 * Bump version + build + OTA pra distribuir a versão "diagnóstica".
 * Toda a telemetria (mediaStore.cacheStoreAttempts etc) continua
 * acumulando em background — só a renderização visual é gated.
 */
const SHOW_CACHE_DIAG = false;

/**
 * Libera decoder OMX no Android antes de carregar o próximo src.
 *
 * 2026-07-03 (Renato): REMOVIDO o delay de 600ms. Era legado de um problema
 * de vídeo alta-resolução que o tablet não dava conta — resolvido de outra
 * forma. O sleep só somava tela preta na transição (o freeze canvas cobre,
 * mas quando a captura falha o preto de 600ms ficava visível). Mantém só o
 * teardown instantâneo (pause/removeAttribute/load) que garante swap limpo
 * do decoder; o yield de microtask deixa o load() commitar antes do novo src.
 */
async function releaseAndroidVideoDecoder(
  video: HTMLVideoElement,
): Promise<void> {
  video.pause();
  video.removeAttribute('src');
  video.load();
  await Promise.resolve();
}

/** SEAMLESS→VÍDEO (2026-07-16): teto (ms) que o freeze pode ficar visível após o
 *  evento 'playing' esperando o `requestVideoFrameCallback` do 1º frame. Fallback
 *  de segurança: se o rVFC nunca disparar, o freeze é escondido mesmo assim (nunca
 *  fica preso). Faixa recomendada 500–800ms; o freeze mostra o frame anterior
 *  (não preto) durante essa janela → sempre ≤ preto que hoje. */
const HIDE_FREEZE_SAFETY_MS = 700;

/** SEAMLESS→VÍDEO (Tizen/webOS, 2026-07-21): teto (ms) do freeze na TV. Na Tizen o
 *  plano de vídeo é HW overlay — o `requestVideoFrameCallback` NÃO dispara nele e não
 *  há sinal confiável de "1º frame composto" (readback volta preto, currentTime avança
 *  ANTES da composição). Então o hide EFETIVO na TV é este timeout wall-clock. Os 700ms
 *  eram curtos pra vídeo PESADO (compõe o 1º frame depois) → preto na entrada (bug
 *  "paisagem"). A capa mostra o ÚLTIMO frame VÁLIDO (não preto), então segurar mais custa
 *  só leve "atualidade" em vídeo leve; zero preto no pesado. Tunável pra calibrar na
 *  bancada sem rebuild de lógica. Android/browser seguem em HIDE_FREEZE_SAFETY_MS + rVFC. */
const HIDE_FREEZE_TV_MS =
  Number(import.meta.env.VITE_HIDE_FREEZE_TV_MS) || 2000;

/** PHASE 1 — HOLD-FRAME (Tizen/webOS, video→img): atraso (ms) entre ESCONDER o
 *  `<video>` segurado (visibility:hidden — revela a imagem, hide limpo sem flash) e
 *  DESMONTÁ-lo. Curto de propósito: a imagem já está visível quando escondemos o
 *  vídeo; o delay só garante que o `visibility:hidden` commitou 1 frame antes do
 *  unmount, pra o tear-down do HW overlay nunca aparecer. */
const HELD_VIDEO_UNMOUNT_DELAY_MS = 200;

/** SEAMLESS vídeo→imagem (Tizen/webOS, 2026-07-21): settle (ms) EXTRA entre a imagem
 *  de destino reportar pronta (decode()+2rAF) e ESCONDER o `<video>` segurado. Os 2 rAF
 *  (~32ms) bastam no Consumer (Tizen 9) mas NÃO no compositor mais lento da SSSP (6.5):
 *  o overlay HW do vídeo cai ~1 frame antes da imagem compor → preto bem curto. Este
 *  settle mantém o ÚLTIMO frame do vídeo na tela um tico a mais (garante a imagem
 *  composta antes do teardown do overlay). NÃO atrasa o load da imagem — ela já está
 *  pronta; só adia o REVEAL (o frame do vídeo cobre). Tunável pra calibrar o mínimo na
 *  SSSP. Só afeta o caminho held-video (Tizen/webOS); Android/browser não usam. */
const HELD_VIDEO_REVEAL_SETTLE_MS =
  Number(import.meta.env.VITE_HELD_VIDEO_REVEAL_SETTLE_MS) || 200;

/** BACKEND AVPLAY (Fase 1) — flag de build. Default OFF → caminho `<video>` de hoje
 *  (prod intocada, WGT novo a qualquer hora). ON só na bancada/validação. */
const AVPLAY_BACKEND_FLAG = import.meta.env.VITE_AVPLAY_BACKEND === 'true';

/** Pulso de sincronia: de quanto em quanto tempo a posição no loop é reavaliada. */
const SYNC_PULSE_INTERVAL_MS = 2_000;
/**
 * Carência antes de PULAR um vídeo que falhou ao abrir (vide shouldRetryVideoOpen). Medido no
 * tablet: o hook re-resolve a URL e o efeito reaplica o src sozinho; o segundo load dá
 * canPlay ~190 ms após o erro. Reabrir por conta própria nesse meio-tempo derrubava o
 * load que já tinha dado certo — então aqui só se ESPERA e confere.
 */
const VIDEO_OPEN_ERROR_GRACE_MS = 500;
/** Espera após uma correção — evita corrigir em rajada enquanto a mídia assenta. */
const SYNC_PULSE_COOLDOWN_MS = 2_500;
/** Erro mínimo pra dar seek num vídeo. Abaixo disto o seek incomoda mais que o desvio. */
const SYNC_PULSE_VIDEO_TOLERANCE_S = 1.5;
/** Erro mínimo pra re-armar o avanço da imagem. Invisível ao operador, tolera menos. */
const SYNC_PULSE_IMAGE_TOLERANCE_S = 0.4;
/**
 * Erro mínimo pra dar seek ao ENTRAR num vídeo.
 *
 * <p>Era 0,5s. Seek em `<video>` custa caro: o decoder descarta o buffer e reabre, e isso
 * aparece como engasgo na troca. Com a compensação de pintura o alvo ficou ~0,2s (tablet/
 * navegador) a ~1,3s (TV) mais adiante, então esse limiar passou a ser cruzado em QUASE toda
 * entrada de vídeo — troca menos fluida, relatado na bancada 2026-09-14. Acima de 1s o desvio
 * incomoda mais que o engasgo; abaixo, não compensa: o pulso e o slot seguinte absorvem.</p>
 */
const VIDEO_ENTRY_SEEK_MIN_S = 1.0;
/**
 * Folga ao redor do slot atual antes de o pulso PULAR de item. Sem ela, a borda de cada
 * slot (onde o alvo já é o próximo item) dispararia um pulo, cortando a troca natural e
 * piscando preto a cada transição.
 */
const SYNC_PULSE_HOP_MIN_S = 2.0;
/**
 * Quanto falta pro fim do item para o pulso NÃO pular e deixar terminar sozinho.
 *
 * <p>O pulo arrancava o item mesmo faltando um piscar: o `ended` não disparava e a
 * impressão (`playback_complete`) nunca nascia — player sob sync canônico gerava ZERO
 * impressão (medido em prod 2026-09-29, `EMBEDDED-B4AF3DE5BBB8A740`). Só adia quando o
 * alvo do pulso JÁ É o próximo item, então terminar naturalmente chega no MESMO destino.</p>
 *
 * <p>Cabe com folga na tolerância que o sync já aceita dentro do item
 * ({@link SYNC_PULSE_VIDEO_TOLERANCE_S} = 1,5s), logo não afrouxa o sincronismo.</p>
 */
const HOP_LET_FINISH_MAX_S = 0.5;
/**
 * Fração do item que precisa ter sido exibida para o corte virar impressão
 * (decisão Renato, 2026-09-30: conta como impressão cheia; limiar parametrizado em 50%).
 *
 * <p>Abaixo disso o pulso corta sem reportar — exibição curta demais pra valer entrega.</p>
 */
const MIN_SHOWN_RATIO_FOR_IMPRESSION = 0.5;
/** Erro mínimo pra saltar no AVPlay. Maior que o do <video>: salto no plano HW aparece mais. */
const SYNC_PULSE_AVPLAY_TOLERANCE_S = 1.5;

/**
 * CORREÇÃO FINA POR VELOCIDADE (em vez de seek).
 *
 * <p>Abaixo de {@link SYNC_PULSE_VIDEO_TOLERANCE_S} o pulso não fazia nada: corrigir com seek
 * um erro de algumas centenas de ms custa mais do que o erro — o seek re-buferiza, pode
 * re-inicializar o decoder e aparece como engasgo. Então a deriva pequena ficava lá, e é
 * exatamente ela que se vê como "milissegundos de atraso entre um player e outro".</p>
 *
 * <p>A saída é trocar o instrumento: em vez de saltar, muda-se a VELOCIDADE por alguns
 * segundos. ±2% é imperceptível (o ouvido humano detecta alteração de pitch por volta de 5%,
 * e aqui o conteúdo é majoritariamente sem fala) e recupera ~200ms a cada 10s de slot. O seek
 * continua existindo para erro grande — rate não daria conta e demoraria demais.</p>
 *
 * <p>Zona morta de 80ms: abaixo disso não se mexe, senão o player fica oscilando em torno do
 * alvo (o próprio relógio tem ruído maior que isso).</p>
 *
 * <p>🔴 <b>ESCOPO: Android, web e embedded — NÃO a Tizen.</b> O bloco que usa estas constantes
 * fica DEPOIS do gate do AVPlay, e o AVPlay sai antes por {@link AVPLAY_IN_ITEM_SEEK_ENABLED}
 * = false. Ou seja, a TV (que é justamente a plataforma com a pior deriva histórica) segue sem
 * correção nenhuma abaixo de {@link SYNC_PULSE_AVPLAY_TOLERANCE_S}. Isso é intencional, não
 * esquecimento: o seek repetido no AVPlay entra em laço (vide a nota da própria flag).</p>
 *
 * <p>Levar o ajuste fino pra Tizen exigiria velocidade no plano HW, e o
 * {@code AvplaySyncHandle} só expõe {@code currentSeconds}/{@code seekTo}. O
 * {@code tizen.AVPlay.setSpeed} nativo existe, mas é <i>trick play</i> (2×/4×/-2×) — não há
 * garantia de aceitar 1,02×. É spike próprio, não extensão deste código.</p>
 */
const SYNC_RATE_DEADBAND_S = 0.08;
/** Teto do ajuste de velocidade (fração). 0.02 = ±2%. */
const SYNC_RATE_MAX_ADJ = 0.02;
/** Em quantos segundos o ajuste tenta zerar o erro (constante de tempo). */
const SYNC_RATE_RECOVER_S = 10;
/**
 * HISTERESE PÓS-SEEK. Logo depois de um seek o vídeo re-bufferiza e o erro medido no pulso
 * seguinte é o custo do PRÓPRIO seek — com a tolerância normal, isso disparava outro seek,
 * e outro, a cada 2,5s ("travamento" medido no tablet). Por esta janela a tolerância dobra.
 */
const SYNC_PULSE_POST_SEEK_TOLERANCE_S = 3.0;
const SYNC_PULSE_POST_SEEK_WINDOW_MS = 10_000;
/**
 * COVER NA 1ª EXIBIÇÃO. O último frame só era capturado no `ended`, então a 1ª saída de
 * cada vídeo (todo o 1º loop pós-boot) ficava sem cover → preto. Agora, só na 1ª
 * exibição, captura um frame PROVISÓRIO a esta distância do fim; o `ended` grava o
 * definitivo por cima. "Voltar" 0,4s é invisível; preto não é.
 */
const FIRST_PLAY_FRAME_CAPTURE_LEAD_S = 0.4;
/**
 * Erro mínimo pra saltar já na ENTRADA do vídeo no AVPlay. Menor que a tolerância do pulso:
 * aqui o salto acontece antes de o operador ver o quadro se firmar, então quase não aparece —
 * e é o que tira o atraso herdado do tempo de abrir/preparar o plano HW.
 */
const AVPLAY_ENTRY_SYNC_TOLERANCE_S = 0.4;
/**
 * 🔴 DESLIGADO — correção REPETIDA, dentro do item, feita pelo pulso a cada 2s.
 *
 * <p>Entra em laço: após o `seekTo`, o `currentTime` do plano HW não reflete o salto de
 * imediato, então a verificação seguinte vê o mesmo erro e salta de novo — na tela, o
 * vídeo fica voltando sem parar (observado na TV, 2026-09-14). Imagem e escolha de item
 * NÃO dependem disto e seguem sincronizando normalmente na TV.</p>
 *
 * <p>Reabrir só com: leitura de posição confiável pós-seek (ou espera do evento de seek
 * concluído) + teste longo de estabilidade — o ciclo de vida do AVPlay tem histórico de
 * crash nativo nesta plataforma.</p>
 */
const AVPLAY_IN_ITEM_SEEK_ENABLED = false;
/**
 * ✅ LIGADO — sync de ENTRADA, UMA vez por item, no 1º quadro do AVPlay.
 *
 * <p>Era a MESMA flag da correção repetida acima, e por isso nasceu desligado junto com
 * ela (commit 15c13cb, 2026-09-14) — nunca rodou em produção. Mas o laço descrito acima é
 * uma propriedade do pulso, que RE-LÊ a posição a cada 2s e volta a saltar: aqui o salto
 * acontece uma vez só, no `handleAvplayFirstFrame`, e não há segunda leitura que possa
 * realimentá-lo.</p>
 *
 * <p>Sem ele a TV herda, pelo slot inteiro, o tempo de abrir/preparar o plano HW. Medido
 * na bancada em 2026-09-30, com relógio corrigido pelo `sync_beat` (a TV estava 16,3s
 * fora): a TV mostrava o MESMO conteúdo 2,64s depois do navegador (n=7) e 2,09s depois do
 * tablet (n=15), dos quais 1,13s (n=15) são decisão→1º quadro. O resíduo é o que este
 * sync de entrada existe pra tirar.</p>
 *
 * <p>Continua valendo o histórico de crash nativo do AVPlay — por isso o caminho REPETIDO
 * segue desligado e este entra sozinho, sob observação longa na bancada.</p>
 */
const AVPLAY_ENTRY_SEEK_ENABLED = true;

const Stream: FC = observer(() => {
  const {
    streamStore,
    syncStore,
    authStore,
    schedulerStore,
    otaStore,
    mediaStore,
    triggerStore,
    kioskStore,
  } = useStore();
  const platform = usePlatform();
  // MATCH DE COR/VIVIDEZ DO PLANO DE VÍDEO (Renato 2026-07-16): o freeze canvas vive
  // no plano GRÁFICO (DOM), que a TV renderiza CRU — enquanto o <video> vive no plano
  // HW overlay, que recebe o processamento de imagem da Samsung (saturação, contraste,
  // realce/nitidez). Medido: o frame decodado é PIXEL-PERFEITO
  // (none==default, bt709, nítido) → a diferença "borrado/cor apagada" é 100% do
  // display, não do decode. Pré-compensamos com filtro CSS (GPU-composited, barato)
  // SÓ em Tizen/webOS, pra o canvas imitar o realce do plano de vídeo. Android/browser
  // não têm essa dualidade de planos (freeze via <video> readback no mesmo plano) → 'none'.
  // Tunável via VITE_FREEZE_FILTER (calibração final = olho na TV; varia c/ picture mode).
  // A capa cobre um FRAME DE VÍDEO (lastFrameWc)? Só então o filtro de match do
  // plano de vídeo se aplica. Pra capa de IMAGEM (heldCanvas/nextImage) a imagem
  // vive no MESMO plano gráfico do canvas (sem realce HW) → filtro 'none', senão a
  // capa fica mais saturada/contrastada que a <img> normal = pulo de cor no
  // image→X (Renato 2026-07-21). Setado nos sites de desenho da capa.
  const [freezeCoverIsVideo, setFreezeCoverIsVideo] = useState(false);
  const freezeCanvasFilter =
    platform.isTV() && freezeCoverIsVideo
      ? String(
          import.meta.env.VITE_FREEZE_FILTER ||
            'saturate(1.3) contrast(1.12) brightness(1.03)',
        )
      : 'none';
  // EARLY FREEZE (Renato 2026-07-16): antecedência (s) com que o freeze é mostrado
  // ANTES do fim do vídeo em Tizen/webOS, pra cobrir o tear-down do HW overlay sem
  // preto. Tunável via VITE_EARLY_FREEZE_LEAD_MS (default 300ms). timeupdate na Tizen
  // dispara ~4×/s → lead ≥ ~300ms garante que pega a janela final.
  const earlyFreezeLeadS =
    (Number(import.meta.env.VITE_EARLY_FREEZE_LEAD_MS) || 130) / 1000;
  // PHASE 1.1 — EARLY HOLD (SSSP video→img): antecedência (s) com que o `<video>` é
  // pausado ANTES do fim pra segurar um frame MID-STREAM (o overlay de HW segura o
  // frame; pausar no `ended` seria tarde → flash preto). Lead maior que o do
  // early-freeze (o timeupdate ~4×/s precisa pegar a janela com folga; margem antes
  // do teardown do decoder). Tunável via VITE_EARLY_HOLD_LEAD_MS (default 400ms).
  const earlyHoldLeadS =
    (Number(import.meta.env.VITE_EARLY_HOLD_LEAD_MS) || 400) / 1000;
  // v1.0.190 (Fase 2 — feature flag fit mode): object-fit dinâmico em
  // VideoPlayer/ImagePlayer baseado no modo efetivo (ENV + override kiosk OFF).
  const effectiveFitMode = useEffectiveFitMode();
  const { batteryStatus, isSupported: isBatterySupported } = useBatteryStatus();
  const [currentVolume, setCurrentVolume] = useState<number | null>(null);
  const [defaultVolume, setDefaultVolume] = useState<number | null>(null);
  const [hardwareBrightness, setHardwareBrightness] = useState<number | null>(
    null,
  );
  // Polling do overlay CSS brightness — sem isso, getOverlayBrightness() é
  // chamado direto no render mas o React não re-renderiza quando o
  // setBrightness do BrightnessService altera overlay.style.opacity.
  // Resultado visível em Tizen Consumer (onde brightness usa overlay CSS):
  // painel mostrava sempre "100% (overlay)" mesmo com a TV física
  // corretamente escurecida pelo overlay.
  const [overlayBrightnessState, setOverlayBrightnessState] = useState<
    number | null
  >(null);

  // Single video player ref
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const volumeServiceRef = useRef(VolumeService.getInstance());

  // 2026-05-18 — proteção contra onEnded prematuro do WebView Android.
  // Capacitor/Chromium Android às vezes dispara `ended` antes do vídeo
  // terminar de fato (~3s em MP4 com moov atom no fim, codec H.265, etc).
  // Quando detectamos isso, tentamos 1× retomar antes de avançar pro
  // próximo. Por mídia — reseta quando currentMedia muda.
  const prematureEndedRetryRef = useRef<{
    mediaId: string | null;
    attempted: boolean;
  }>({
    mediaId: null,
    attempted: false,
  });

  // 2026-06-06 — polyfill pro evento `ended` que o WebKit Tizen 9.0
  // silencia quando vídeo natural (sem duration override) chega
  // currentTime ≥ duration. Sintoma observado em campo (Renato): "Pepsi
  // Best Moment" 30s travou em 30/30, currentTime estagnado, sem
  // disparar onEnded. Bug é DIFERENTE do prematureEnded (Android dispara
  // cedo) — aqui o evento simplesmente nunca dispara. NÃO é watchdog
  // genérico: só ativa quando há evidência concreta (currentTime ≥
  // duration && !paused && !ended por >1.5s).
  const endStuckRef = useRef<{
    videoSrc: string | null;
    firstSeenAt: number;
  }>({
    videoSrc: null,
    firstSeenAt: 0,
  });

  // 2026-06-08 — polyfill cut-short quando WebKit Tizen 9.0 para de
  // disparar timeupdate/ended no fim do slot (bug Prada 15/15 tela preta).
  // Case 3 (ended silenciado) só cobre !shouldCutShort; cut-short dependia
  // só de video.currentTime. Debounce evita double-advance com o interval.
  const cutShortStuckRef = useRef<{
    mediaId: string | null;
    triggered: boolean;
  }>({
    mediaId: null,
    triggered: false,
  });

  /** Bloqueia upgrade remote→cache enquanto o item está tocando (Tab A7). */
  const deferCacheUpgradeRef = useRef(false);

  // 2026-06-23 — decoder stall mid-playback (currentTime congela ~13s).
  // Distinto de onEnded prematuro: vídeo fica no frame, sem paused/ended.
  const playbackStallRef = useRef<{
    mediaId: string | null;
    lastTime: number;
    sinceMs: number;
    recovery: 'none' | 'seek' | 'reload';
  }>({
    mediaId: null,
    lastTime: 0,
    sinceMs: 0,
    recovery: 'none',
  });
  const waitingRecoveryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  // Fullscreen state and refs
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showFullscreenButton, setShowFullscreenButton] = useState(true);
  const fullscreenButtonTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const muteTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const volumeIntervalRef = useRef<ReturnType<typeof setInterval>>();
  const mediaInfoTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const imageTimerRef = useRef<ReturnType<typeof setTimeout>>();
  /** Avanço da imagem atual — permite ao pulso de sincronia re-armar sem duplicar lógica. */
  const imageAdvanceRef = useRef<{
    fire: () => void;
    mediaId: string;
    baseDurationSeconds: number;
    firesAt: number;
  } | null>(null);
  /** Momento da última correção do pulso — evita corrigir em rajada durante transições. */
  const lastPulseActionRef = useRef<number>(0);
  /** Quando o último seek foi PEDIDO (pulso ou entrada) — mede a latência até o `seeked`. */
  const seekIssuedAtRef = useRef<number>(0);
  /** Latência medida do último seek (ms, alisada) — somada ao alvo do próximo. */
  const seekLatencyMsRef = useRef<number>(0);
  /** Quando o último seek TERMINOU (`seeked`) — base da histerese e do cooldown. */
  const lastSeekedAtRef = useRef<number>(0);
  /** Frame capturado no instante do hop do pulso (cover transitório, vale 1 troca). */
  const hopFrameCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const hopFrameReadyIdRef = useRef<string | null>(null);
  /** Vídeo cuja 1ª exibição já capturou o frame provisório (1× por exibição). */
  const firstPlayCaptureDoneRef = useRef<string | null>(null);
  /** MOTOR AGENDADO: o 2º elemento de vídeo (standby) e qual dos dois está ativo. */
  const standbyVideoRef = useRef<HTMLVideoElement | null>(null);
  const [activeVideoSlot, setActiveVideoSlot] = useState<'A' | 'B'>('A');
  /** Mídia que ENTROU pelo swap A/B: o elemento ativo já está tocando — não recarregar o src. */
  const scheduledSwapForMediaIdRef = useRef<string | null>(null);
  /** Troca agendada em curso (mídia atual + instante T no relógio do servidor). */
  const scheduledSwitchRef = useRef<{
    mediaId: string;
    switchAtMs: number;
  } | null>(null);
  /**
   * Última mídia cuja conclusão o swap agendado já reportou. No swap o vídeo que SAI vira
   * standby e perde os handlers (incl. `onEnded`), então quem emite a impressão é o próprio
   * `doSwitch`. Até o React re-renderizar, porém, o elemento antigo ainda tem os handlers —
   * se ele terminar nessa fresta, o `handleVideoEnded` emitiria de novo. Este ref é o guard.
   */
  const scheduledCompleteEmittedForRef = useRef<string | null>(null);
  /** Handle do <AvplayVideo> — único caminho pra corrigir posição no plano HW da TV. */
  const avplaySyncHandleRef = useRef<AvplaySyncHandle | null>(null);
  // Loop de 1 conteúdo: o effect "Image changed" re-roda a cada volta (currentMedia
  // é objeto novo, mesmo id). Sem guarda ele remontaria o BlackTransition sobre o
  // <img> e o WebKit Tizen re-decodificaria o bitmap 1440×2304 (~13MB nativos) toda
  // volta → dreno de memória nativa. Estas refs deixam o effect reconhecer "mesma
  // imagem já decodificada" e só re-armar o avanço (handleImageLoad), sem re-decode.
  const displayedImageIdRef = useRef<string | null>(null);
  const handleImageLoadRef = useRef<(() => void) | null>(null);
  // Safety-net timeout da imagem (avanço SÓ se a imagem nunca carregar).
  // Guardado em ref pra que `handleImageLoad` possa CANCELÁ-LO quando a imagem
  // carrega de fato — senão ele corre em paralelo com o `imageTimerRef` (o timer
  // real, que emite `playback_complete`) e, numa imagem de 10s, dispara ANTES
  // (armado no início vs. o real armado no onLoad ~300ms depois) → avança via
  // `handleMediaEnd(undefined)` SEM emitir analytics → imagem conta 0.
  const imageSafetyTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  // Tracks the latest isUrlLoading value so the safety timeout can read it
  const isUrlLoadingRef = useRef(false);
  // Tracks whether we already performed the synchronized-playback seek for the
  // current media. Without this, every canplay event (including the one
  // triggered by the seek itself) re-runs the seek → infinite loop.
  const syncedMediaIdRef = useRef<string | null>(null);
  /**
   * Último `src` de imagem que já ganhou UMA nova tentativa após `onError`.
   * Existe para distinguir erro REAL de "phantom error" — ver `handleImageError`.
   */
  const imageRetriedSrcRef = useRef<string | null>(null);
  const landscapeButtonRef = useRef<HTMLButtonElement>(null);
  const portraitButtonRef = useRef<HTMLButtonElement>(null);
  // 2026-06-01 (v1.0.108) — ref pro botão "Reativar Kiosk" pra incluir na
  // navegação por setas no painel debug. Antes o foco ficava preso em
  // Paisagem↔Retrato e não alcançava Reativar Kiosk (testado por Renato).
  const reactivateKioskButtonRef = useRef<HTMLButtonElement>(null);
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [isVideoLoading, setIsVideoLoading] = useState(false);
  // EARLY FREEZE "atrás do vídeo" (Renato 2026-07-16): quando o freeze é mostrado
  // ANTES do vídeo acabar, ele fica PRONTO mas o vídeo continua VISÍVEL por cima
  // (na Tizen o HW overlay renderiza acima do canvas) tocando seus últimos ~130ms
  // → SEM "retorno em movimento" (a volta). Só quando o vídeo REALMENTE acaba
  // (troca de src, que piscaria preto) o vídeo é escondido e o freeze — já lá —
  // é revelado. Este flag desliga o hide do vídeo durante a janela do early-freeze.
  const [isEarlyFreeze, setIsEarlyFreeze] = useState(false);

  // Canvas Bridge (v1.0.36) — durante a transição entre vídeos, captura o
  // último frame do vídeo que acabou e desenha num canvas overlay. O canvas
  // cobre o <video> enquanto ele troca de src + carrega + começa a tocar.
  // Visualmente parece "vídeo travado no último frame" mas tecnicamente é
  // canvas estático com 1 frame congelado.
  //
  // Por que não usar 2 <video> elements (que seria mais simples)?
  //   v1.0.30: VideoPreloader paralelo → Tizen Consumer 9.0 travou (decoder)
  //   v1.0.31: revert
  //   v1.0.32: preloader nos últimos 2.5s → AINDA travou (TV preta)
  // Tizen é intolerante a 2 <video> no DOM mesmo sem play paralelo. Canvas
  // resolve sem violar essa restrição.
  const freezeFrameCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [isFreezeFrameVisible, setIsFreezeFrameVisible] = useState(false);

  // BACKEND AVPLAY (Fase 1): ativo só em tizen + flag + sem fallback acionado. Quando
  // ativo, o slot fullscreen de VÍDEO renderiza <AvplayVideo> no lugar do <VideoPlayer>.
  // onFallback (erro/AVPlay ausente) liga avplayFallback → volta pro <video> (nunca preto).
  const [avplayFallback, setAvplayFallback] = useState(false);
  // Último fallback foi erro REAL do AVPlay (prefixo "avplay:") ou skip SOFT (vídeo remoto
  // não-file://)? Só re-tentamos o AVPlay num vídeo cacheado quando o último NÃO foi erro
  // real (senão flicker: erro real em file:// → fica no <video>, estável).
  const avplayHardErrorRef = useRef(false);
  const avplayActive =
    AVPLAY_BACKEND_FLAG && platform.platform === 'tizen' && !avplayFallback;

  // C4 — recycle por opens (mitigação do leak de CMA). Trocar `avplayGeneration` remonta o
  // <AvplayVideo> (via key) → React recria os 2 <object> → plano nativo recriado → (hipótese A)
  // reclama o CMA. Avaliado no BOUNDARY de slot (nunca mid-vídeo). Seamless quando o slot novo é
  // IMAGEM (o <img> DOM cobre a tela; o plano recriado fica atrás). Vide tizenAvplay (C4).
  const [avplayGeneration, setAvplayGeneration] = useState(0);
  useEffect(() => {
    // Kill-switch OFF (A reprovado: crashou ~720). Gate no CALL SITE (função pura fica testável).
    if (!AVPLAY_RECYCLE_ENABLED || !avplayActive) return; // A desligado → no-op
    const isImage = streamStore.currentMedia?.type === 'image';
    const decision = shouldRecycleAvplay(avplayOpensSinceRecycle(), isImage);
    if (decision) {
      noteAvplayRecycle(); // telemetria (black-box `r`) ANTES do remount
      setAvplayGeneration((g) => g + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streamStore.currentMedia?.id, avplayActive]);

  // MUTE sob AVPlay (Tizen): o plano HW do AVPlay NÃO tem mute/volume por instância
  // (a API só expõe open/prepare/play/stop/rect/stillMode) — o áudio no Tizen é
  // system-level. Então espelhamos o `isMuted` do app no MUTE de sistema
  // (tvaudiocontrol.setMute). Gate build-level (flag AVPlay + tizen) → prod (flag OFF)
  // fica byte-idêntica: lá o `<video muted={isMuted}>` cuida do mudo sozinho. Reage só
  // a isMuted (não ao slot) → sem churn; os `<video>` remanescentes (held-frame/fallback)
  // também espelham isMuted, então system-mute e atributo do elemento nunca divergem.
  useEffect(() => {
    if (!AVPLAY_BACKEND_FLAG || platform.platform !== 'tizen') return;
    void volumeServiceRef.current.setMute(streamStore.isMuted);
  }, [platform.platform, streamStore.isMuted]);

  // PHASE 1 — HOLD-FRAME (Tizen/webOS, video→img). Vide `shouldHoldVideoFrameForImage`
  // (playbackHelpers) + os blocos no handleVideoEnded/handleImageLoad/render.
  //  • `heldVideoForImage`: mantém o `<video>` que SAI montado + VISÍVEL (pausado no
  //    último frame, segurado pelo HW overlay) enquanto a imagem de destino pinta por
  //    baixo → zero preto. Só true em Tizen/webOS (gate no handleVideoEnded).
  //  • `heldVideoHidden`: após a imagem pintar (handleImageLoad), esconde o vídeo
  //    (visibility:hidden — hide limpo, sem flash de tear-down) revelando a imagem;
  //    o vídeo é desmontado logo depois (HELD_VIDEO_UNMOUNT_DELAY_MS).
  //  • `heldVideoForImageRef`: espelho pro handleImageLoad ler sem stale closure/dep.
  const [heldVideoForImage, setHeldVideoForImage] = useState(false);
  const [heldVideoHidden, setHeldVideoHidden] = useState(false);
  const heldVideoForImageRef = useRef(false);
  heldVideoForImageRef.current = heldVideoForImage;
  const heldVideoUnmountTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  // Boot grace (Renato 2026-07-05): timestamp do mount do Stream. Durante os
  // primeiros BOOT_GRACE_MS, se ainda não há mídia e o estado não é definitivo,
  // mostra "Carregando" em vez de "Aguardando conteúdo" (mata o flash de boot).
  const bootStartRef = useRef<number>(Date.now());
  const bootGraceLoggedRef = useRef(false);
  // Última exibição de slot já reportada em `slot_started`. Mata o churn de re-render (a dep do
  // efeito é uma referência do MobX) SEM subcontar exibições reais — o `slot_completed` limpa isto.
  const lastSlotStartedKeyRef = useRef<string | null>(null);
  const clearSlotStartedDedup = (): void => {
    lastSlotStartedKeyRef.current = null;
  };
  // BLK (2026-07-27) — quando o boot foi pedido pelo PRÓPRIO app (mitigação do leak nativo), o
  // cliente da loja não pode ver UI de sistema: só preto curto. Tem teto de segurança
  // (`RECYCLE_BOOT_CAP_MS`), então uma falha real volta a mostrar a UI normal.
  const { isRecycleBoot, finishRecycleBoot } = useRecycleBoot();

  // SEAMLESS v2 (Renato 2026-07-04/05): o antigo "Canvas Bridge de VÍDEO"
  // (screenshot ~250ms antes do fim, exibido no canvas ao terminar) foi
  // REMOVIDO — causava piscada/zoom/chacoalhada (salto temporal + dims do
  // canvas ≠ vídeo) e, em Tizen, gastava CPU/GPU sem entregar nada (HW overlay
  // blinda readback). A cobertura de preto hoje é: img→X e vídeo→imagem no
  // useLayoutEffect (Passo 2), e vídeo→X via último frame do vídeo capturado 1×
  // (Etapa B, lastFrameCache). Nada disso mexe no decoder na hora do release.
  const [mediaDuration, setMediaDuration] = useState(10); // Duration in seconds for progress bar
  const [playbackSpeed, setPlaybackSpeed] = useState(1); // Playback speed multiplier (1-4)
  /**
   * MOTOR AGENDADO (A/B) — vide `services/playback/scheduledEngine.ts`. Só vídeo→vídeo em
   * viewport único; imagem, zona, gatilho, loop de 1 item e velocidade ≠ 1 seguem no motor
   * reativo. Na TV o AVPlay de 2 players já é este modelo.
   */
  /**
   * ESTRUTURAL — a plataforma comporta o A/B? Decide apenas se os DOIS `<video>` são montados.
   * Constante na sessão: a flag que muda é a de comportamento, abaixo, e virar esta remontaria
   * o elemento de vídeo em TODA a frota a cada boot.
   */
  const abDisponivel =
    isScheduledEngineEnabled(platform.platform) &&
    !avplayActive &&
    playbackSpeed === 1;

  /**
   * COMPORTAMENTAL — o A/B está LIGADO? Começa `false` e só liga depois que a sonda conseguir
   * um 2º decoder de verdade (vide o efeito da sonda).
   *
   * <p><b>Seguro por padrão (Renato, 2026-10-12).</b> Enquanto não se prova que o aparelho
   * aguenta 2, rodamos no motor reativo — que foi MEDIDO tocando com 1 decoder: 21 quadros,
   * 19 trocas, zero congelamento, gatilho entrando normal. O A/B é uma otimização que precisa
   * ser conquistada, não um padrão a ser desfeito quando quebra.</p>
   *
   * <p>Perguntar ao aparelho não serve: o Tab A7 DECLARA 16 instâncias de AVC e na prática
   * recusa a 3ª. Só tentar diz a verdade.</p>
   */
  const abHabilitado = useSegundoDecoder({
    disponivel: abDisponivel,
    mediaId: streamStore.currentMedia?.id ?? null,
    emGatilho: streamStore.isTriggerPlayback,
    obterStandby: () => {
      const standby = standbyVideoRef.current;
      return standby && standby !== videoRef.current ? standby : null;
    },
    obterAtivo: () => videoRef.current,
    resolverUrl: async () => {
      const alvo = streamStore.currentMedia;
      const standby = standbyVideoRef.current;
      if (!alvo || alvo.type !== 'video' || !standby) return null;
      const { url, source } = await resolvePlayableUrl(alvo);
      if (!url) return null;
      applyMediaCrossOrigin(standby, source);
      return url;
    },
  });
  const scheduledEngine = abDisponivel && abHabilitado;
  /**
   * Compensação de pintura dos VÍDEOS. No motor agendado o próximo vídeo já está decodificado
   * quando entra, então a latência de início é ~1 frame e o lead cai pra 0 — com lead fixo, o
   * pulso "corrigiria" o vídeo pra ficar 200–250ms à frente da régua.
   */
  const videoPaintLeadMs = scheduledEngine ? 0 : paintLeadMs(platform.platform);
  // videoProgress (0-100%) REMOVIDO (Renato 2026-07-05): a barra virou o
  // componente-folha <PlaybackProgressBar>, que roda o próprio rAF e escreve no
  // DOM via ref — o Stream não re-renderiza mais por progresso. Vide o guardrail
  // de plataformas (gargalo do A7: setState de progresso 60×/s re-renderizava
  // a árvore inteira).
  const [zoneTimelineSec, setZoneTimelineSec] = useState(0);
  const zoneTimelineStartRef = useRef(performance.now());

  // Capture the playlistVersion when current content starts rendering,
  // so stale handleMediaEnd calls from previous playlists are rejected.
  // Updated ONLY in handleImageLoad / handleCanPlay (when content STARTS),
  // NOT when playlist changes — this ensures old video onEnded events still
  // carry the old version and get rejected.
  const mediaVersionRef = useRef(streamStore.playlistVersion);
  // Qual mídia estava tocando quando a reprodução COMEÇOU. É a identidade autoritativa pro
  // descarte de fim-obsoleto: o `playlistVersion` sozinho gerava deadlock permanente quando a
  // playlist era substituída com o vídeo já tocando (vide isStaleMediaEnd no stream.store).
  const playingMediaIdRef = useRef<string | undefined>(undefined);
  const videoLoopControlRef = useRef({
    shouldManualLoop: false,
    shouldCutShort: false,
    scheduledDuration: 0,
    actualDuration: 0,
    playbackStartTime: 0,
  });

  // Track animation timing for smooth speed transitions
  // consumedTime = total animation time consumed at 1x speed equivalent
  const animationTimingRef = useRef({
    startTime: Date.now(),
    speed: 1,
    consumedTime: 0,
  });
  const [focusedButton, setFocusedButton] = useState<
    'landscape' | 'portrait' | 'reactivate-kiosk' | null
  >(null);

  // Estado para controlar visibilidade do MediaInfo.
  //
  // 2026-06-12 (Renato): inicia SEMPRE oculto, mesmo em DEV. Antes, em
  // `yarn dev` o painel aparecia por default e ficava preso atrás do
  // KioskOverlay (z-9998) quando o sync trazia `kioskModeEnabled=true` —
  // operador precisava desabilitar kiosk → fechar painel → reabilitar.
  // Acesso ao painel em DEV ou release sempre via toque (3-tap rápido em
  // qualquer plataforma), tecla H, ou Down arrow — caminhos já existem.
  const [showMediaInfo, setShowMediaInfo] = useState(false);
  // NOTA (Renato 2026-07-05, gargalo A7): os ~6 pollings de telemetria
  // (cache/volume/brilho/bateria/zone-timeline) só alimentam o painel de debug
  // (MediaInfo, $visible={showMediaInfo}). Gateamos os setState por showMediaInfo
  // pra o Stream NÃO re-renderizar por eles quando o painel está fechado (default
  // em prod) → app ocioso quando estático. O heartbeat de crash
  // (setHeartbeatContext) NÃO é gateado (roda sempre). O interval do cacheInfo lê
  // o valor live via `showMediaInfoRef` (declarado logo abaixo).
  const [showCustomSelector, setShowCustomSelector] = useState(false);
  const customOrientations = syncStore.availableOrientations.filter(
    (o) => !o.is_default,
  );

  // v1.0.51 — ref mirror pra que handlers do TVRemote (closure capturada
  // no momento do render) leiam SEMPRE o estado atual. Sem isso, o
  // `onUp` pra limpar crashes podia ver `showMediaInfo=false` quando
  // o usuário já tinha aberto o overlay (stale closure clássico).
  const showMediaInfoRef = useRef(showMediaInfo);
  showMediaInfoRef.current = showMediaInfo;

  // v1.0.143 — espelha o estado do overlay de controle pro store, pra o
  // KioskSettingsButton (renderizado fora do Stream, no KioskGuard) saber se o
  // painelzinho está aberto e só então mostrar o botão de Configurações durante
  // a mídia. Reset no unmount pra não vazar "aberto" pra tela de waiting.
  useEffect(() => {
    streamStore.setControlsVisible(showMediaInfo);
    return () => streamStore.setControlsVisible(false);
  }, [showMediaInfo, streamStore]);

  // v1.0.218 (Renato 2026-07-17): safety net — se admin habilitar kiosk
  // remotamente enquanto o painel debug estiver aberto (SSE config_update),
  // fecha o painel imediatamente. Complementa o gate do onDown/onInfo.
  useEffect(() => {
    if (kioskStore.isLocked && showMediaInfo) {
      setShowMediaInfo(false);
    }
  }, [kioskStore.isLocked, showMediaInfo]);

  // Renato 2026-11-04 (embedded): ao DESTRAVAR o kiosk (transição travado→
  // destravado), auto-mostra o painel de troubleshooting (MediaInfo). Destravar
  // é ação de manutenção — o técnico quer ver o diagnóstico sem precisar tocar.
  // Universal (resolve o embedded, onde o toque não abria o painel). Só dispara na
  // transição REAL (não em dev/browser, onde o kiosk fica sempre OFF = isLocked
  // sempre false, sem transição).
  const prevLockedRef = useRef(kioskStore.isLocked);
  useEffect(() => {
    const was = prevLockedRef.current;
    prevLockedRef.current = kioskStore.isLocked;
    if (was && !kioskStore.isLocked) {
      setShowMediaInfo(true);
    }
  }, [kioskStore.isLocked]);

  // Cache info state
  const [cacheInfo, setCacheInfo] = useState({
    cached: 0,
    downloading: 0,
    // 2026-05-28 — `saving` = download terminou (100% rede) mas cache.store()
    // ainda está commitando o blob. Em Tizen Consumer 9.0 com vídeos grandes,
    // essa fase chega a 30s (watchdog) — sem esse counter, o operador via
    // "100% baixando" e achava que travou (print Renato 2026-05-28).
    saving: 0,
    pending: 0,
    errors: 0,
    // 2026-05-24 — lastError/lastErrorAt: erro mais recente do cache.store()
    // ou download. Exposto no overlay debug pra diagnosticar TV Tizen sem
    // acesso a adb shell (consumer TV bloqueia).
    lastError: null as string | null,
    lastErrorAt: null as number | null,
    // 2026-05-24 — Rastreio de remoções pra identificar quem está descartando
    // o cache logo após download (sintoma observado em UN50U8600F / Tizen 7.7).
    totalRemovals: 0,
    lastRemovalSource: null as string | null,
    lastRemovalCount: 0,
    lastRemovalAt: null as number | null,
    // 2026-05-24 — Telemetria de checksum mismatch (graceful degradation
    // ativa por padrão a partir de v1.0.8, ver media.store).
    checksumMismatchCount: 0,
    lastChecksumMismatch: null as string | null,
    // 2026-05-24 — Crashes persistidos (window.onerror + unhandledrejection)
    // entre boots. Tizen consumer crashou várias vezes sem deixar pista,
    // esse capture serve como caixa-preta minimal.
    crashCount: 0,
    lastCrashMessage: null as string | null,
    lastCrashAt: null as number | null,
    lastCrashStack: null as string | null,
    downloads: [] as Array<{ id: string; name: string; progress: number }>,
    cachedFiles: [] as Array<{ id: string; name: string }>,
  });

  // Orientation management
  const {
    orientation: detectedOrientation,
    setOverride: setOrientationOverride,
  } = useOrientation();
  const effectiveOrientation = authStore.getEffectiveOrientation();

  // Handler for orientation change from MediaInfo overlay
  // Persists to server and triggers content re-sync for orientation-appropriate content
  const handleOrientationChange = useCallback(
    async (newOrientation: ScreenOrientation): Promise<void> => {
      const isAutoDetected = newOrientation === detectedOrientation;
      const override = isAutoDetected ? null : newOrientation;

      // Update local state immediately for responsive UI
      setOrientationOverride(override);
      authStore.setSelectedOrientation(override);

      // Persist to server (works for both pending and approved devices)
      const orientationToSend = override ?? detectedOrientation;
      await authStore.updateOrientation(orientationToSend);

      // Server will send SSE content_changed which triggers automatic re-sync
      // But we also force a local re-sync to ensure immediate update
      syncStore.forceResync({ resetPlaylistFromStart: true });
    },
    [detectedOrientation, setOrientationOverride, authStore, syncStore],
  );

  // Handler for custom orientation selection from the dropdown (MediaInfo overlay).
  const handleCustomOrientationSelect = useCallback(
    async (orientacaoId: number): Promise<void> => {
      setShowCustomSelector(false);
      await authStore.updateOrientationWithId(orientacaoId);
      syncStore.forceResync({ resetPlaylistFromStart: true });
    },
    [authStore, syncStore],
  );

  // Sync física → admin/server: MOVIDO pra useOrientationSync() (montado no
  // App, sempre ativo). Antes vivia AQUI (cena Stream), então enquanto o
  // device ficava "Aguardando aprovação" (cena Auth) a rotação física NÃO
  // chegava ao servidor — o 1º cadastro (tablet na mão = landscape) ficava
  // gravado e o servidor servia o criativo landscape mesmo montado em
  // portrait (diagnóstico Renato, 2026-06-06). Agora a propagação é global.

  // Fullscreen change listener
  useEffect(() => {
    if (!platform.supportsFullscreen()) return;

    const unsubscribe = platform.onFullscreenChange((fullscreen) => {
      setIsFullscreen(fullscreen);
    });

    return unsubscribe;
  }, [platform]);

  // Auto-hide fullscreen button after inactivity
  useEffect(() => {
    if (!platform.supportsFullscreen()) return;

    const resetHideTimer = (): void => {
      // Show button
      setShowFullscreenButton(true);

      // Clear existing timeout
      if (fullscreenButtonTimeoutRef.current) {
        clearTimeout(fullscreenButtonTimeoutRef.current);
      }

      // Set new timeout to hide
      fullscreenButtonTimeoutRef.current = setTimeout(() => {
        setShowFullscreenButton(false);
      }, FULLSCREEN_BUTTON_HIDE_DELAY_MS);
    };

    // Initial timer
    resetHideTimer();

    // Listen for user activity
    const events = [
      'mousemove',
      'mousedown',
      'touchstart',
      'touchmove',
      'keydown',
    ];
    events.forEach((event) => {
      document.addEventListener(event, resetHideTimer, { passive: true });
    });

    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, resetHideTimer);
      });
      if (fullscreenButtonTimeoutRef.current) {
        clearTimeout(fullscreenButtonTimeoutRef.current);
      }
    };
  }, [platform]);

  // Handler for toggle fullscreen
  const handleToggleFullscreen = useCallback(async () => {
    if (!platform.supportsFullscreen()) return;

    try {
      if (platform.isFullscreen()) {
        await platform.exitFullscreen();
      } else {
        await platform.enterFullscreen(
          containerRef.current ?? document.documentElement,
        );
      }
    } catch (err) {
      console.warn('[Stream] Fullscreen error:', err);
    }
  }, [platform]);

  // Handle speed change from slider (1-4)
  const handleSpeedChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newSpeed = parseInt(event.target.value, 10);
      const {
        startTime,
        speed: oldSpeed,
        consumedTime,
      } = animationTimingRef.current;

      // Calculate elapsed real time since last speed change
      const elapsedRealTime = (Date.now() - startTime) / 1000; // in seconds

      // Calculate total consumed animation time (at 1x speed equivalent)
      // = previous consumed time + new elapsed time at old speed
      const totalConsumedTime = consumedTime + elapsedRealTime * oldSpeed;

      // Update refs and state
      animationTimingRef.current = {
        startTime: Date.now(),
        speed: newSpeed,
        consumedTime: totalConsumedTime,
      };
      setPlaybackSpeed(newSpeed);

      // Apply to video immediately
      const video = videoRef.current;
      if (video) {
        video.playbackRate = newSpeed;
      }
    },
    [],
  );

  // Resolve playable URL (cache or remote)
  const {
    url: playableUrl,
    source: playbackSource,
    isLoading: isUrlLoading,
  } = usePlayableUrl(
    syncStore.hasMultipleZones && streamStore.currentMedia?.zonaId != null
      ? null
      : streamStore.currentMedia,
    null,
    { deferCacheUpgradeRef },
  );

  // Keep ref in sync so the image safety timeout can read the latest value
  isUrlLoadingRef.current = isUrlLoading;

  // ⚠️ TEMP (INVESTIGACAO-TV-TELA-PRETA.md): último source resolvido (cache/remote)
  // do conteúdo ATUAL, num ref — pra o HUD ler a origem no instante do paint sem
  // adicionar deps aos callbacks (que re-renderizaria/re-attacharia handlers).
  const playbackSourceRef = useRef(playbackSource);
  playbackSourceRef.current = playbackSource;

  // LAST-FRAME WebCodecs (2026-07-16) — gera PROATIVAMENTE o ÚLTIMO frame do vídeo
  // ATUAL (pra estar pronto quando ELE acabar) e do PRÓXIMO (pra já estar cacheado
  // quando ELE vier a sair no loop seguinte), via WebCodecs. Cobre a transição que
  // SAI de um vídeo em Tizen/webOS (paridade com o lastFrameCache nativo do
  // Android). Gate: SÓ tizen/webos (Android/browser usam lastFrameCache via
  // drawImage(<video>), que lá funciona). Serializado + throttle + cache-first dentro do ensureLastFrameWc
  // (GOP é pesado → não contende com o decoder de reprodução; async, não bloqueia).
  const isTizenLike = platform.isTV();
  // WebCodecs presente? Consumer Tizen 9 / webOS TÊM → captura o lastFrameWc
  // (usado só pro poster do video→video). SSSP 6.5 (Chromium ~76-85) NÃO tem →
  // não tenta capturar (o video→img usa o hold-frame do <video> vivo; o
  // video→video fica sem poster e pisca até o AVPlay). Gate do ensureLastFrameWc.
  const hasWebCodecs =
    typeof (window as unknown as { VideoDecoder?: unknown }).VideoDecoder !==
    'undefined';
  useEffect(() => {
    // Sem WebCodecs (SSSP 6.5, ou simulação VITE_FORCE_NO_WEBCODECS) não há como
    // capturar o lastFrameWc → não tenta (a rota vira o early-hold da Fase 1.1).
    // Sob AVPlay (avplayActive) o poster NÃO é consumido (o still-mode do plano HW cobre a
    // transição; o early-freeze lê videoRef que é null) → o decode de poster WebCodecs seria
    // PURO desperdício competindo pelo mesmo decoder HW/CMA no 9.0. Gate #2: pula sob AVPlay.
    if (!isTizenLike || !hasWebCodecs || avplayActive) return;
    let cancelled = false;
    const targets = [streamStore.currentMedia, streamStore.nextMedia];
    for (const media of targets) {
      if (!media || media.type !== 'video') continue;
      if (lastFrameWcCache.has(media.id)) continue;
      // Duração EFETIVA (instante do corte): com duration override o vídeo é
      // CORTADO em media.duration → o frame que fica na tela é o do corte, não o
      // fim do arquivo. Sem override → null (último frame do arquivo, fim natural).
      // Espelha o Android, que captura o frame VIVO no corte (shouldCaptureLastFrame).
      const targetSec =
        media.hasDurationOverride === true ? (media.duration ?? null) : null;
      void resolvePlayableUrl(media).then(({ url }) => {
        if (cancelled || !url) return;
        ensureLastFrameWc(media.id, url, targetSec); // no-op se já cacheado/em voo
      });
    }
    return () => {
      cancelled = true;
    };
  }, [
    isTizenLike,
    hasWebCodecs,
    avplayActive,
    streamStore.currentMedia?.id,
    streamStore.nextMedia?.id,
  ]);

  // BOUND DE RAM (2026-09-18): ao trocar a programação, solta os últimos frames de
  // conteúdos que saíram da playlist — os tetos do cache já limitam o total, isto
  // evita que ids mortos fiquem ocupando as vagas dos que ainda tocam. Id que voltar re-captura
  // na próxima exibição (degrada gracioso: 1 preto curto). Playlist vazia
  // (sync em voo / NoContent) NÃO descarta nada.
  useEffect(() => {
    const ids = streamStore.playlist.map((item) => item.id);
    if (ids.length === 0) return;
    lastFrameCache.retain(ids);
  }, [streamStore.playlist]);

  // v1.0.49 — Reporta estado do vídeo pro heartbeat. Se o app crashar
  // durante uma transição (suspeito: pico de memória nativa quando
  // decoder do <video> está abrindo um source novo + blob URL anterior
  // ainda vivo), o boot seguinte vai mostrar `TRANSITIONING` no log do
  // crash, isolando a causa.
  useEffect(() => {
    if (!streamStore.currentMedia) {
      setHeartbeatVideoState({ sourceKind: 'none', transitioning: false });
      return;
    }
    let sourceKind: 'blob' | 'http' | 'file' | 'none' = 'none';
    if (playableUrl) {
      if (playableUrl.startsWith('blob:')) sourceKind = 'blob';
      else if (playableUrl.startsWith('file://')) sourceKind = 'file';
      else if (playableUrl.startsWith('http')) sourceKind = 'http';
    }
    // Campanha do conteúdo atual + estado do kiosk — baratos, já em memória. Enriquecem o
    // heartbeat/crash context (aparecem no alerta PLAYER_CRASH e no logtail). Best-effort.
    const currentCampaign = schedulerStore.campaigns.find(
      (c) => Number(c.id) === streamStore.currentMedia?.campanhaId,
    )?.name;
    setHeartbeatVideoState({
      currentName: streamStore.currentMedia.name,
      sourceKind,
      transitioning: isUrlLoading,
      ...(currentCampaign ? { currentCampaign } : {}),
      kioskLocked: kioskStore.isLocked,
    });
  }, [
    streamStore.currentMedia,
    playableUrl,
    isUrlLoading,
    schedulerStore.campaigns,
    kioskStore.isLocked,
  ]);

  // RE-ATTEMPT do AVPlay (Tizen, flag ON): o fallback NÃO é mais permanente. Quando entra
  // um VÍDEO CACHEADO (file://) e o fallback vigente veio de um skip SOFT (vídeo remoto,
  // que o AVPlay não abre), re-arma o AVPlay pra esse cacheado (gapless). Se o último
  // fallback foi erro REAL do AVPlay em file:// (avplayHardErrorRef), NÃO re-arma → fica
  // no <video> estável (sem flicker). Prod (flag OFF) não roda.
  useEffect(() => {
    if (!AVPLAY_BACKEND_FLAG || platform.platform !== 'tizen') return;
    if (
      streamStore.currentMedia?.type === 'video' &&
      playbackSource === 'cache' &&
      playableUrl?.startsWith('file://') &&
      avplayFallback &&
      !avplayHardErrorRef.current
    ) {
      setAvplayFallback(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    streamStore.currentMedia?.id,
    playbackSource,
    playableUrl,
    avplayFallback,
  ]);

  // v1.0.51 — CRASH LOOP PROTECTION → REVERTIDO em v1.0.52
  //
  // Renato (2026-05-28): "eu não posso pular videos, o cliente comprou,
  // se tiver erro em videos, preciso corrigir, não pule não".
  //
  // Regra de negócio: TODO conteúdo da playlist DEVE tocar — é contratual
  // com o anunciante. Em vez de pular vídeos que crasham, o fix correto
  // é atacar a causa raiz. Análise dos prints de campo revelou que o
  // problema NÃO era o vídeo específico (Prada inicialmente suspeito),
  // mas o watchdog IDB de 30s que estava amputando transactions legítimas
  // em Tizen Consumer 9.0 — múltiplos vídeos diferentes (PAISAGENS,
  // Chanel, Pepsi, Coca-Cola, Prada) deram timeout, mostrando que era
  // problema sistêmico de timing, não de conteúdo.
  //
  // Fix em v1.0.52: `IndexedDBCache.ts` watchdog 30s → 120s. Vídeos
  // agora têm tempo suficiente pra gravar no IDB mesmo competindo com
  // HTTP video stream + decoder.
  //
  // `countCrashesForVideo` continua exportado em crashCapture.ts pra
  // diagnóstico futuro (operador pode consultar via console se quiser),
  // mas NÃO dispara skip automático.

  // PRELOAD DO PRÓXIMO VÍDEO — DESATIVADO (v1.0.33 = revert da v1.0.32).
  //
  // v1.0.30: preloader desde o início → concorrência decoder → travou
  // v1.0.31: preloader desativado → app voltou ao normal (com "Carregando" visível)
  // v1.0.32: preloader nos últimos 2.5s + source filter → AINDA TRAVOU
  //          (cenário: TV preto + status Buffering mesmo com cache)
  //
  // O Tizen Consumer 9.0 parece ter intolerância forte com QUALQUER
  // <video preload="auto"> em paralelo, mesmo quando ativado nos
  // últimos segundos. O preloader interfere no decoder do principal
  // pegando recursos que o principal precisa pra concluir o frame
  // atual + iniciar o próximo.
  //
  // Próxima abordagem (não trivial — descartar pre-load via 2º video):
  //   - Double buffering com 2 video ELEMENTS no DOM, SEM preload
  //     adicional. Vídeo "B" só recebe src=newUrl no momento que
  //     vídeo "A" termina (handleVideoEnded). Visibility alterna.
  //     Ganho menor (não pre-decoda), mas elimina o "Carregando..."
  //     porque A ainda mostra último frame enquanto B faz load.
  //
  // 2026-10-08: REATIVADO só onde há decoder sobrando — navegador e desktop (Chrome /
  // WebView2). Android e Tizen continuam SEM preloader (vide `shouldPreloadNextVideo`:
  // no Tab A7 um 2º consumidor do OMX já derrubou o decoder pra software; na Tizen travou).
  // Lá o cover de último frame é quem esconde a troca.
  const [nextPreloadUrl, setNextPreloadUrl] = useState<string | null>(null);
  useEffect(() => {
    const cur = streamStore.currentMedia;
    const next = streamStore.nextMedia;
    if (
      !shouldPreloadNextVideo(platform.platform) ||
      !cur ||
      !next ||
      next.type !== 'video' ||
      next.id === cur.id
    ) {
      setNextPreloadUrl(null);
      return;
    }
    let cancelled = false;
    void resolvePlayableUrl(next).then(({ url }) => {
      if (!cancelled) setNextPreloadUrl(url || null);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    streamStore.currentMedia?.id,
    streamStore.nextMedia?.id,
    platform.platform,
  ]);

  // Get device/network info for debug display
  const deviceInfo = useDeviceInfo();
  const orientationLabel = getOrientationLabel(
    effectiveOrientation,
    syncStore.orientationWidth,
    syncStore.orientationHeight,
  );
  const volumeLabel = currentVolume !== null ? `${currentVolume}%` : 'N/A';
  const serverDefaultVolume = syncStore.getLastConfigUpdate?.()?.volumeLevel;
  const defaultVolumeLabel =
    typeof serverDefaultVolume === 'number'
      ? `${serverDefaultVolume}%`
      : defaultVolume !== null
        ? `${defaultVolume}%`
        : 'N/A';
  const brightnessConfig = syncStore.getLastConfigUpdate?.();
  // Prioridade: hardware (real) > overlay (efetivo aplicado) > config (server).
  // Sufixo (hw/overlay/config) deixa explícito a fonte do valor exibido.
  // Sufixo (set:hw|set:overlay) indica de onde foi o ÚLTIMO setBrightness —
  // útil pra detectar quando o config pede X mas o app cai no fallback.
  const brightnessSvc = BrightnessService.getInstance();
  // overlayBrightness vem do estado pollado (não da leitura direta), pra
  // que React re-renderize quando setBrightness mudar o overlay.
  const overlayBrightness = overlayBrightnessState;
  const lastSetSource = brightnessSvc.getLastSource();
  const configuredBrightness = brightnessConfig?.brightnessAuto
    ? 'Auto'
    : typeof brightnessConfig?.brightnessLevel === 'number'
      ? `${brightnessConfig.brightnessLevel}%`
      : null;
  let brightnessValue: string;
  if (hardwareBrightness !== null) {
    brightnessValue = `${hardwareBrightness}% (hw)`;
  } else if (overlayBrightness !== null) {
    brightnessValue = `${overlayBrightness}% (overlay)`;
  } else if (configuredBrightness) {
    brightnessValue = `${configuredBrightness} (config)`;
  } else {
    brightnessValue = 'N/A';
  }
  const brightnessLabel = lastSetSource
    ? `${brightnessValue} [set:${lastSetSource}]`
    : brightnessValue;

  // Auto-hide MediaInfo após 4 segundos quando ativado manualmente
  useEffect(() => {
    if (showMediaInfo && !import.meta.env.DEV) {
      mediaInfoTimeoutRef.current = setTimeout(() => {
        setShowMediaInfo(false);
      }, AUTO_HIDE_DELAY_MS);
    }

    return () => {
      if (mediaInfoTimeoutRef.current) {
        clearTimeout(mediaInfoTimeoutRef.current);
      }
    };
  }, [showMediaInfo]);

  // Detecção de LOOP do vídeo via timeupdate (re-arma elementos interativos ao
  // reiniciar). O progresso da barra NÃO é mais calculado aqui — migrou pro
  // <PlaybackProgressBar> (Renato 2026-07-05, gargalo do A7).
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const onTimeUpdate = () => {
      const ct = video.currentTime;
      const prev = lastVideoTimeRef.current;

      // Detect video loop: currentTime jumped backward significantly
      if (prev > 2 && ct < prev - 1) {
        const media = streamStore.currentMedia;
        if (media) {
          interactiveElementsStore.setElementsForContent(media.id);
        }
      }
      lastVideoTimeRef.current = ct;
      // O cálculo de progresso da barra SAIU daqui (Renato 2026-07-05) → agora
      // vive no componente-folha <PlaybackProgressBar> (rAF próprio + escrita no
      // DOM via ref, sem re-render do Stream). Este listener mantém APENAS a
      // detecção de loop acima (re-arma os elementos interativos no restart).
    };

    video.addEventListener('timeupdate', onTimeUpdate);
    return () => video.removeEventListener('timeupdate', onTimeUpdate);
  }, [streamStore.currentMedia]);

  // Reset sync flag when media changes.
  // The sync flag must reset so the next media can synchronize once on its
  // first canplay (without it, the second media in the playlist would never
  // sync to the server timestamp).
  // (O reset visual da barra é feito dentro do <PlaybackProgressBar>, no effect
  // keyado em mediaId — Renato 2026-07-05.)
  useEffect(() => {
    syncedMediaIdRef.current = null;
  }, [streamStore.currentMedia]);

  // CP10 — Track slot_started quando currentMedia muda (Tier 2/3 only).
  // Single source of truth — cobre imagens E vídeos (não precisa wire-up
  // separado em handleImageLoad / handleVideoPlay). No-op em Tier 4 porque
  // getSlotContextForAnalytics retorna null sem slot_sequence.
  useEffect(() => {
    if (!streamStore.currentMedia) return;
    const slotCtx = getSlotContextForAnalytics();
    if (slotCtx) {
      // DEDUP (2026-07-27): a dep deste efeito é `currentMedia`, um computed do MobX que devolve
      // `playlist[currentIndex]` — uma REFERÊNCIA. Cada `setPlaylist` recria os itens, então a
      // referência muda mesmo com conteúdo idêntico e o efeito re-dispara. Medido na bancada:
      // `slot_started slotIndex=0` emitido de 2 em 2 SEGUNDOS, sem nenhum `open` de AVPlay
      // correspondente — evento gravado à toa, inflando o IndexedDB.
      // ⚠️ A regra é "uma exibição por slot_started", NÃO "um por slot": quando o loop dá a volta
      // e o mesmo slot toca de novo, aquela exibição é REAL e conta pro mínimo contratual (R1).
      // Por isso o `slot_completed` LIMPA a chave (vide clearSlotStartedDedup) — só o repique
      // consecutivo da mesma exibição é suprimido. Decisão pura em playbackHelpers.
      const key = slotExhibitionKey(slotCtx);
      if (
        shouldEmitSlotStarted({
          key,
          lastEmittedKey: lastSlotStartedKeyRef.current,
        })
      ) {
        lastSlotStartedKeyRef.current = key;
        try {
          analyticsService.trackSlotStarted(slotCtx);
        } catch {
          // Defensivo: analytics nunca pode quebrar playback
        }
      }
    }
    // Loop de 1 conteúdo: `currentMedia` é o mesmo slot a cada volta, então quem
    // re-dispara o slot_started é `currentItemStartedAt` (muda a cada avanço) —
    // pareado com o slot_completed. O dedup acima suprime o repique espúrio de
    // setPlaylist (mesmo slot, sem volta real).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streamStore.currentMedia, streamStore.currentItemStartedAt]);

  // Wire interactive elements to store when current media changes.
  useEffect(() => {
    const media = streamStore.currentMedia;
    lastVideoTimeRef.current = 0;
    if (!media) {
      interactiveElementsStore.clearElements();
      return;
    }
    interactiveElementsStore.setElementsForContent(media.id);
  }, [streamStore.currentMedia]);

  // CP11 (2026-05-16) — Live playback reporting. O player manda um snapshot do
  // estado atual; o backend cacheia por deviceId e o admin consulta via
  // GET /api/v1/players/{id}/playback-status/latest sem round-trip SSE.
  //
  // A CADÊNCIA quem decide é o SERVIDOR (`playback_report_interval_seconds` em
  // /api/config/player/me). O heartbeat fixo de 15s + um report a cada troca de
  // slot dava ~12/min de TODO player o tempo todo — a 26 mil players, ~5.200 req/s
  // pra alimentar consumidores que, em sua maioria, só olham sob demanda. Agora:
  //
  //   ausente → legado (15s + troca de slot). Servidor antigo ou kill-switch.
  //   0       → não reporta sozinho; responde ao comando SSE `playback_status`
  //             (que a própria consulta do admin dispara). É a frota inteira.
  //   N > 0   → a cada N s, sem o disparo por troca de slot. É quem está em grupo
  //             com sincronismo canônico, no passo do supervisor.
  const { intervalMs: reportIntervalMs, reportOnSlotChange: reportaEmTrocaDeSlot } =
    resolveReportCadence(
      syncStore.getLastConfigUpdate()?.playbackReportIntervalSeconds,
    );

  useEffect(() => {
    const deviceId = authStore.deviceId;
    if (!deviceId) return;
    const apiUrl = resolveApiBaseUrl('');
    if (!apiUrl) return;

    playbackStatusService.configure(apiUrl, deviceId);
    // Report no mount mesmo com cadência 0: custa um request por boot e deixa o
    // cache do servidor com algo pra mostrar quando o operador abrir o painel
    // pela primeira vez, em vez de uma tela vazia esperando o round-trip.
    playbackStatusService.reportLive().catch(() => {
      // Defensivo: nunca bloqueia playback
    });

    if (reportIntervalMs <= 0) return; // sob demanda — sem timer

    const interval = window.setInterval(() => {
      playbackStatusService.reportLive().catch(() => {
        // idem
      });
    }, reportIntervalMs);

    return () => clearInterval(interval);
  }, [authStore.deviceId, reportIntervalMs]);

  // CP11 — Report imediato na troca de slot (latência ≤1s pro "qual slot agora"
  // no admin). Só vale no modo legado — vide acima.
  useEffect(() => {
    if (!reportaEmTrocaDeSlot) return;
    if (!streamStore.currentMedia) return;
    playbackStatusService.reportLive().catch(() => {
      // Defensivo
    });
  }, [streamStore.currentMedia, reportaEmTrocaDeSlot]);

  // Progresso de imagem por wall-clock REMOVIDO (Renato 2026-07-05): antes, um
  // loop de requestAnimationFrame chamava setVideoProgress ~60×/s → re-render do
  // Stream inteiro a cada frame só pra mover a barra (o maior ofensor de
  // main-thread no A7). Agora o <PlaybackProgressBar> roda o próprio rAF e
  // escreve a largura no DOM via ref, SEM tocar o Stream.

  // Update cache info every 1 second
  useEffect(() => {
    const updateCacheInfo = () => {
      let cached = 0;
      let downloading = 0;
      let saving = 0;
      let pending = 0;
      let errors = 0;
      const downloads: Array<{ id: string; name: string; progress: number }> =
        [];
      const cachedFiles: Array<{ id: string; name: string }> = [];

      mediaStore.cacheStatus.forEach((status, id) => {
        // Resolução de nome em 3 níveis:
        //   1. CachedMedia.name — salvo no enfileiramento (queueDownload) E
        //      persistido no metadata do cache (sobrevive a restart do app).
        //   2. streamStore.playlist[i].name — playlist atual (fallback caso
        //      o item esteja na playlist mas o cache ainda não tem o nome,
        //      ex: rehydrate de metadata antigo pré-v1.0.48).
        //   3. id.substring(0, 8) — último recurso pra itens órfãos (cache
        //      pré-existente sem nome nem playlist correspondente — não deve
        //      mais acontecer em condições normais, mas guarda contra
        //      regressão silenciosa).
        const playlistItem = streamStore.playlist.find(
          (item) => item.id === id,
        );
        const name = status.name || playlistItem?.name || id.substring(0, 8);

        if (status.status === 'cached') {
          cached++;
          cachedFiles.push({ id, name });
        } else if (status.status === 'downloading') {
          downloading++;
          const progress = mediaStore.downloadProgress.get(id) || 0;

          downloads.push({
            id,
            name,
            progress: Math.round(progress), // progress already in 0-100 range
          });
        } else if (status.status === 'saving') {
          // Entra na lista de downloads como "salvando" pra dar feedback
          // visual ao operador (o item NÃO está travado, está commitando).
          saving++;
          downloads.push({
            id,
            name,
            progress: 100, // download de rede 100%; UI renderiza "salvando…"
          });
        } else if (status.status === 'pending') pending++;
        else if (status.status === 'error') errors++;
      });

      const crashes = getRecentCrashes();
      const lastCrash = crashes.length > 0 ? crashes[crashes.length - 1] : null;

      // Alimenta o heartbeat com snapshot do cache. Se o app crashar
      // silenciosamente (OOM nativo do WebView, kill da OS por janela de
      // input perdida), o boot seguinte detecta via timestamp do
      // heartbeat e registra um entry `silent_crash` com este snapshot —
      // assim o overlay mostra "tinha N baixando + M salvando quando
      // morreu" e a gente entende a causa raiz. (Renato 2026-05-28)
      //
      // activeBlobUrls = suspeito principal de OOM nativo em Tizen (v1.0.49).
      // Cada blob URL alive segura ~40 MB de RAM nativa. Cap em Tizen é 2,
      // então esperamos 1-2; se aparecer >3 num crash, bug de revoke.
      setHeartbeatContext({
        cached,
        downloading,
        saving,
        errors,
        activeBlobUrls: mediaStore.activeBlobUrlCount,
      });

      // Só re-renderiza (setCacheInfo) com o painel de debug aberto. Sem isso o
      // Stream inteiro re-renderizava a cada 5s à toa (Renato 2026-07-05,
      // gargalo A7). O heartbeat acima (crash detection) roda SEMPRE.
      if (!showMediaInfoRef.current) return;

      setCacheInfo({
        cached,
        downloading,
        saving,
        pending,
        errors,
        lastError: mediaStore.lastCacheError,
        lastErrorAt: mediaStore.lastCacheErrorAt,
        totalRemovals: mediaStore.totalRemovals,
        lastRemovalSource: mediaStore.lastRemovalSource,
        lastRemovalCount: mediaStore.lastRemovalCount,
        lastRemovalAt: mediaStore.lastRemovalAt,
        checksumMismatchCount: mediaStore.checksumMismatchCount,
        lastChecksumMismatch: mediaStore.lastChecksumMismatch,
        crashCount: crashes.length,
        lastCrashMessage: lastCrash?.message ?? null,
        lastCrashAt: lastCrash?.timestamp ?? null,
        lastCrashStack: lastCrash?.stack ?? null,
        downloads,
        cachedFiles,
      });
    };

    updateCacheInfo();
    // 2026-05-28 (v1.0.69) — interval AUMENTADO de 1s pra 5s pra reduzir
    // carga de CPU no Tizen Consumer 9.0 (UN50U8600FGXZD). updateCacheInfo
    // itera mediaStore.cacheStatus + streamStore.playlist + lê localStorage
    // (getRecentCrashes) + dispara React re-render do Stream completo a
    // cada tick. Em CPU fraco (TV vs celular), 1s causava watchdog kill
    // pela OS após acumular GC pressure. 5s mantém UX do debug overlay
    // aceitável (operador não precisa ver progresso real-time milissegundo).
    const interval = setInterval(updateCacheInfo, 5000);
    return () => clearInterval(interval);
  }, [mediaStore, streamStore]);

  useEffect(() => {
    // Telemetria de volume só alimenta o painel de debug (MediaInfo) → só roda
    // com ele aberto (Renato 2026-07-05, gargalo A7). Sem gate, cada tick de 5s
    // (setCurrentVolume/setDefaultVolume) re-renderizava o Stream inteiro.
    if (!showMediaInfo) return;
    let isMounted = true;

    if (platform.platform === 'browser') {
      setCurrentVolume(null);
      setDefaultVolume(null);
      return () => {
        isMounted = false;
      };
    }

    const updateVolume = async () => {
      const updateStoredVolume = () => {
        const storedConfig = localStorage.getItem(CONFIG_KEY);
        if (storedConfig) {
          try {
            const parsed = JSON.parse(storedConfig) as { volumeLevel?: number };
            const storedVolume = parsed.volumeLevel;
            console.log('[Stream] Loaded config from localStorage:', {
              volumeLevel: storedVolume,
            });
            setDefaultVolume(
              typeof storedVolume === 'number' ? storedVolume : null,
            );
          } catch (error) {
            console.warn('[Stream] Failed to parse stored config:', error);
            setDefaultVolume(null);
          }
        } else {
          setDefaultVolume(null);
        }
      };

      try {
        const volume = await volumeServiceRef.current.getVolume();
        if (!isMounted) return;
        setCurrentVolume(volume >= 0 ? volume : null);
        updateStoredVolume();
      } catch (error) {
        if (!isMounted) return;
        console.warn('[Stream] Failed to read volume:', error);
        setCurrentVolume(null);
        updateStoredVolume();
      }
    };

    updateVolume();
    volumeIntervalRef.current = setInterval(updateVolume, 5000);

    return () => {
      isMounted = false;
      if (volumeIntervalRef.current) {
        clearInterval(volumeIntervalRef.current);
      }
    };
  }, [platform.platform, showMediaInfo]);

  // Polling do brilho de hardware (5s). Em Tizen consumer sempre retorna
  // null — plataforma não expõe; o painel cai pro overlay como fallback.
  useEffect(() => {
    // Brilho hw só alimenta o painel de debug → só com ele aberto
    // (Renato 2026-07-05, gargalo A7).
    if (!showMediaInfo) return;
    let isMounted = true;
    let intervalId: ReturnType<typeof setInterval> | null = null;

    const update = async () => {
      try {
        const value = await platform.getHardwareBrightness();
        if (!isMounted) return;
        setHardwareBrightness(value);
      } catch (error) {
        if (!isMounted) return;
        console.warn('[Stream] Failed to read hardware brightness:', error);
        setHardwareBrightness(null);
      }
    };

    update();
    intervalId = setInterval(update, 5000);

    return () => {
      isMounted = false;
      if (intervalId) clearInterval(intervalId);
    };
  }, [platform, showMediaInfo]);

  // Polling do overlay CSS brightness (2s). React não observa
  // mudanças em overlay.style.opacity disparadas pelo BrightnessService —
  // o painel mostrava sempre o valor inicial (100%). Polling resolve sem
  // precisar de listener/observer no DOM. 2s = balance entre responsividade
  // visual e overhead (lê 1 atributo CSS).
  useEffect(() => {
    // Brilho overlay (valor exibido) só alimenta o painel de debug → só com ele
    // aberto (Renato 2026-07-05, gargalo A7). NÃO confundir com o overlay REAL
    // de dimming, que é aplicado pelo BrightnessService (independe deste poll).
    if (!showMediaInfo) return;
    let isMounted = true;
    const brightnessSvc = BrightnessService.getInstance();

    const update = () => {
      if (!isMounted) return;
      setOverlayBrightnessState(brightnessSvc.getOverlayBrightness());
    };

    update();
    const intervalId = setInterval(update, 2000);

    return () => {
      isMounted = false;
      clearInterval(intervalId);
    };
  }, [showMediaInfo]);

  // Handler para tecla "H" (apenas browser)
  useEffect(() => {
    if (platform.platform !== 'browser') {
      return () => {}; // Cleanup vazio para manter consistência de hooks
    }

    const handleKeyPress = (event: KeyboardEvent): void => {
      if (event.keyCode === KEY_H) {
        setShowMediaInfo((prev) => !prev);
        // Cancela auto-hide se estava agendado
        if (mediaInfoTimeoutRef.current) {
          clearTimeout(mediaInfoTimeoutRef.current);
        }
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [platform.platform]);

  // Handler para toque/clique na tela — toggle MediaInfo IMEDIATO.
  //
  // v1.0.129 (Renato 2026-06-02): REMOVIDO double-click → fullscreen.
  // Renato reportou que double-click no SM-X520 "aumentava a tela tipo um zoom"
  // (era o requestFullscreen do WebView expandindo). Comportamento confuso pro
  // operador. Fullscreen continua acessível via botão dedicado no MediaInfo
  // overlay (linha ~2389).
  //
  // Removido também o setTimeout de 300ms (esperava double-click) — sem
  // double-click, não há razão pra delay. Single-click vira toggle imediato.
  // Removido refs órfãos: lastClickTimeRef, clickTimeoutRef, tapCountRef,
  // lastTapTimeRef, tapResetTimeoutRef (mantidos no código pra evitar quebrar
  // referências em outros lugares, mas não são mais lidos aqui).
  const handleContainerClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (kioskStore.isLocked) return;

      // Ignora cliques em botões e elementos interativos
      const target = event.target as HTMLElement;
      if (
        target.tagName === 'BUTTON' ||
        target.closest('button') ||
        target.classList.contains('focusable')
      ) {
        return;
      }

      // Toggle MediaInfo imediato. Auto-hide em 4s acontece via useEffect.
      setShowMediaInfo((prev) => !prev);
      if (mediaInfoTimeoutRef.current) {
        clearTimeout(mediaInfoTimeoutRef.current);
      }
    },
    [],
  );

  // Auto-focus the selected orientation button when MediaInfo becomes visible
  useEffect(() => {
    if (showMediaInfo) {
      // Focus the currently selected orientation button
      const buttonToFocus =
        effectiveOrientation === 'landscape' ? 'landscape' : 'portrait';
      setFocusedButton(buttonToFocus);

      // Apply DOM focus
      const ref =
        buttonToFocus === 'landscape' ? landscapeButtonRef : portraitButtonRef;
      ref.current?.focus();
    } else {
      // Clear focus state when MediaInfo is hidden
      setFocusedButton(null);
    }
  }, [showMediaInfo, effectiveOrientation]);

  // Cleanup on unmount
  // 2026-06-12 (Renato): buffering watchdog declarado AQUI (alto no escopo)
  // porque é usado em useEffects de cleanup logo abaixo. Lógica do watchdog
  // (start/check) está em handleWaiting/handlePlaying mais pra baixo.
  //
  // CRÍTICO (Renato 2026-06-13): o watchdog SÓ pode disparar ANTES da
  // primeira reprodução do item. Re-buffer no MEIO do vídeo (rede ruim
  // em loja, congestão no streaming online) NÃO pode pular pro próximo
  // — operador prometeu N exibições ao anunciante e o vídeo precisa
  // terminar mesmo com gagueira/re-buffer. Flag `hasEverPlayedRef`
  // tracka isso: vira `true` no 1º `onPlaying` do item, reset ao trocar
  // de mídia. Watchdog só agenda timer se `hasEverPlayedRef.current === false`.
  //
  // Cenário coberto: boot offline com cache local que não consegue tocar
  // por algum motivo (URL inválida, filesystem path errado, etc) — em vez
  // de buffering eterno, avança pro próximo item em 30s + log de diagnóstico.
  const BUFFERING_WATCHDOG_MS = 30_000;
  const bufferingWatchdogRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const hasEverPlayedRef = useRef<boolean>(false);
  /** Exibição (id da mídia + início do item) que já ganhou a carência após erro de abertura. */
  const videoOpenRetriedForRef = useRef<string | null>(null);
  // BOOT-BLACK FIX (2026-08-17): o 1º <video> após um reload fica preso em
  // NETWORK_NO_SOURCE (readyState=0). Causa provada por VIDTRACE: o swap
  // REMOTE→CACHE (getLocalUrlAsync gateia em cacheStatus, vazio no boot) roda a
  // seleção de recurso do WebView com src vazio → ele desiste do source e NÃO
  // re-tenta sozinho. `video.load()` re-dispara a seleção e recupera na hora
  // (é exatamente o que o teardown do advance watchdog fazia aos ~16s, tarde).
  // Este "load-kick" recupera em ~3s SEM pular o slot. Idempotente por slot.
  const loadKickDoneRef = useRef<boolean>(false);
  const loadKickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** canPlay disparou — habilita stall recovery antes do evento playing. */
  const canPlayReachedRef = useRef<boolean>(false);
  /** EARLY FREEZE (Renato 2026-07-16): armado 1× por exibição de vídeo (reset no
   *  canPlay/troca de mídia) — mostra o freeze antes do fim p/ zerar a piscada preta. */
  const earlyFreezeDoneRef = useRef(false);
  /** PHASE 1.1 — EARLY HOLD armado 1× por exibição de vídeo (reset na troca de mídia
   *  / canPlay). Evita re-disparar o hold no mesmo slot. */
  const earlyHoldDoneRef = useRef(false);
  /** rAF do probe de fim (gatilho FINO do early-freeze — o timeupdate é grosso ~234ms). */
  const endProbeRafRef = useRef<number | null>(null);
  const clearBufferingWatchdog = useCallback((): void => {
    if (bufferingWatchdogRef.current) {
      clearTimeout(bufferingWatchdogRef.current);
      bufferingWatchdogRef.current = null;
    }
  }, []);

  // FIX B — WATCHDOG DE AVANÇO (rede de segurança cross-platform, 2026-07-20).
  //
  // Distinto do buffering watchdog acima:
  //   • buffering watchdog: arma no evento `waiting`, só vídeo, 30s.
  //   • advance watchdog:  arma no START de TODO slot principal (vídeo E imagem),
  //     e dispara em max(10s, duraçãoDoSlot + 6s) se a mídia principal NUNCA
  //     ficar tocável.
  //
  // Causa raiz coberta (bug crítico de prod, Tizen): com um MEDIA_OVERLAY de
  // vídeo tocando, o <video> PRINCIPAL ficava preso em readyState=0 /
  // currentTime=0 pra sempre (decoder faminto) sem nem emitir
  // `waiting`/`playing`/`ended` → o slot congelava eternamente e o buffering
  // watchdog (que depende do evento `waiting`) não cobria. Este net arma no
  // início do slot e garante que a TV NUNCA fica presa — mesmo se a causa for
  // source-fail, decoder, etc.
  //
  // CONSERVADOR — ZERO efeito colateral em reprodução normal (decisão Renato):
  //   • grace GENEROSO (max(10s, slot+6s)) pra TVs SSSP fracas que decodificam
  //     devagar;
  //   • cancela IMEDIATAMENTE no 1º de: loadedmetadata/loadeddata/canplay/
  //     playing/timeupdate(currentTime>0) [vídeo] ou onLoad [imagem];
  //   • só dispara em STALL REAL: readyState < HAVE_CURRENT_DATA (2) E
  //     currentTime == 0 (não avançou) — conteúdo lento-mas-válido (readyState
  //     subindo / loadeddata disparou) NUNCA é pulado;
  //   • idempotente por slot (version-gated via handleMediaEnd) — re-arma na
  //     troca de slot, limpa no unmount, NÃO re-arma no mesmo slot → sem loop de
  //     recovery e sem double-advance com a stall-recovery/decode-error;
  //   • escopo: SÓ o slot principal do Stream (pula triggers e zonas).
  const ADVANCE_WATCHDOG_FLOOR_MS = 10_000;
  // BOOT-BLACK FIX — delays do load-kick. 1º kick ~0,5s (um vídeo de cache
  // saudável já passou de readyState=0 em ~150ms), 2º kick +0,8s se persistir.
  // Recupera o 1º vídeo preso muito antes do skip de ~16s do advance watchdog.
  const LOAD_KICK_MS = 500;
  const LOAD_KICK_RETRY_MS = 800;
  const advanceWatchdogRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearAdvanceWatchdog = useCallback((): void => {
    if (advanceWatchdogRef.current) {
      clearTimeout(advanceWatchdogRef.current);
      advanceWatchdogRef.current = null;
    }
  }, []);
  const clearLoadKick = useCallback((): void => {
    if (loadKickTimerRef.current) {
      clearTimeout(loadKickTimerRef.current);
      loadKickTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      console.log(
        '[Stream] 🧹 Component unmounting - cleaning up timeouts only (playlist preserved)',
      );
      clearBufferingWatchdog();
      clearAdvanceWatchdog();
      clearLoadKick();
      // PHASE 1 — limpa o timer de desmonte do vídeo segurado (hold-frame).
      if (heldVideoUnmountTimerRef.current) {
        clearTimeout(heldVideoUnmountTimerRef.current);
        heldVideoUnmountTimerRef.current = null;
      }
    };
  }, [clearBufferingWatchdog, clearAdvanceWatchdog, clearLoadKick]);

  // PHASE 1 — HOLD-FRAME safety net: limpa o estado de hold sempre que o slot atual
  // deixa de ser IMAGEM (próximo slot é vídeo/outro, ou a imagem foi pulada). Garante
  // que o próximo `<video>` real use o `videoRef` limpo e cobre o caso raro de o
  // `nextMedia` previsto no handleVideoEnded ter divergido do slot que de fato entrou.
  // No-op fora do hold (Android/browser nunca ligam `heldVideoForImage`).
  useEffect(() => {
    if (heldVideoForImage && streamStore.currentMedia?.type !== 'image') {
      setHeldVideoForImage(false);
      setHeldVideoHidden(false);
      if (heldVideoUnmountTimerRef.current) {
        clearTimeout(heldVideoUnmountTimerRef.current);
        heldVideoUnmountTimerRef.current = null;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streamStore.currentMedia?.id]);

  // Reset do watchdog + flag `hasEverPlayed` ao trocar de mídia.
  // Cada novo item da playlist começa do zero: pode levar até 30s pra
  // iniciar (cache local quebrado, stream lento etc.), mas re-buffer
  // depois de ter tocado uma vez NÃO ativa o watchdog.
  useEffect(() => {
    clearBufferingWatchdog();
    clearLoadKick();
    loadKickDoneRef.current = false;
    hasEverPlayedRef.current = false;
    canPlayReachedRef.current = false;
    earlyFreezeDoneRef.current = false; // re-arma o early-freeze na troca de mídia
    earlyHoldDoneRef.current = false; // PHASE 1.1 — re-arma o early-hold na troca
    setIsEarlyFreeze(false);
    if (endProbeRafRef.current != null) {
      cancelAnimationFrame(endProbeRafRef.current);
      endProbeRafRef.current = null;
    }
    deferCacheUpgradeRef.current = false;
    playbackStallRef.current = {
      mediaId: null,
      lastTime: 0,
      sinceMs: 0,
      recovery: 'none',
    };
    if (waitingRecoveryTimerRef.current) {
      clearTimeout(waitingRecoveryTimerRef.current);
      waitingRecoveryTimerRef.current = null;
    }
  }, [streamStore.currentMedia?.id, clearBufferingWatchdog, clearLoadKick]);

  // FIX B — arma o watchdog de avanço no início de CADA slot de VÍDEO (vide bloco
  // de constantes acima). Re-arma a cada troca de mídia; cancela no cleanup.
  useEffect(() => {
    clearAdvanceWatchdog();
    // Escopo: SÓ o slot principal do Stream, e SÓ vídeo.
    // - Item de zona → ZonePlayer tem avanço próprio (timer de zoneMax, ~1710).
    // - Trigger → conteúdo interruptivo com ciclo próprio (TriggerStore); não
    //   é o loop principal e não deve ser pulado por este net.
    // - IMAGEM → já coberta pelo safety-timeout de 10s existente (effect de
    //   imagem, ~1730, `handleMediaEnd(undefined)`). NÃO armar um 2º timer aqui
    //   pra imagem: os dois disparariam por volta de 10s (o de imagem é
    //   `undefined`, sem version-guard) → risco de DOUBLE-ADVANCE. O watchdog
    //   cobre o gap que o de imagem NÃO cobre: o <video> preso no início.
    if (
      syncStore.hasMultipleZones &&
      streamStore.currentMedia?.zonaId != null
    ) {
      return;
    }
    if (streamStore.isTriggerPlayback) return;
    const media = streamStore.currentMedia;
    if (!media || media.type !== 'video') return;

    const timeoutMs = computeAdvanceWatchdogTimeoutMs(
      media.duration,
      ADVANCE_WATCHDOG_FLOOR_MS,
    );
    const version = streamStore.playlistVersion;
    const mediaId = media.id;

    advanceWatchdogRef.current = setTimeout(() => {
      advanceWatchdogRef.current = null;
      // Troca de mídia limpa o timer; defensivo contra callback stale.
      if (streamStore.currentMedia?.id !== mediaId) return;

      const video = videoRef.current;
      const fire = shouldAdvanceWatchdogFire({
        mediaType: 'video',
        hasEverPlayed: hasEverPlayedRef.current,
        videoReadyState: video ? video.readyState : null,
        videoCurrentTime: video ? video.currentTime : null,
        imageLoaded: false,
      });
      if (!fire) return;

      otaLog.warn(
        'Stream advance watchdog — vídeo travado no início, avançando slot',
        {
          contentId: mediaId.slice(0, 8),
          readyState: video?.readyState ?? '?',
          currentTime: video?.currentTime?.toFixed?.(2) ?? '?',
          timeoutMs,
        },
      );
      // Vídeo que nunca começou é falha: passa pelo freio de cascata (mesmo caminho do
      // buffering watchdog). version rejeita avanços de playlist antiga.
      streamStore.handleMediaFailure('advance-watchdog', version);
    }, timeoutMs);

    // BOOT-BLACK FIX (2026-08-17) — LOAD-KICK: RECUPERA o 1º <video> preso (não pula).
    // Causa raiz provada (só Android): `applyVideoSource` faz
    // `await releaseAndroidVideoDecoder(video)` (que faz `removeAttribute('src')` →
    // src VAZIO) e logo depois `if (cancelled) return`. Na tempestade de re-renders
    // do boot/reload, o effect é cancelado DURANTE o await → bail deixando o `<video>`
    // com `src=""` (NETWORK_EMPTY) → preto eterno até o skip de ~16s. (No browser não
    // ocorre: lá o applyVideoSource seta src direto, sem release/removeAttribute.)
    // Recuperação: se ~0,5s após o start o vídeo ainda está readyState=0 e nunca tocou,
    // RE-RESOLVE a url (cache-first via mediaStore) e RE-APLICA o src → carrega em ~ms.
    // Guard SUPER específico (rs=0 + ct=0 + nunca tocou) → NUNCA age em playback normal
    // (um vídeo de cache saudável já passou de rs=0 em ~150ms).
    if (isLoadKickPlatform(platform.platform)) {
      const kickMedia = media;
      const kick = async (stage: number): Promise<void> => {
        loadKickTimerRef.current = null;
        const video = videoRef.current;
        if (!video) return;
        // Guard puro + testado (playbackHelpers.shouldLoadKickFire): só dispara em
        // slot ainda atual, nunca-tocado, readyState=0 e currentTime=0 (o <video>
        // preso pela race do applyVideoSource). NUNCA age em playback normal.
        const fire = shouldLoadKickFire({
          stillCurrent: streamStore.currentMedia?.id === mediaId,
          hasEverPlayed: hasEverPlayedRef.current,
          videoReadyState: video.readyState,
          videoCurrentTime: video.currentTime,
        });
        if (!fire) return;
        loadKickDoneRef.current = true;
        const hadSrc = !!video.src;
        otaLog.warn('Stream load-kick — <video> preso, recuperando', {
          stage,
          contentId: mediaId.slice(0, 8),
          rs: video.readyState,
          ns: video.networkState,
          hadSrc,
        });
        try {
          if (!hadSrc) {
            // src esvaziado pela race → RE-RESOLVE com a MESMA lógica do usePlayableUrl
            // (`resolvePlayableUrl`: cache-first; se NÃO tem cache → URL remota =
            // STREAMING online) e re-aplica com o crossOrigin correto por source.
            const { url, source } = await resolvePlayableUrl(kickMedia);
            if (
              streamStore.currentMedia?.id !== mediaId ||
              hasEverPlayedRef.current
            )
              return;
            const v2 = videoRef.current;
            if (!v2) return;
            if (url) {
              applyMediaCrossOrigin(v2, source);
              v2.src = url;
            }
            v2.load();
            v2.play().catch(() => undefined);
          } else {
            video.load();
            video.play().catch(() => undefined);
          }
        } catch (e) {
          otaLog.error('Stream load-kick falhou', { err: String(e) });
        }
        if (stage < 2 && !hasEverPlayedRef.current) {
          loadKickTimerRef.current = setTimeout(
            () => void kick(2),
            LOAD_KICK_RETRY_MS,
          );
        }
      };
      loadKickTimerRef.current = setTimeout(() => void kick(1), LOAD_KICK_MS);
    }

    return () => {
      clearAdvanceWatchdog();
      clearLoadKick();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    streamStore.currentMedia?.id,
    streamStore.isTriggerPlayback,
    syncStore.hasMultipleZones,
    clearAdvanceWatchdog,
    clearLoadKick,
  ]);

  // Resume video playback when app returns from background
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        console.log('[Stream] 📱 App went to background');
      } else {
        console.log(
          '[Stream] 📱 App returned to foreground - resuming playback',
        );
        const video = videoRef.current;
        if (video && streamStore.currentMedia?.type === 'video') {
          // Check if video is paused and should be playing
          if (video.paused && video.src) {
            console.log('[Stream] ▶️ Resuming video playback');
            video.play().catch((err) => {
              console.warn('[Stream] Failed to resume video:', err.message);
            });
          }
        }
        // Reconnect SSE (browsers kill EventSource in background tabs)
        syncStore.ensureSSEConnected();
        // Also trigger a sync to check for updates
        syncStore.sync();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [streamStore, syncStore]);

  // Embedded (§6/D2): host escondeu/mostrou a EffluxView → congela/retoma o
  // <video>. `isHostHidden` default false → NÃO roda em tizen/webos/web/android
  // (só o comando hide/stop do canal embedded seta). Como o advance-watchdog
  // re-playa vídeo pausado, seguramos pausado re-pausando no evento 'play'.
  // ⚠️ Guarda `wasHostHiddenRef`: no MOUNT (isHostHidden=false) NÃO faz nada —
  // só age nas TRANSIÇÕES (hide→pausa, show→retoma). Sem isso, o mount daria
  // video.play() num vídeo pausado em transição → glitch na frota. Regressão-safe.
  const wasHostHiddenRef = useRef(false);
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (streamStore.isHostHidden) {
      wasHostHiddenRef.current = true;
      const keepPaused = (): void => {
        if (streamStore.isHostHidden && !video.paused) video.pause();
      };
      video.pause();
      video.addEventListener('play', keepPaused);
      return () => video.removeEventListener('play', keepPaused);
    }
    // isHostHidden=false: só retoma se ESTAVA hidden (transição real hide→show).
    if (wasHostHiddenRef.current) {
      wasHostHiddenRef.current = false;
      if (
        video.paused &&
        video.src &&
        streamStore.currentMedia?.type === 'video'
      ) {
        video.play().catch(() => undefined);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streamStore.isHostHidden]);

  const handlePlayFailure = useCallback(
    (error: unknown, context: string): void => {
      // Ignore "The play() request was interrupted by a new load request"
      // This is a common AbortError when switching media rapidly.
      if (error instanceof Error && error.name === 'AbortError') {
        return;
      }

      const message =
        error instanceof Error ? error.message : 'Erro ao iniciar video';
      console.warn(`[Stream] Play failed (${context}):`, message);

      if (
        message.includes('no supported sources') ||
        message.includes('NotSupportedError')
      ) {
        streamStore.handleError('Formato de mídia não suportado');
      }
    },
    [streamStore],
  );

  // Update video source when current media changes
  useEffect(() => {
    // Zera loop control ao trocar mídia — playbackStartTime stale do slot
    // anterior fazia wall-clock polyfill avançar com currentTime=0.
    videoLoopControlRef.current = {
      shouldManualLoop: false,
      shouldCutShort: false,
      scheduledDuration: 0,
      actualDuration: 0,
      playbackStartTime: 0,
    };

    // Reset animation timing for smooth speed transitions (preserve current speed)
    animationTimingRef.current = {
      startTime: Date.now(),
      speed: animationTimingRef.current.speed,
      consumedTime: 0,
    };

    const video = videoRef.current;
    if (!video || streamStore.currentMedia?.type !== 'video') return;
    // MOTOR AGENDADO: esta mídia entrou pelo swap A/B — o elemento ativo JÁ está tocando
    // ela. Recarregar o src aqui reiniciaria o vídeo, o engasgo que o swap existe pra evitar.
    if (
      scheduledSwapForMediaIdRef.current !== null &&
      scheduledSwapForMediaIdRef.current === streamStore.currentMedia.id
    ) {
      return;
    }

    let cancelled = false;

    const applyVideoSource = async (): Promise<void> => {
      if (!isUrlLoading && playableUrl) {
        // v1.0.33: NÃO seta isVideoLoading=true aqui. O handleLoadStart
        // (chamado pelo browser quando video.load() roda) faz com delay
        // de 350ms — em transições rápidas (cache local, gapless Tizen)
        // o overlay nunca aparece visualmente.
        streamStore.setPlaying(false);
        if (decodeErrorTimerRef.current) {
          clearTimeout(decodeErrorTimerRef.current);
          decodeErrorTimerRef.current = null;
        }

        if (isAndroidWebView(platform.platform) && video.src) {
          await releaseAndroidVideoDecoder(video);
        }
        if (cancelled) return;

        applyMediaCrossOrigin(video, playbackSource);
        video.src = playableUrl;
        video.load();
        video.play().catch((err) => {
          handlePlayFailure(err, 'autoplay');
          setIsVideoLoading(false);
        });
      } else if (isUrlLoading) {
        // URL ainda resolvendo (IndexedDB read ou remote fetch). NÃO ligar
        // o overlay "Carregando vídeo..." aqui — já existe o overlay
        // "Resolvendo mídia..." separado (controlado por isUrlLoading direto
        // no JSX, linha ~1835) que aparece naturalmente nesta fase.
        //
        // BUG v1.0.33: setar isVideoLoading=true aqui causava flash do
        // overlay "Carregando vídeo..." em TODA transição entre mídias,
        // mesmo com cache local de blob URL (resolução em <50ms). O
        // handleCanPlay só desliga o overlay quando o vídeo carrega — entre
        // o setIsVideoLoading(true) aqui e o handleCanPlay passavam ~200ms
        // visíveis. O fix de v1.0.33 (timer 350ms no handleLoadStart) não
        // atacava esse caminho.
        streamStore.setPlaying(false);
      }
    };

    void applyVideoSource();

    return () => {
      cancelled = true;
    };
  }, [
    streamStore.currentMedia,
    streamStore,
    handlePlayFailure,
    playableUrl,
    isUrlLoading,
    platform.platform,
    playbackSource,
  ]);

  // Recovery: re-apply video src after exiting blocked state.
  //
  // When the admin blocks the player, syncStore.isPlayerBlocked turns true
  // and this scene renders the NoContent branch — which unmounts <VideoPlayer>
  // (videoRef goes null). On unblock, playerStatus voltou a 'active', the
  // video branch renders again and a fresh <video> element is mounted. But
  // the "Update video source when current media changes" effect above does
  // NOT re-fire because its deps (currentMedia, playableUrl, isUrlLoading)
  // didn't change — sync.store.ts intentionally preserves the playlist during
  // block (see comment line ~543 there) and setPlaylist is idempotent when
  // the content is identical (stream.store.ts line ~116). Result: fresh
  // <video> element with no src → black screen, even though sync, SSE and
  // overlays keep working.
  //
  // We track previous block state and, on the unblock transition, re-apply
  // src/load/play to the freshly-mounted video element. No-op if conditions
  // aren't right (currentMedia not video, no playableUrl yet, etc.) — the
  // main effect above will pick it up when playableUrl resolves.
  const wasBlockedRef = useRef(syncStore.isPlayerBlocked);
  useEffect(() => {
    const justUnblocked = wasBlockedRef.current && !syncStore.isPlayerBlocked;
    wasBlockedRef.current = syncStore.isPlayerBlocked;
    if (!justUnblocked) return;

    const video = videoRef.current;
    if (
      video &&
      streamStore.currentMedia?.type === 'video' &&
      playableUrl &&
      !isUrlLoading
    ) {
      console.log(
        '[Stream] 🔓 Player desbloqueado — reaplicando src no <video> remontado',
      );
      void (async () => {
        if (isAndroidWebView(platform.platform) && video.src) {
          await releaseAndroidVideoDecoder(video);
        }
        applyMediaCrossOrigin(video, playbackSource);
        video.src = playableUrl;
        video.load();
        video.play().catch((err) => {
          handlePlayFailure(err, 'unblock-resume');
          setIsVideoLoading(false);
        });
      })();
    }
  }, [
    syncStore.isPlayerBlocked,
    streamStore.currentMedia,
    playableUrl,
    isUrlLoading,
    handlePlayFailure,
    platform.platform,
    playbackSource,
  ]);

  // Renato 2026-06-12: mesmo recovery do "unblock" acima, mas pra transição
  // pending_approval → approved.
  //
  // Cenário (bug "tela preta após admin deletar + re-aprovar"):
  // 1) Player aprovado e tocando.
  // 2) Admin deleta player no admin.
  // 3) auth.store detecta 404 → registrationStatus='pending_approval'.
  // 4) Render branch acima (linha ~2422) muda pra <NoContent registrationStatus='pending_approval'>.
  //    <VideoPlayer> é UNMOUNTED — videoRef vira null.
  // 5) Admin aprova → registrationStatus='approved'.
  // 6) Branch volta pra <VideoPlayer> — <video> element é REMOUNTED (fresh).
  // 7) setPlaylist recebe a mesma playlist → idempotent return → src effect não dispara.
  // 8) Resultado: <video> sem src → tela preta. Apenas F5 resolvia.
  //
  // Fix: detecta a transição pending_approval (registering/initializing) → approved
  // e força o src/load/play no <video> remontado. Reusa a mesma lógica do
  // wasBlockedRef acima.
  const wasInPendingStateRef = useRef(
    authStore.registrationStatus !== 'approved',
  );
  useEffect(() => {
    const isPendingNow = authStore.registrationStatus !== 'approved';
    const justApproved = wasInPendingStateRef.current && !isPendingNow;
    wasInPendingStateRef.current = isPendingNow;
    if (!justApproved) return;

    const video = videoRef.current;
    if (
      video &&
      streamStore.currentMedia?.type === 'video' &&
      playableUrl &&
      !isUrlLoading
    ) {
      console.log(
        '[Stream] ✅ Device aprovado — reaplicando src no <video> remontado',
      );
      void (async () => {
        if (isAndroidWebView(platform.platform) && video.src) {
          await releaseAndroidVideoDecoder(video);
        }
        applyMediaCrossOrigin(video, playbackSource);
        video.src = playableUrl;
        video.load();
        video.play().catch((err) => {
          handlePlayFailure(err, 'post-approval-resume');
          setIsVideoLoading(false);
        });
      })();
    }
  }, [
    authStore.registrationStatus,
    streamStore.currentMedia,
    playableUrl,
    isUrlLoading,
    handlePlayFailure,
    platform.platform,
    playbackSource,
  ]);

  // Multi-zone: mark as playing so debug overlay doesn't show "Aguardando"
  useEffect(() => {
    if (
      syncStore.hasMultipleZones &&
      streamStore.currentMedia?.zonaId != null &&
      streamStore.playlist.length > 0
    ) {
      streamStore.setPlaying(true);
    }
  }, [
    syncStore.hasMultipleZones,
    streamStore.playlist.length,
    streamStore.currentMedia?.zonaId,
  ]);

  // Multi-zone timeline clock for MediaInfo overlay (só mídia multi-zona)
  useEffect(() => {
    // Só alimenta o painel de debug → só roda com ele aberto (Renato 2026-07-05,
    // gargalo A7): antes, setZoneTimelineSec re-renderizava o Stream 2×/s à toa.
    if (!showMediaInfo) return;
    const current = streamStore.currentMedia;
    if (!syncStore.hasMultipleZones || current?.zonaId == null) return;

    const activeZones = activeZonesForCampaign(
      syncStore.zones,
      streamStore.playlist,
      current.campanhaId,
    );
    const globalMax = zoneGroupMaxDurationSec(
      streamStore.playlist,
      activeZones,
    );
    if (globalMax <= 0) return;

    zoneTimelineStartRef.current = performance.now();
    const interval = setInterval(() => {
      const elapsed =
        ((performance.now() - zoneTimelineStartRef.current) / 1000) % globalMax;
      setZoneTimelineSec(Math.floor(elapsed));
    }, 500);
    return () => clearInterval(interval);
  }, [
    showMediaInfo,
    syncStore.hasMultipleZones,
    syncStore.zones,
    streamStore.playlist,
    streamStore.currentMedia?.id,
    streamStore.currentMedia?.zonaId,
    streamStore.currentMedia?.campanhaId,
  ]);

  // Multi-zone auto-advance: when current item has zonaId, calculate the
  // zone group duration (max of sibling durations) and advance after that.
  useEffect(() => {
    const current = streamStore.currentMedia;
    if (!current || current.zonaId == null) return;
    let zoneMax = current.duration || 10;
    for (const item of streamStore.playlist) {
      if (item.zonaId != null && item.campanhaId === current.campanhaId) {
        zoneMax = Math.max(zoneMax, item.duration || 10);
      }
    }
    console.log(
      `[Stream] Zone group timer: ${zoneMax}s for campaign ${current.campanhaId}`,
    );
    const timer = setTimeout(() => {
      // MESMA REGRA DE TRANSIÇÃO p/ ZONAS: captura o composite das zonas AGORA
      // (elementos ainda montados) pra capa cobrir a saída do layout — igual
      // imagem→X captura no load. Sem isso, zona→fullscreen piscava preto.
      console.info('[Stream] zone slot END → captura composite + playNext');
      captureZonesToHeldCanvas();
      streamStore.playNext();
    }, zoneMax * 1000);
    return () => clearTimeout(timer);
  }, [streamStore.currentMedia?.id, streamStore.currentMedia?.zonaId]);

  useEffect(() => {
    // BUGFIX (2026-06-26): só pula o ciclo de imagem (loading + safety-net de
    // 10s que avança via handleMediaEnd) quando o conteúdo ATUAL é renderizado
    // pelo ZonePlayer (i.e., é item DE zona). Antes, `hasMultipleZones` sozinho
    // desligava o safety-net pra QUALQUER imagem — inclusive imagem SEM zona num
    // player que apenas COMPARTILHA uma campanha zonada → a imagem ficava sem
    // nenhum fallback de avanço e o loop CONGELAVA nela (freeze determinístico,
    // cross-platform — CENTRUM em prod). Espelha a condição de render
    // (~linha 3368: hasMultipleZones && zonaId != null ? ZonePlayer : viewport único).
    if (syncStore.hasMultipleZones && streamStore.currentMedia?.zonaId != null)
      return;
    if (!streamStore.currentMedia) return;
    if (streamStore.currentMedia.type !== 'image') return;

    // A MESMA imagem já está decodificada na tela → NÃO remonta o BlackTransition
    // (que forçaria re-decode do bitmap toda volta). Só re-arma o avanço via
    // handleImageLoad; a imagem já está pintada. Avanço/analytics idênticos.
    const shownImgEl = imgRef.current;
    if (
      shownImgEl &&
      shouldReuseDecodedImageOnLoop({
        displayedImageId: displayedImageIdRef.current,
        currentMediaId: streamStore.currentMedia.id,
        imgComplete: shownImgEl.complete,
        imgNaturalWidth: shownImgEl.naturalWidth,
      })
    ) {
      if (imageSafetyTimeoutRef.current) {
        clearTimeout(imageSafetyTimeoutRef.current);
      }
      handleImageLoadRef.current?.();
      return () => {
        if (imageSafetyTimeoutRef.current) {
          clearTimeout(imageSafetyTimeoutRef.current);
        }
        if (imageTimerRef.current) {
          clearTimeout(imageTimerRef.current);
        }
      };
    }

    console.log(
      '[Stream] Image changed, setting loading state:',
      streamStore.currentMedia.url,
    );
    setIsImageLoading(true);
    streamStore.setPlaying(false);

    if (imageTimerRef.current) {
      clearTimeout(imageTimerRef.current);
    }

    // Safety timeout: silently advance after 10s if image never loads.
    // Uses handleMediaEnd (not handleError) to avoid showing the error overlay
    // on screen — for digital signage, silently skipping is better than
    // flashing "Timeout ao carregar imagem" for 3 seconds.
    imageSafetyTimeoutRef.current = setTimeout(() => {
      const stillResolvingUrl = isUrlLoadingRef.current;
      console.warn(
        `[Stream] Image load timeout (10s), advancing. isUrlLoading=${stillResolvingUrl}`,
      );
      setIsImageLoading(false);
      // Conta como falha pro freio de cascata: se o item já está segurado, não pula por fora.
      streamStore.handleMediaFailure('image-load-timeout');
    }, 10000);

    return () => {
      if (imageSafetyTimeoutRef.current) {
        clearTimeout(imageSafetyTimeoutRef.current);
      }
      if (imageTimerRef.current) {
        clearTimeout(imageTimerRef.current);
      }
    };
    // Re-roda por `currentItemStartedAt` (muda a cada avanço) além do objeto
    // `currentMedia`. Numa playlist de 1 slot o playNext re-cai no MESMO índice →
    // mesmo `currentMedia`; sem esta dep o effect não re-rodava, o imageTimer não
    // era re-armado e o loop morria após 1 volta — barra congela, proof-of-play
    // zera, e o recycle anti-leak do playNext (maybeReloadAtLoopWrap) nunca dispara
    // → OOM. Multi-item: inalterado (currentMedia já muda a cada avanço).
  }, [streamStore.currentMedia, streamStore.currentItemStartedAt, streamStore]);

  // SEAMLESS (2026-07-03) — CAPTURA (não mostra) o frame da imagem atual no
  // canvas persistente NO MOMENTO QUE ELA CARREGA. Imagem é estática → o frame
  // de load == último frame. Desacopla captura (confiável, no load) da exibição
  // (no avanço, via useLayoutEffect abaixo) — robusto a QUALQUER caminho de
  // avanço (timer da imagem, playNext do scheduler, zonas).
  // TV seamless (INVESTIGACAO-TV-TELA-PRETA.md): drawImage(<img>) é seguro no
  // WebKit Tizen; só o readback de <video> fica bloqueado. Roda em TODAS as
  // plataformas (imagem não é HW overlay).
  const freezeSafetyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const heldCanvasReadyRef = useRef(false);
  // src que o freeze-canvas já segura. Sem esta guarda, captureImageToHeldCanvas
  // re-atribuiria `canvas.width/height` (realoca o backing store nativo/GPU) +
  // drawImage 1440×2304 a CADA volta do loop de 1 imagem — churn de memória nativa
  // no WebKit Tizen. Se o canvas já segura ESTA imagem, pula. Invalidado (=null)
  // nos caminhos de vídeo/zona que sobrescrevem o canvas, pra não pular a recaptura
  // numa volta legítima de playlist mista.
  const heldCanvasSrcRef = useRef<string | null>(null);
  const captureImageToHeldCanvas = useCallback((): void => {
    const img = imgRef.current;
    const canvas = freezeFrameCanvasRef.current;
    if (!img || !canvas || !img.complete || img.naturalWidth === 0) return;
    // LEAK FIX: o canvas já segura ESTA imagem → não realoca/redesenha (evita o
    // dreno de memória nativa no loop de 1 imagem). currentSrc reflete a URL de
    // fato resolvida (cache/blob), estável entre voltas do mesmo item.
    if (
      shouldSkipHeldCanvasCapture({
        heldReady: heldCanvasReadyRef.current,
        heldSrc: heldCanvasSrcRef.current,
        currentSrc: img.currentSrc,
      })
    )
      return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    try {
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      ctx.drawImage(img, 0, 0, img.naturalWidth, img.naturalHeight);
      heldCanvasReadyRef.current = true; // canvas tem um frame válido pronto
      heldCanvasSrcRef.current = img.currentSrc; // marca o que o canvas segura
    } catch (err) {
      console.warn('[Stream] capture image to held canvas failed', err);
    }
  }, []);

  // MESMA REGRA DE TRANSIÇÃO p/ ZONAS (2026-08-24): captura o COMPOSITE das
  // zonas do layout (imgs + vídeos, nas posições em tela) no heldCanvas, no
  // FIM do slot de zona — momento em que os elementos AINDA estão montados
  // (o zone group timer chama isto antes do playNext). Saindo do layout, o
  // useLayoutEffect usa essa capa ('heldCanvas') exatamente como imagem→X.
  // drawImage de <video> só onde readback é permitido (Android/browser);
  // em Tizen/webOS desenha só as <img> das zonas (readback de vídeo é
  // bloqueado pelo HW overlay — mesma regra do freeze comum).
  const captureZonesToHeldCanvas = useCallback((): void => {
    const container = containerRef.current;
    const canvas = freezeFrameCanvasRef.current;
    if (!container || !canvas) return;
    // ESPAÇO DE COORDENADAS ÚNICO (causa raiz da capa meio-preta): o Container
    // vive dentro do ScaledContent (transform: scale) → clientWidth/Height são
    // px de LAYOUT e getBoundingClientRect é px VISUAL. Misturar os dois fazia
    // o composite ser desenhado encolhido no canto sup-esquerdo de um canvas
    // maior, com o resto preto (fillRect) = a piscada. Tudo em px VISUAIS.
    const cRectSize = container.getBoundingClientRect();
    const cw = Math.round(cRectSize.width);
    const ch = Math.round(cRectSize.height);
    if (cw === 0 || ch === 0) return;
    const videoReadbackAllowed = isVideoReadbackAllowedForPlatform(
      platform.platform,
    );
    try {
      // OFFSCREEN primeiro: só substitui a capa se REALMENTE desenhou algo.
      // (Desenhar direto no freeze canvas limpava a capa anterior; se nenhum
      // elemento desenhasse, a capa virava PRETO puro = piscada. Regra:
      // capa anterior VELHA > canvas preto.)
      const off = document.createElement('canvas');
      off.width = cw;
      off.height = ch;
      const octx = off.getContext('2d');
      if (!octx) return;
      octx.fillStyle = '#000';
      octx.fillRect(0, 0, cw, ch);
      const cRect = container.getBoundingClientRect();
      const els = container.querySelectorAll('img, video');
      let drew = 0;
      els.forEach((el) => {
        if (el === canvas) return;
        const r = el.getBoundingClientRect();
        if (r.width < 2 || r.height < 2) return;
        if (el instanceof HTMLVideoElement) {
          if (!videoReadbackAllowed || el.readyState < 2) return;
        } else if (el instanceof HTMLImageElement) {
          if (!el.complete || el.naturalWidth === 0) return;
        } else {
          return;
        }
        try {
          octx.drawImage(
            el as HTMLImageElement | HTMLVideoElement,
            r.left - cRect.left,
            r.top - cRect.top,
            r.width,
            r.height,
          );
          drew += 1;
        } catch {
          /* elemento indesenhável — segue pros demais */
        }
      });
      console.info(`[Stream] zoneCapture: els=${els.length} drew=${drew}`);
      if (drew === 0) return; // mantém a capa anterior (stale > preto)
      canvas.width = cw;
      canvas.height = ch;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.drawImage(off, 0, 0);
      heldCanvasReadyRef.current = true;
      heldCanvasSrcRef.current = null; // canvas agora segura ZONAS, não a imagem
    } catch (err) {
      console.warn('[Stream] capture zones to held canvas failed', err);
    }
  }, [platform.platform]);

  // SEAMLESS→VÍDEO (2026-07-16): "geração" do freeze — invalida hides pendentes.
  // Cada showFreeze (nova transição) e cada hide bumpa este contador. Um hide
  // agendado via requestVideoFrameCallback só executa se a geração ainda casar —
  // senão um freeze NOVO já assumiu a tela e NÃO pode ser escondido pelo hide
  // antigo (evita "matar" a cobertura da transição seguinte numa troca rápida).
  const freezeGenerationRef = useRef(0);

  // ENTRADA DE ZONA (T-2): contador de zonas que já pintaram na entrada atual. Em
  // multi-zona o viewport principal renderiza null → handlePlaying/handleImageLoad
  // NUNCA disparam pra esconder a capa, que então segurava até o safety longo
  // (lentidão na entrada). Cada ZonePlayer chama handleZoneReady no 1º paint;
  // quando todas pintaram, esconde a capa. Resetado no useLayoutEffect ao entrar em zona.
  const zoneReadyRef = useRef(0);

  // Esconde o freeze AGORA: limpa o safety timer, bumpa a geração (invalida
  // qualquer hide via rVFC pendente) e fecha o gap no HUD (TEMP). Caminho
  // síncrono usado pela imagem (onLoad) e pelo fallback sem rVFC.
  const hideFreezeFrame = useCallback((): void => {
    freezeGenerationRef.current += 1;
    if (freezeSafetyTimerRef.current) {
      clearTimeout(freezeSafetyTimerRef.current);
      freezeSafetyTimerRef.current = null;
    }
    setIsFreezeFrameVisible(false);
    setIsEarlyFreeze(false);
  }, []);

  // ENTRADA DE ZONA (T-2): chamado por cada ZonePlayer no 1º paint. Quando o nº de
  // zonas prontas atinge o total ativo, esconde a capa (freeze) — a entrada de
  // multi-zona revela assim que as zonas pintam, sem esperar o safety longo.
  const handleZoneReady = useCallback(
    (expected: number): void => {
      zoneReadyRef.current += 1;
      if (zoneReadyRef.current >= expected) hideFreezeFrame();
    },
    [hideFreezeFrame],
  );

  // MOSTRA o freeze AGORA (visível): bumpa geração (invalida hide pendente via rVFC),
  // seta visível, arma safety 30s. Extraído pra ser reusado pelo useLayoutEffect
  // (reativo, na troca de mídia) E pelo early-show do handleTimeUpdate (proativo,
  // ANTES do fim do vídeo — zera a piscada preta do tear-down do overlay em Tizen).
  const showFreezeVisible = useCallback((): void => {
    freezeGenerationRef.current += 1;
    setIsFreezeFrameVisible(true);
    if (freezeSafetyTimerRef.current)
      clearTimeout(freezeSafetyTimerRef.current);
    freezeSafetyTimerRef.current = setTimeout(
      () => setIsFreezeFrameVisible(false),
      30000,
    );
  }, []);

  // Desenha o último frame (WebCodecs, Tizen/webOS) no freeze canvas rasterizando na
  // densidade da TELA (nitidez). Retorna true se desenhou. Reusado pelo early-show.
  const drawLastFrameWcToCanvas = useCallback(
    (bitmap: ImageBitmap): boolean => {
      const canvas = freezeFrameCanvasRef.current;
      if (!canvas || !bitmap || bitmap.width <= 0) return false;
      const ctx = canvas.getContext('2d');
      if (!ctx) return false;
      const dpr = window.devicePixelRatio || 1;
      const targetH = (canvas.clientHeight || bitmap.height) * dpr;
      const scale =
        bitmap.height > 0 ? Math.max(1, targetH / bitmap.height) : 1;
      canvas.width = Math.round(bitmap.width * scale);
      canvas.height = Math.round(bitmap.height * scale);
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
      return true;
    },
    [],
  );

  // EARLY FREEZE — gatilho FINO via requestAnimationFrame (Renato 2026-07-16).
  // Medido na TV .189 (126 transições): o `timeupdate` da Tizen
  // tem cadência ~234ms → grosso demais pra um lead pequeno (dava lead 44-300ms = a
  // "volta" em vídeo de ação). O rVFC não dispara/não dá mediaTime na Tizen (0 frames).
  // Então o rAF (síncrono com o compositor, ~16-33ms) lê `video.currentTime` e dispara
  // o early-freeze num lead PEQUENO e CONSISTENTE (~earlyFreezeLeadS) — cobre o gap
  // preto (~120ms medido) SEM o lead gigante. Armado pelo handleTimeUpdate ao entrar
  // na janela final; para no disparo / troca de mídia / ended.
  const startEndFreezeProbe = useCallback(
    (endSec: number, mediaId: string): void => {
      if (endProbeRafRef.current != null) return;
      const tick = (): void => {
        endProbeRafRef.current = null;
        const v = videoRef.current;
        if (!v || earlyFreezeDoneRef.current) return;
        if (streamStore.currentMedia?.id !== mediaId) return; // trocou de mídia
        if (v.ended) return; // já acabou → rota reativa cobre
        if (v.currentTime >= endSec - earlyFreezeLeadS) {
          const bmp = lastFrameWcCache.get(mediaId);
          if (bmp && drawLastFrameWcToCanvas(bmp)) {
            earlyFreezeDoneRef.current = true;
            setFreezeCoverIsVideo(true); // capa = frame de vídeo → filtro de match aplica
            setIsEarlyFreeze(true); // freeze PRONTO atrás, vídeo segue visível/tocando
            showFreezeVisible();
          }
          return; // disparou 1× — não reagenda
        }
        endProbeRafRef.current = requestAnimationFrame(tick);
      };
      endProbeRafRef.current = requestAnimationFrame(tick);
    },
    [streamStore, drawLastFrameWcToCanvas, showFreezeVisible, earlyFreezeLeadS],
  );

  // SEAMLESS→VÍDEO (2026-07-16): esconde o freeze só quando o 1º frame do vídeo
  // for REALMENTE APRESENTADO (composto), via requestVideoFrameCallback. O evento
  // 'playing' dispara ANTES da composição do 1º frame → esconder ali deixa ~1
  // frame preto na borda "entra o vídeo real". Esperar o rVFC = estritamente
  // MENOS preto (o freeze cobre até o frame subir de verdade).
  //
  // Fallbacks OBRIGATÓRIOS (o freeze NUNCA pode ficar preso):
  //  1. Sem rVFC no elemento (ou sem <video>) → esconde JÁ (= comportamento
  //     antigo no 'playing'). Não regride onde o rVFC não existir.
  //  2. Safety timeout (HIDE_FREEZE_SAFETY_MS) → esconde de qualquer jeito se o
  //     rVFC não disparar. O freeze mostra o frame anterior nesse meio-tempo
  //     (não preto) → nunca PIOR que hoje.
  const scheduleFreezeHideOnFramePresented = useCallback((): void => {
    const video = videoRef.current as
      | (HTMLVideoElement & {
          requestVideoFrameCallback?: (
            cb: (now: number, metadata: unknown) => void,
          ) => number;
        })
      | null;

    const isTV = platform.isTV();

    // Sem <video> → esconde já. FORA da TV sem rVFC → esconde no 'playing' (atual,
    // byte-idêntico Android/browser). Na TV NÃO esconde já mesmo sem rVFC: vai pro
    // timeout (o rVFC não dispara no plano HW, mas esconder na hora = preto).
    if (
      !video ||
      (!isTV && typeof video.requestVideoFrameCallback !== 'function')
    ) {
      hideFreezeFrame();
      return;
    }

    freezeGenerationRef.current += 1;
    const gen = freezeGenerationRef.current;
    let done = false;
    const finish = (): void => {
      if (done) return;
      done = true;
      if (gen !== freezeGenerationRef.current) return; // um freeze novo assumiu
      if (freezeSafetyTimerRef.current) {
        clearTimeout(freezeSafetyTimerRef.current);
        freezeSafetyTimerRef.current = null;
      }
      setIsFreezeFrameVisible(false);
    };

    // Safety timeout = hide efetivo. Na TV usa um teto MAIOR (HIDE_FREEZE_TV_MS) pra
    // cobrir vídeo pesado (rVFC não dispara no plano HW da Tizen). Android/browser:
    // 700ms (rVFC cobre o caso comum bem antes). showFreeze limpa este timer ao
    // iniciar a próxima transição.
    if (freezeSafetyTimerRef.current)
      clearTimeout(freezeSafetyTimerRef.current);
    freezeSafetyTimerRef.current = setTimeout(
      finish,
      isTV ? HIDE_FREEZE_TV_MS : HIDE_FREEZE_SAFETY_MS,
    );

    // rVFC = 1º frame COMPOSTO. Android/browser sempre dispara → esconde no frame real.
    // Na TV normalmente NÃO dispara (plano HW) → cai no timeout acima; se algum firmware
    // suportar, esconde antes (sem atualidade extra). Nunca esconde cedo demais: o
    // rVFC, por definição, só chama quando um frame foi apresentado.
    if (typeof video.requestVideoFrameCallback === 'function') {
      try {
        video.requestVideoFrameCallback(() => finish());
      } catch {
        finish();
      }
    }
  }, [hideFreezeFrame, platform]);

  // SEAMLESS vídeo→imagem (Renato 2026-07-04): enquanto um VÍDEO toca e o
  // PRÓXIMO conteúdo é IMAGEM, pré-carrega a imagem de DESTINO num <img> fora do
  // DOM. Na transição, essa imagem é desenhada no canvas (idêntica à que o
  // <ImagePlayer> vai exibir → troca invisível, SEM screenshot do vídeo = SEM
  // piscada). Cobre o instante em que o <video> descarta o frame ao terminar.
  const nextImageElRef = useRef<HTMLImageElement | null>(null);
  const nextImageReadyIdRef = useRef<string | null>(null);
  useEffect(() => {
    // TV seamless (INVESTIGACAO-TV-TELA-PRETA.md): drawImage(<img>) é seguro no
    // WebKit Tizen; só o readback de <video> fica bloqueado. Pré-carregar a
    // imagem de DESTINO (new Image() fora do DOM) não toca em <video> → seguro
    // em Tizen/webOS também.
    const cur = streamStore.currentMedia;
    const next = streamStore.nextMedia;
    if (cur?.type !== 'video' || next?.type !== 'image') return;
    if (nextImageReadyIdRef.current === next.id) return; // já pronto
    let cancelled = false;
    void (async () => {
      const { url, source } = await resolvePlayableUrl(next);
      if (cancelled || !url) return;
      const img = new Image();
      // Mesmo modo CORS do <ImagePlayer> (anonymous só em remoto). Sem isso o
      // pré-load no-cors e o <img crossorigin> pediam a MESMA URL do R2 em modos
      // diferentes e o Chrome podia reaproveitar a resposta sem
      // Access-Control-Allow-Origin → imagem bloqueada por CORS só no player web.
      applyMediaCrossOrigin(img, source);
      img.onload = () => {
        if (cancelled) return;
        nextImageElRef.current = img;
        nextImageReadyIdRef.current = next.id;
      };
      img.src = url;
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streamStore.currentMedia?.id, streamStore.nextMedia?.id]);

  const handleImageLoad = useCallback(() => {
    const media = streamStore.currentMedia;
    if (!media || media.type !== 'image') return;

    // Carregou: devolve a "segunda chance" do `handleImageError`. Sem zerar aqui, a
    // MESMA imagem na volta seguinte do loop já entraria com o retry gasto e um
    // phantom error viraria erro real.
    imageRetriedSrcRef.current = null;

    // Loga o src REALMENTE pintado + a origem resolvida — NÃO `media.url`.
    // `media.url` é sempre a presigned do sync (rotaciona a cada sync), então o
    // log antigo mostrava uma assinatura diferente a cada exibição mesmo quando
    // a imagem vinha do cache LOCAL. Isso já custou um diagnóstico errado
    // ("imagem tocando do R2 remoto") — o log tem que distinguir cache × rede.
    console.log(
      `[Stream] Image loaded successfully (${playbackSource}):`,
      imgRef.current?.currentSrc || imgRef.current?.src || media.url,
    );
    setIsImageLoading(false);
    // Imagem carregou de fato → CANCELA o safety-net de 10s (que avança sem
    // emitir analytics). A partir daqui quem avança é o `imageTimerRef` real
    // (setado abaixo), que emite `playback_complete` + `slot_completed`. Sem
    // isso, o safety-net (armado no início) vence a corrida numa imagem de 10s
    // e a exibição nunca é contabilizada (proof-of-play zerado pra imagem).
    if (imageSafetyTimeoutRef.current) {
      clearTimeout(imageSafetyTimeoutRef.current);
      imageSafetyTimeoutRef.current = undefined;
    }
    streamStore.notePlaybackStarted();
    // Fecha o orçamento de tempo do boot TAMBÉM pelo caminho da imagem.
    //
    // 🔴 A marca estava só no `handleAvplayFirstFrame` (vídeo), e isso MENTIU na leitura de
    // 2026-07-28: quando o 1º item do loop é imagem, `frame` só era gravado quando o 1º VÍDEO
    // aparecia, muito depois — o `boot_timing` mostrou `open` em 12,6s e eu li como "12,6s de tela
    // preta", quando na verdade a imagem já estava tocando desde ~2,5s. O preto real era ~4s, como o
    // Carlos viu na tela. Idempotente, então quem chegar primeiro (imagem ou vídeo) fecha.
    markBoot('frame');
    flushBootTiming();
    // ...e encerra a janela do recycle-boot AQUI TAMBÉM, pelo mesmo motivo. Estava só no
    // `handleAvplayFirstFrame`: com imagem no 1º slot, ninguém fechava a janela e o `blackMs` saía
    // medindo "decisão → 1º VÍDEO" (13.684ms e 13.415ms medidos na TV) em vez de "decisão → 1º
    // pixel" (~2.750ms). O 1º frame de uma imagem é tão "playback começou" quanto o de um vídeo.
    finishRecycleBoot();
    // SEAMLESS (2026-07-03) — imagem nova pintou de verdade → solta o freeze
    // (que segurava o último frame do conteúdo anterior). Zero preto.
    // hideFreezeFrame limpa o safety timer, bumpa a geração (invalida um hide
    // via rVFC pendente de um vídeo anterior) e fecha o gap no HUD (TEMP —
    // t1 da imagem = onLoad = 1º frame real).
    hideFreezeFrame();
    // PHASE 1 — HOLD-FRAME (Tizen/webOS, video→img): se estávamos SEGURANDO o
    // `<video>` que saiu (ele cobriu a transição com seu último frame), a imagem
    // acabou de pintar → esconde o vídeo (visibility:hidden — hide limpo, sem flash
    // de tear-down do HW overlay) revelando a imagem, e desmonta logo depois (curto
    // delay: o vídeo já está escondido, o unmount não pisca). Lê via ref (sem stale
    // closure / dep extra). No-op fora do hold (Android/browser, imagem regular).
    if (heldVideoForImageRef.current) {
      const im = imgRef.current;
      // PHASE 1.1 FIX (intermitência no video→img): NÃO esconde o vídeo segurado
      // no `onLoad`. `onLoad` = recurso CARREGADO, mas NÃO garante o 1º PAINT da
      // imagem — escondê-lo cedo deixa ~1 frame preto quando a composição ainda não
      // subiu (race, ~1/5 → intermitente). Espera `decode()` (bitmap pronto) + 2 rAF
      // (frame composto) e SÓ ENTÃO esconde → a imagem está garantidamente na tela
      // quando o HW overlay do vídeo cai. Idempotente contra o double-onLoad.
      const revealAndHide = (): void => {
        if (!heldVideoForImageRef.current) return; // slot trocou / já revelado
        setHeldVideoHidden(true);
        if (heldVideoUnmountTimerRef.current) {
          clearTimeout(heldVideoUnmountTimerRef.current);
        }
        heldVideoUnmountTimerRef.current = setTimeout(() => {
          setHeldVideoForImage(false);
          setHeldVideoHidden(false);
          heldVideoUnmountTimerRef.current = null;
        }, HELD_VIDEO_UNMOUNT_DELAY_MS);
      };
      const afterPaint = (): void => {
        // 2 rAF = frame composto (basta no Consumer). +settle EXTRA cobre o
        // compositor mais lento da SSSP (fecha o preto curto no video→img).
        requestAnimationFrame(() =>
          requestAnimationFrame(() => {
            if (HELD_VIDEO_REVEAL_SETTLE_MS > 0) {
              setTimeout(revealAndHide, HELD_VIDEO_REVEAL_SETTLE_MS);
            } else {
              revealAndHide();
            }
          }),
        );
      };
      const imEl = im as
        | (HTMLImageElement & { decode?: () => Promise<void> })
        | null;
      if (imEl && typeof imEl.decode === 'function') {
        imEl.decode().then(afterPaint, afterPaint);
      } else {
        afterPaint();
      }
    }
    // Captura ESTA imagem no canvas (escondido) pra cobrir a PRÓXIMA transição,
    // seja qual for o caminho de avanço. Roda após esconder — a imagem já pintou.
    captureImageToHeldCanvas();

    // Base duration for the image (not adjusted - speed is applied in JSX)
    const baseDurationSeconds = Math.max(1, Math.round(media.duration || 10));
    setMediaDuration(baseDurationSeconds);

    // Start playing (animation will start via isPlaying prop)
    streamStore.setPlaying(true);

    // Capture playlist version at the time this content STARTS playing
    mediaVersionRef.current = streamStore.playlistVersion;
    playingMediaIdRef.current = streamStore.currentMedia?.id;
    // Marca esta imagem como decodificada/na-tela → nas próximas voltas do loop
    // (mesmo id) o effect re-arma o avanço sem re-decodificar.
    displayedImageIdRef.current = media.id;

    // Use campanhaId from the media item if available (prefer number from item)
    const campanhaId = media.campanhaId ?? undefined;

    analyticsService.trackPlaybackStart(media.id, media.name, campanhaId);
    // CP10 — slot_started é trackeado via useEffect global que escuta
    // currentMedia (cobre imagens E vídeos). Não duplica aqui.
    if (imageTimerRef.current) {
      clearTimeout(imageTimerRef.current);
    }

    // SYNC CANÔNICO NA IMAGEM. O realinhamento vivia só no `canplay` do vídeo, então
    // num loop que começa com imagens o player ficava dezenas de segundos sem chance
    // de se corrigir — e quem reiniciava só reencontrava o grupo no próximo vídeo.
    // Aqui não dá pra "dar seek" numa imagem: o equivalente é ENCURTAR o que falta
    // dela, pra que a PRÓXIMA troca caia no horário canônico. Se o item certo é
    // outro, pula direto.
    let remainingSeconds = baseDurationSeconds;
    if (media.playbackStartedAt && syncedMediaIdRef.current !== media.id) {
      const target = resolveLoopTarget({
        playbackStartedAt: media.playbackStartedAt,
        durations: streamStore.playlist.map((it) => it.duration || 0),
        nowMs: serverNow() + paintLeadMs(platform.platform),
      });
      if (target) {
        if (target.index !== streamStore.currentIndex) {
          console.log(
            `[Stream] Sync: hopping to item ${target.index} (offset ${target.offsetSeconds.toFixed(1)}s, loopPos ${target.loopPosition.toFixed(1)}s of ${target.loopTotal.toFixed(0)}s)`,
          );
          syncedMediaIdRef.current = media.id;
          streamStore.setCurrentIndex(target.index);
          return; // a mídia certa vai carregar e armar o próprio avanço
        }
        const restante = baseDurationSeconds - target.offsetSeconds;
        // Piso de 1s: sem ele, entrar no finalzinho do slot dispararia troca
        // imediata e poderia encadear pulos a cada volta.
        if (restante >= 1 && restante < baseDurationSeconds - 0.5) {
          remainingSeconds = restante;
          console.log(
            `[Stream] Sync: encurtando imagem para ${restante.toFixed(1)}s (loopPos ${target.loopPosition.toFixed(1)}s, item ${target.index})`,
          );
        }
        syncedMediaIdRef.current = media.id;
      }
    }

    // Timer uses speed-adjusted duration
    const adjustedDuration = remainingSeconds / playbackSpeed;
    const avancarImagem = () => {
      // Use campanhaId from the media item if available (prefer number from item)
      const campanhaId = media.campanhaId ?? undefined;

      // Unificado 2026-09-21: o contexto do slot viaja NO playback_complete
      // (era um `slot_completed` separado, emitido 1:1 com este).
      const slotCtxEnd = getSlotContextForAnalytics();
      analyticsService.trackPlaybackComplete(
        media.id,
        baseDurationSeconds, // Report original duration for analytics
        media.name,
        campanhaId,
        slotCtxEnd ?? undefined,
      );
      if (slotCtxEnd) {
        clearSlotStartedDedup(); // exibição terminou → o mesmo slot pode reemitir na próxima volta
        analyticsService.trackSlotCompleted({
          ...slotCtxEnd,
          durationActual: baseDurationSeconds,
          completedNaturally: true,
        });
      }
      streamStore.handleMediaEnd(
        mediaVersionRef.current,
        playingMediaIdRef.current,
      );
    };
    // Guardado pra que o PULSO de sincronia possa RE-ARMAR o avanço sem duplicar
    // a lógica de analytics/fim de slot. `firesAt` é quando o timer atual dispara —
    // é o que permite ao pulso saber se a imagem está adiantada ou atrasada.
    imageAdvanceRef.current = {
      fire: avancarImagem,
      mediaId: media.id,
      baseDurationSeconds,
      firesAt: Date.now() + adjustedDuration * 1000,
    };
    imageTimerRef.current = setTimeout(avancarImagem, adjustedDuration * 1000);
  }, [
    streamStore,
    playbackSpeed,
    captureImageToHeldCanvas,
    hideFreezeFrame,
    playbackSource,
  ]);

  // ─── Quanto tempo o item está NA TELA (2026-09-30) ─────────────────────────
  // Mede a entrega em tempo de RELÓGIO, não pelo `currentTime` do vídeo. A diferença
  // importa: sob sync canônico o player ENTRA no item já pela metade, e aí `currentTime`
  // diria "exibiu inteiro" quando exibiu metade. Só isto responde "quanto dele apareceu".
  const itemNaTelaDesdeRef = useRef<{
    mediaId: string;
    desdeMs: number;
  } | null>(null);
  /** Guarda "deixa terminar": adia o pulo UMA vez por item — nunca trava o avanço. */
  const hopAdiadoParaMediaRef = useRef<string | null>(null);

  const marcarItemNaTela = useCallback((mediaId: string): void => {
    if (itemNaTelaDesdeRef.current?.mediaId === mediaId) return;
    itemNaTelaDesdeRef.current = { mediaId, desdeMs: Date.now() };
    hopAdiadoParaMediaRef.current = null;
  }, []);

  const currentMediaId = streamStore.currentMedia?.id;
  useEffect(() => {
    if (currentMediaId) marcarItemNaTela(currentMediaId);
  }, [currentMediaId, marcarItemNaTela]);

  // ─── PULSO DE SINCRONIA ────────────────────────────────────────────────────
  // O realinhamento acontecia só na TROCA de slot. Um desvio que nasce no MEIO de
  // um item (decode lento, timer atrasado por throttling do SO, aba em segundo
  // plano) era carregado até o próximo — medido: o desvio crescia de -0,2s para
  // -1,2s ao longo de um loop de 90s e só voltava na troca. Aqui a posição é
  // reavaliada periodicamente contra a época, então o erro é corrigido onde nasce.
  //
  // Local e sem rede: usa a MESMA época que já veio no sync + o relógio do
  // servidor. Não elege líder — um player travado se corrige sozinho ao voltar e
  // nunca arrasta os outros.
  //
  // Sob AVPlay (Tizen) o pulso CONTINUA valendo: pular de item e reajustar imagem são
  // independentes do backend de vídeo. Só o seek dentro do vídeo é impossível ali (o
  // quadro vive no plano HW) — essa parte é pulada adiante.
  /**
   * Frame do vídeo ATUAL no instante do corte → cover da troca que o hop vai causar. Sem
   * isto, o hop do pulso no 1º loop (sem último-frame ainda) ficava preto, e nos loops
   * seguintes mostrava o frame de FIM do vídeo — uma cena que a tela não tinha chegado.
   */
  const captureHopFrame = useCallback(
    (mediaId: string): void => {
      const video = videoRef.current;
      if (!video || !isVideoReadbackAllowedForPlatform(platform.platform))
        return;
      if (!hopFrameCanvasRef.current) {
        hopFrameCanvasRef.current = document.createElement('canvas');
      }
      hopFrameReadyIdRef.current = drawVideoFrameToCanvas(
        video,
        hopFrameCanvasRef.current,
      )
        ? mediaId
        : null;
    },
    [platform.platform],
  );

  // ─── MOTOR AGENDADO (A/B) ──────────────────────────────────────────────────────────
  // Para a mídia atual: calcula T (fim do slot pela régua), prepara o próximo vídeo no
  // standby ~2s antes e, em T, dá play nele e troca os papéis dos elementos. Quando não
  // há o que preparar (próximo é imagem, mesmo id, standby não pronto), em T só avança o
  // índice e o motor reativo carrega como sempre — nunca pior que hoje.
  useEffect(() => {
    scheduledSwitchRef.current = null;
    const media = streamStore.currentMedia;
    if (scheduledSwapForMediaIdRef.current !== media?.id) {
      scheduledSwapForMediaIdRef.current = null; // vale só pra mídia que entrou pelo swap
    }
    if (!scheduledEngine || !media || media.type !== 'video') return;
    if (!media.playbackStartedAt) return;
    const playlist = streamStore.playlist;
    if (
      streamStore.isTriggerPlayback ||
      media.zonaId != null ||
      playlist.length < 2
    ) {
      return;
    }
    const plan = resolveNextSwitch({
      playbackStartedAt: media.playbackStartedAt,
      durations: playlist.map((it) => it.duration || 0),
      currentIndex: streamStore.currentIndex,
      nowMs: serverNow(),
    });
    if (!plan) return;
    const next = playlist[plan.nextIndex];
    const canPrepare = !!next && next.type === 'video' && next.id !== media.id;
    scheduledSwitchRef.current = {
      mediaId: media.id,
      switchAtMs: plan.switchAtMs,
    };

    let cancelled = false;
    let standbyReady = false;
    let prepareTimer: ReturnType<typeof setTimeout> | null = null;
    let switchTimer: ReturnType<typeof setTimeout> | null = null;
    let raf: number | null = null;

    const prepare = async (): Promise<void> => {
      const standby = standbyVideoRef.current;
      if (cancelled || !canPrepare || !standby || standby === videoRef.current)
        return;
      try {
        const { url, source } = await resolvePlayableUrl(next);
        if (cancelled || !url) return;
        applyMediaCrossOrigin(standby, source);
        standby.muted = true;
        standby.preload = 'auto';
        standby.addEventListener(
          'loadeddata',
          () => {
            standbyReady = true;
          },
          { once: true },
        );
        standby.src = url;
        standby.load();
      } catch (err) {
        console.warn(
          '[Stream] motor agendado: falha ao preparar o próximo vídeo',
          err,
        );
      }
    };

    const doSwitch = (): void => {
      if (cancelled) return;
      if (streamStore.currentMedia?.id !== media.id) return; // já avançou por outro caminho
      const standby = standbyVideoRef.current;
      const swap =
        canPrepare &&
        !!standby &&
        standby !== videoRef.current &&
        (standbyReady || standby.readyState >= 2);
      if (swap && standby) {
        // IMPRESSÃO (2026-10-11): o vídeo que SAI vira standby e perde TODOS os handlers,
        // inclusive o `onEnded` — e era só de lá (`handleVideoEnded`) que saíam
        // `trackPlaybackComplete`/`trackSlotCompleted`. Sem isto, toda troca vídeo→vídeo do
        // motor agendado some do analytics (medido no Tab A7: 342 ev/h no legado × 66 aqui).
        // Emitimos ANTES de trocar os papéis e de `setCurrentIndex`, pra que o `slotCtx` seja
        // o do slot que acabou de tocar — mesmo cuidado do "CP10" no caminho reativo.
        const saindo = videoRef.current;
        const duracaoSaindo = Math.round(saindo?.duration || 0);
        const campanhaSaindo = media.campanhaId ?? undefined;
        const slotCtxSaindo = getSlotContextForAnalytics();
        scheduledCompleteEmittedForRef.current = media.id;
        setTimeout(() => {
          analyticsService.trackPlaybackComplete(
            media.id,
            duracaoSaindo,
            media.name,
            campanhaSaindo,
            slotCtxSaindo ?? undefined,
          );
          if (slotCtxSaindo) {
            clearSlotStartedDedup();
            analyticsService.trackSlotCompleted({
              ...slotCtxSaindo,
              durationActual: duracaoSaindo,
              // Renato 2026-10-11: T vem da duração PROGRAMADA do item, então o vídeo roda o
              // slot inteiro; o corte residual é desprezível. Conta como natural.
              completedNaturally: true,
            });
          }
        }, 0);

        // Em T: play no já-decodificado + troca de papéis NO MESMO frame. O estado que o
        // canplay/playing fariam é reproduzido aqui — esses eventos já passaram no standby.
        standby.muted = streamStore.isMuted;
        standby.playbackRate = 1;
        scheduledSwapForMediaIdRef.current = next.id;
        void standby.play().catch(() => undefined);
        setActiveVideoSlot((slot) => (slot === 'A' ? 'B' : 'A'));
        cancelLoadingOverlayTimer();
        clearAdvanceWatchdog();
        clearBufferingWatchdog();
        setIsVideoLoading(false);
        hasEverPlayedRef.current = true;
        canPlayReachedRef.current = true;
        syncedMediaIdRef.current = next.id; // a entrada já é exata: sem seek de entrada
        if (Number.isFinite(standby.duration) && standby.duration > 0) {
          setMediaDuration(standby.duration);
        }
        videoLoopControlRef.current = {
          shouldManualLoop: false,
          shouldCutShort: false,
          scheduledDuration: next.duration || 0,
          actualDuration: standby.duration || 0,
          playbackStartTime: Date.now(),
        };
        marcarItemNaTela(next.id);
        streamStore.setPlaying(true);
        console.log(
          `[Stream] ⏱️ troca agendada: ${media.id.slice(0, 8)} → ${next.id.slice(0, 8)} (desvio ${(serverNow() - plan.switchAtMs).toFixed(0)}ms)`,
        );
      } else {
        scheduledSwapForMediaIdRef.current = null; // entra pelo motor reativo
      }
      streamStore.setCurrentIndex(plan.nextIndex);
    };

    const armPrecise = (): void => {
      const tick = (): void => {
        if (cancelled) return;
        if (serverNow() >= plan.switchAtMs) {
          doSwitch();
          return;
        }
        raf = requestAnimationFrame(tick);
      };
      tick();
    };

    const now = serverNow();
    prepareTimer = setTimeout(
      () => void prepare(),
      Math.max(0, plan.switchAtMs - SCHEDULED_PREPARE_LEAD_MS - now),
    );
    switchTimer = setTimeout(
      armPrecise,
      Math.max(0, plan.switchAtMs - SCHEDULED_SWITCH_COARSE_LEAD_MS - now),
    );
    return () => {
      cancelled = true;
      if (prepareTimer) clearTimeout(prepareTimer);
      if (switchTimer) clearTimeout(switchTimer);
      if (raf !== null) cancelAnimationFrame(raf);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    scheduledEngine,
    streamStore,
    streamStore.currentMedia?.id,
    streamStore.playlistVersion,
    streamStore.isMuted,
  ]);

  // ── ZELADOR: TODO <video> QUE SAI DO DOM DEVOLVE O DECODER ─────────────────────
  //
  // 🔴 VAZAMENTO DE DECODER PELO GATILHO (medido no Tab A7 SM-T505, 2026-10-10/11).
  //
  // `releaseAndroidVideoDecoder` só roda dentro do `applyVideoSource`, ou seja, quando a
  // FONTE muda num elemento MONTADO. Quando o React DESMONTA o elemento, ninguém solta
  // nada: o nó sai do DOM com `src` setado e `readyState=4`, e como nenhum caminho de
  // código volta a tocá-lo, o decoder de HW fica preso até o GC — que no A7 não vem.
  //
  // Medido no disparo de um gatilho de IMAGEM (que troca a playlist inteira), com o DOM
  // instrumentado ao vivo por CDP:
  //     <video> criados       3 → 6     (o React recriou o bloco)
  //     órfãos fora do DOM    0 → 3
  //     órfãos AINDA com src  0 → 2     ← seguram decoder, rs=4
  //     DOM                   A e B na MESMA fonte, ambos MEDIA_ERR_DECODE
  // Sem decoder livre, toda alocação nova falha (`internalError -12`, 12/min) e o player
  // fica preto sem se recuperar. Em janelas de 15 min, mesmo processo: sem gatilho = 0
  // falhas / 276 ciclos; com gatilho = 180 falhas / 178 ciclos.
  //
  // Soltar os 2 órfãos À MÃO devolveu o playback na hora, sem restart — prova de que
  // soltar funciona mesmo com o elemento destacado, e de que o buraco é a soltura nunca
  // acontecer. (O motor A/B NÃO é a causa: sozinho roda 15 min limpo. Acusei o A/B duas
  // vezes antes de medir direito; as duas vezes errado.)
  //
  // ⚠️ Por que um zelador e não um cleanup no ponto certo: o `<video>` daqui desmonta por
  // vários caminhos (gatilho, zona, stream, held-frame, troca de motor). Amarrar a
  // soltura ao `useEffect` de UM deles resolve um e deixa os outros — foi o que aconteceu
  // na 1.0.465, cujo cleanup não roda neste caminho (medido: os órfãos saíram com o `src`
  // intacto, e as falhas ficaram em 179 vs 180). O zelador garante a invariante por
  // observação do DOM, então vale pra todo caminho de desmontagem. Vide `decoderJanitor`.
  useEffect(
    () =>
      criarZeladorDeDecoder({
        raiz: document.body,
        soltar: soltarDecoderDoVideo,
      }),
    [],
  );

  /**
   * GATILHO × MOTOR AGENDADO — devolve o decoder do standby enquanto o gatilho toca.
   *
   * <p>O motor A/B mantém DOIS `<video>` montados com `src`, e o Tab A7 segura 2 decoders o
   * tempo todo por causa disso (medido: 2 em 229 de 232 amostras). Quando um gatilho de VÍDEO
   * entra, ele precisa de um TERCEIRO — e o aparelho não tem. Medido ao vivo em 2026-10-12:
   * o contador foi a 3 e o sistema devolveu 42 `InsufficientResources` / `internalError -12`,
   * tela preta por ~70s até liberar sozinho.</p>
   *
   * <p>O standby é inútil durante o gatilho: o efeito do motor agendado já faz early-return em
   * `isTriggerPlayback`, então nenhuma troca será preparada. Soltar o decoder dele devolve
   * exatamente a folga que falta. Ao sair do gatilho o standby é recarregado normalmente pelo
   * `prepare()` da próxima troca — não há estado a restaurar aqui.</p>
   */
  useEffect(() => {
    const standby = standbyVideoRef.current;
    if (
      !deveSoltarStandbyNoGatilho({
        abLigado: scheduledEngine,
        emGatilho: streamStore.isTriggerPlayback,
        standby,
        ativo: videoRef.current,
      }) ||
      !standby
    ) {
      return;
    }
    console.log(
      '[Stream] 🎬 gatilho entrou — devolvendo o decoder do standby do motor A/B',
    );
    soltarDecoderDoVideo(standby);
  }, [scheduledEngine, streamStore.isTriggerPlayback]);


  useEffect(() => {
    const pulso = setInterval(() => {
      const media = streamStore.currentMedia;
      // Porta única e testável (vide resolveSyncPulseGate). Inclui o guard de GATILHO:
      // um gatilho substitui a playlist inteira, e sem o guard o pulso seguia medindo a
      // posição canônica contra as durações do gatilho, concluía que o player estava
      // fora de lugar e arrancava o conteúdo de volta — derrubando o decoder a cada
      // arranque até esgotar o CMA do A7 (erro -12). Vide o javadoc da função.
      const porta = resolveSyncPulseGate({
        hasAnchor: !!media?.playbackStartedAt,
        isPlaying: streamStore.isPlaying,
        isLoading: isImageLoading || isVideoLoading,
        isTriggerPlayback: streamStore.isTriggerPlayback,
        sinceLastActionMs: Date.now() - lastPulseActionRef.current,
        cooldownMs: SYNC_PULSE_COOLDOWN_MS,
      });
      if (!porta.run) return;
      if (!media?.playbackStartedAt) return; // estreita o tipo; a porta já garantiu

      const target = resolveLoopTarget({
        playbackStartedAt: media.playbackStartedAt,
        durations: streamStore.playlist.map((it) => it.duration || 0),
        nowMs: serverNow() + videoPaintLeadMs,
      });
      if (!target) return;

      if (target.index !== streamStore.currentIndex) {
        // 🔴 NÃO pular só porque o índice difere. Na BORDA do slot o alvo já aponta pro
        // próximo item (ainda mais com a compensação de pintura, que mira à frente) —
        // pular ali corta a transição natural e força um carregamento, o que aparece como
        // PRETO a cada troca (relatado no navegador, 2026-09-14). Só vale pular quando a
        // posição está de fato longe; borda de slot o avanço normal resolve sozinho.
        const posicaoAtual = streamStore.playlist
          .slice(0, streamStore.currentIndex)
          .reduce((soma, it) => soma + (it.duration || 0), 0);
        let erroPos = target.loopPosition - posicaoAtual;
        erroPos =
          ((erroPos % target.loopTotal) + target.loopTotal) % target.loopTotal;
        if (erroPos > target.loopTotal / 2) erroPos -= target.loopTotal;
        const duracaoAtual = streamStore.currentMedia?.duration || 0;
        // Dentro do slot atual (com folga) = está no lugar certo, só perto da virada.
        if (
          erroPos >= -SYNC_PULSE_HOP_MIN_S &&
          erroPos <= duracaoAtual + SYNC_PULSE_HOP_MIN_S
        ) {
          return;
        }
        // Fallback de semeadura: se o efeito ainda não marcou (primeiro tick após
        // montar), marca agora — nunca deixa o cálculo de exibição sem âncora.
        marcarItemNaTela(media.id);

        // ── Guarda "deixa terminar" (2026-09-30) ──────────────────────────────
        // Arrancar o item faltando um piscar pro fim impedia o `ended` e, com ele, a
        // impressão: player sob sync gerava ZERO `playback_complete`. Quando o alvo JÁ
        // É o próximo item, esperar o fim natural chega no MESMO destino ~0,1s depois —
        // e conta a exibição. Adia no MÁXIMO UMA rodada por item: se o vídeo estiver
        // travado, o pulso seguinte pula como sempre. Não há como travar o avanço.
        const videoAtual = videoRef.current;
        if (
          shouldLetItemFinish({
            targetIndex: target.index,
            currentIndex: streamStore.currentIndex,
            playlistLength: streamStore.playlist.length,
            isVideo: media.type === 'video',
            video: videoAtual
              ? {
                  paused: videoAtual.paused,
                  readyState: videoAtual.readyState,
                  duration: videoAtual.duration,
                  currentTime: videoAtual.currentTime,
                }
              : null,
            alreadyDeferred: hopAdiadoParaMediaRef.current === media.id,
            maxRemainingSeconds: HOP_LET_FINISH_MAX_S,
          })
        ) {
          hopAdiadoParaMediaRef.current = media.id; // adia UMA vez, e só
          console.log(
            `[Stream] Pulso: item ${streamStore.currentIndex} acaba em ${(
              (videoAtual?.duration ?? 0) - (videoAtual?.currentTime ?? 0)
            ).toFixed(2)}s — deixa terminar (alvo já é o próximo)`,
          );
          return;
        }

        // ── Impressão do item cortado (decisão Renato 2026-09-30) ─────────────
        // Corte de verdade (não a borda acima): reporta o que FOI exibido, marcado
        // `completedNaturally: false`. Conta como impressão cheia no relatório; o
        // limiar de exibição é MIN_SHOWN_RATIO_FOR_IMPRESSION. A duração enviada é a
        // REAL na tela — é ela que permite rever o limiar no servidor depois, sobre
        // dados já gravados, sem precisar de OTA.
        const planejadoSegundos = media.duration || 0;
        const fracaoExibida = shownRatio({
          anchor: itemNaTelaDesdeRef.current,
          mediaId: media.id,
          plannedSeconds: planejadoSegundos,
          nowMs: Date.now(),
        });
        const exibidoSegundos = fracaoExibida * planejadoSegundos;
        if (fracaoExibida >= MIN_SHOWN_RATIO_FOR_IMPRESSION) {
          analyticsService.trackPlaybackComplete(
            media.id,
            Math.round(exibidoSegundos * 100) / 100,
            media.name,
            media.campanhaId ?? undefined,
            undefined,
            false, // completedNaturally — cortado pelo pulso
          );
        }

        lastPulseActionRef.current = Date.now();
        console.log(
          `[Stream] Pulso: item errado — pulando para ${target.index} (erro ${erroPos.toFixed(1)}s, loopPos ${target.loopPosition.toFixed(1)}s, exibido ${(fracaoExibida * 100).toFixed(0)}%)`,
        );
        if (media.type === 'video') captureHopFrame(media.id);
        streamStore.setCurrentIndex(target.index);
        return;
      }

      if (media.type === 'video') {
        // AVPlay (TV): o quadro vive no plano HW — quem corrige é o controlador nativo,
        // via handle publicado pelo <AvplayVideo>. Tolerância maior porque um salto no
        // plano HW é mais perceptível que um seek no <video> do DOM.
        if (avplayActive) {
          if (!AVPLAY_IN_ITEM_SEEK_ENABLED) return;
          const handle = avplaySyncHandleRef.current;
          const atual = handle?.currentSeconds();
          if (handle == null || atual == null) return;
          const erroAv = target.offsetSeconds - atual;
          if (Math.abs(erroAv) > SYNC_PULSE_AVPLAY_TOLERANCE_S) {
            lastPulseActionRef.current = Date.now();
            console.log(
              `[Stream] Pulso: corrigindo AVPlay ${erroAv > 0 ? '+' : ''}${erroAv.toFixed(1)}s (para ${target.offsetSeconds.toFixed(1)}s)`,
            );
            handle.seekTo(target.offsetSeconds);
          }
          return;
        }
        const video = videoRef.current;
        if (!video?.duration || !Number.isFinite(video.duration)) return;
        const erro = target.offsetSeconds - video.currentTime;
        const plano = resolveSeekPlan({
          erroSegundos: erro,
          targetOffsetS: target.offsetSeconds,
          durationS: video.duration,
          seeking: video.seeking,
          readyState: video.readyState,
          sinceLastSeekMs:
            lastSeekedAtRef.current > 0
              ? Date.now() - lastSeekedAtRef.current
              : Number.POSITIVE_INFINITY,
          seekLatencyMs: seekLatencyMsRef.current,
          toleranceS: SYNC_PULSE_VIDEO_TOLERANCE_S,
          postSeekToleranceS: SYNC_PULSE_POST_SEEK_TOLERANCE_S,
          postSeekWindowMs: SYNC_PULSE_POST_SEEK_WINDOW_MS,
        });
        if (plano.action === 'none') return;
        if (plano.action === 'seek') {
          lastPulseActionRef.current = Date.now();
          seekIssuedAtRef.current = Date.now();
          console.log(
            `[Stream] Pulso: corrigindo video ${erro > 0 ? '+' : ''}${erro.toFixed(1)}s (para ${plano.seekTo.toFixed(1)}s, latência ${seekLatencyMsRef.current.toFixed(0)}ms)`,
          );
          video.currentTime = plano.seekTo;
          video.playbackRate = playbackSpeed; // sai do ajuste fino: o seek já zerou o erro
          return;
        }
        // Erro pequeno → velocidade, não salto (vide SYNC_RATE_*). Sem cooldown: o
        // ajuste é contínuo e invisível, diferente de seek/pulo, que são eventos.
        const alvoRate = resolveSyncPlaybackRate({
          erroSegundos: erro,
          baseRate: playbackSpeed,
          deadbandSeconds: SYNC_RATE_DEADBAND_S,
          maxAdjust: SYNC_RATE_MAX_ADJ,
          recoverSeconds: SYNC_RATE_RECOVER_S,
        });
        if (Math.abs(video.playbackRate - alvoRate) > 0.001) {
          video.playbackRate = alvoRate;
        }
        return;
      }

      // IMAGEM — não existe seek; o equivalente é re-armar o que falta dela.
      const avanco = imageAdvanceRef.current;
      if (!avanco || avanco.mediaId !== media.id) return;
      const restanteCerto = avanco.baseDurationSeconds - target.offsetSeconds;
      const restanteAtual = (avanco.firesAt - Date.now()) / 1000;
      const erro = restanteAtual - restanteCerto;
      // Piso de 0,5s no restante: re-armar pra "quase agora" encadearia trocas.
      if (
        Math.abs(erro) > SYNC_PULSE_IMAGE_TOLERANCE_S &&
        restanteCerto >= 0.5
      ) {
        lastPulseActionRef.current = Date.now();
        console.log(
          `[Stream] Pulso: reajustando imagem ${erro > 0 ? '+' : ''}${erro.toFixed(1)}s (restam ${restanteCerto.toFixed(1)}s)`,
        );
        if (imageTimerRef.current) clearTimeout(imageTimerRef.current);
        const ms = (restanteCerto / playbackSpeed) * 1000;
        avanco.firesAt = Date.now() + ms;
        imageTimerRef.current = setTimeout(avanco.fire, ms);
      }
    }, SYNC_PULSE_INTERVAL_MS);

    return () => clearInterval(pulso);
  }, [
    avplayActive,
    captureHopFrame,
    videoPaintLeadMs,
    streamStore,
    isImageLoading,
    isVideoLoading,
    playbackSpeed,
  ]);
  // Mantém a ref fresca a cada render — o effect "Image changed" (declarado ANTES
  // deste useCallback) a usa pra re-armar o avanço da mesma imagem sem re-decode.
  handleImageLoadRef.current = handleImageLoad;

  // SEAMLESS (2026-07-03) — MOSTRA o freeze (que já tem o último frame da imagem,
  // capturado no load) NO INSTANTE que currentMedia muda saindo de uma IMAGEM.
  // useLayoutEffect roda antes do paint → o canvas cobre o novo conteúdo (que
  // ainda está carregando/preto) SEM flash. Pega TODOS os caminhos de avanço
  // (timer, playNext, scheduler) porque só observa a troca de currentMedia.
  // Cobre 3 casos aqui: imagem→X (freeze da imagem capturada no load), vídeo→imagem
  // (Passo 2 — imagem de destino, INVISÍVEL) e vídeo→X (Etapa B — último frame do
  // vídeo que saiu, do lastFrameCache). handleVideoEnded NÃO mexe mais no canvas.
  const prevMediaRef = useRef<{
    id: string;
    type: string;
    zonaId?: number | null;
  } | null>(null);
  useLayoutEffect(() => {
    const cur = streamStore.currentMedia;
    const prev = prevMediaRef.current;
    prevMediaRef.current = cur
      ? { id: cur.id, type: cur.type, zonaId: cur.zonaId }
      : null;
    // MOTOR AGENDADO: a troca já aconteceu com o próximo vídeo pintado (swap A/B). Não há gap
    // a cobrir — e um cover aqui ficaria POR CIMA do vídeo que já está tocando.
    if (cur && scheduledSwapForMediaIdRef.current === cur.id) return;
    // Decodifica JÁ o último frame do vídeo que entra: fica pronto bem antes do fim
    // dele, quando cobre a troca seguinte. Sem isso o frame não estaria decodificado.
    if (cur?.type === 'video') lastFrameCache.prepare(cur.id);
    // ENTRADA DE ZONA (T-2): zera o contador de prontidão. Os ZonePlayers remontam
    // ao entrar em multi-zona e chamam handleZoneReady no 1º paint → a capa some
    // quando TODAS pintarem (em vez de segurar até o safety longo).
    if (cur?.zonaId != null && syncStore.hasMultipleZones) {
      zoneReadyRef.current = 0;
    }
    // TV seamless (INVESTIGACAO-TV-TELA-PRETA.md): drawImage(<img>) é seguro no
    // WebKit Tizen; só o readback de <video> fica bloqueado. Em Tizen/webOS o
    // freeze usa SÓ rotas de <img> (heldCanvas/nextImage) — 'lastFrame' (frame de
    // <video>) fica proibido via isVideoReadbackAllowed.
    const isVideoReadbackAllowed = isVideoReadbackAllowedForPlatform(
      platform.platform,
    );
    // Decisão de QUAL fonte cobrir a troca extraída pro playbackHelpers
    // (getFreezeCandidateName) — testável isoladamente. Ordem: imagem→X
    // (heldCanvas) > vídeo→imagem pré-carregada (nextImage, invisível) > SAINDO de
    // vídeo com último frame WebCodecs (lastFrameWc, Tizen/webOS) > SAINDO de vídeo
    // com último frame nativo (lastFrame, Android/browser). Vide playbackHelpers.test.ts.
    // O frame do hop vale só pra ESTA troca: lê e solta, use-se ou não.
    const hopFrameReadyId = hopFrameReadyIdRef.current;
    hopFrameReadyIdRef.current = null;
    let candidate = getFreezeCandidateName({
      prev,
      cur: cur ? { id: cur.id, type: cur.type } : null,
      heldCanvasReady: heldCanvasReadyRef.current,
      nextImageReady: !!nextImageElRef.current,
      nextImageReadyId: nextImageReadyIdRef.current,
      lastFrameCacheHas: (id) => lastFrameCache.has(id),
      lastFrameWcCacheHas: (id) => lastFrameWcCache.has(id),
      isVideoReadbackAllowed,
      hopFrameReadyId,
    });
    // MESMA REGRA DE TRANSIÇÃO p/ ZONAS (2026-08-24): saindo de conteúdo DE
    // ZONA (layout multi-zona), a capa é o COMPOSITE das zonas capturado no
    // fim do slot (captureZonesToHeldCanvas, no zone group timer). Sem isso a
    // fronteira zona→fullscreen não tinha fonte de capa e caía no
    // BlackTransition (preto), DIFERENTE das trocas entre conteúdos comuns.
    candidate = resolveZoneAwareFreezeCandidate({
      baseCandidate: candidate,
      prevZonaId: prev?.zonaId,
      heldCanvasReady: heldCanvasReadyRef.current,
    });
    // Diagnóstico (fronteira de zona): loga a decisão da capa quando a troca
    // envolve conteúdo de zona — sai no logcat (Capacitor/Console).
    if (prev?.zonaId != null || cur?.zonaId != null) {
      console.info(
        `[Stream] freeze na fronteira de zona: candidate=${candidate ?? 'NENHUM'} ` +
          `prev=${prev?.type}/${prev?.zonaId != null ? 'zona' : 'comum'} ` +
          `cur=${cur?.type}/${cur?.zonaId != null ? 'zona' : 'comum'} ` +
          `heldReady=${heldCanvasReadyRef.current}`,
      );
    }
    // ÚLTIMO RECURSO (2026-10-12): nenhuma fonte preparada. Acontece quando a playlist é
    // trocada INTEIRA de uma vez — tipicamente a SAÍDA DE GATILHO: a imagem pré-carregada é
    // a do gatilho, não a do item que volta, e o último frame do vídeo do gatilho não está em
    // cache. Medido no Tab A7: ~300ms de preto em TODA saída (e ~1s quando havia recusa de
    // decoder junto). O frame que o usuário vê ainda está no <video> — o `src` só troca num
    // effect POSTERIOR a este — então capturamos na hora, com a mesma captura do hop.
    if (
      deveCapturarCapaNaHora({
        candidate,
        prevType: prev?.type,
        prevId: prev?.id,
        curId: cur?.id,
        readbackPermitido: isVideoReadbackAllowed,
      })
    ) {
      captureHopFrame(prev!.id);
      if (hopFrameReadyIdRef.current === prev!.id) candidate = 'hopFrame';
    }
    if (!candidate) return;

    // AVPLAY (2026-07-21): quando SAÍMOS de um vídeo (vídeo→imagem / vídeo→vídeo), o plano
    // HW já segura o último frame do vídeo (still-mode no switch / freeze() no slot de
    // imagem). O cover DOM aqui só ATRAPALHA: na Tizen o frame de vídeo NÃO é capturável no
    // DOM (WebCodecs/readback mortos, [[tizen-video-transition-floor]]) → o candidato cai
    // pra 'nextImage'/'lastFrameWc' e pinta uma IMAGEM STALE por cima do frame HW correto =
    // a "piscada do conteúdo anterior" reportada. Sair de vídeo sob AVPlay = plano HW cobre;
    // pula o cover DOM. (image→video segue usando o cover — heldCanvas da imagem, que
    // funciona no DOM e segura até o 1º frame do vídeo.)
    // EXCEÇÃO ZONA (2026-08-24): se o vídeo que saiu era DE ZONA, ele tocou no
    // <video> DOM do ZonePlayer — NÃO no plano AVPlay. O still-mode do plano HW
    // não está segurando nada → sem esta exceção, sair de zona com vídeo na
    // Tizen ficava SEM capa nenhuma (preto garantido).
    if (
      shouldSkipDomCoverOnVideoExit({
        avplayActive,
        prevType: prev?.type,
        prevZonaId: prev?.zonaId,
      })
    )
      return;

    // Mostra o freeze + arma safety generoso (30s): segura o frame anterior mesmo
    // se o próximo demorar a decodificar. Escondido no paint real
    // (handlePlaying/handleImageLoad).
    // Delega pro helper de componente (reusado pelo early-show do handleTimeUpdate).
    const showFreeze = showFreezeVisible;

    // SAINDO de um VÍDEO com último frame WebCodecs (Tizen/webOS): desenha o ÚLTIMO
    // frame do vídeo que SAI (prev). É o frame que JÁ estava na tela → zero preto
    // no fim + a chegada do vídeo/imagem novo vira corte de conteúdo (mascara o
    // "soco" de qualidade). ImageBitmap (não HTMLImageElement) → não tem
    // .complete/.naturalWidth; usa .width/.height. O canvas herda o object-fit do
    // <video> (effectiveFitMode) → mesma escala/crop, sem "soco" de dimensão.
    if (candidate === 'lastFrameWc') {
      const bitmap = prev ? lastFrameWcCache.get(prev.id) : null;
      const canvas = freezeFrameCanvasRef.current;
      if (canvas && bitmap && bitmap.width > 0) {
        const ctx = canvas.getContext('2d');
        if (ctx) {
          // NITIDEZ (Renato 2026-07-16): rasterizar o canvas na densidade da TELA
          // (não na do poster 1080p). O poster já é a resolução do vídeo, mas se o
          // backing for 1080p e a TV 4K, o COMPOSITOR faz o upscale bruto → mais mole
          // que o vídeo (rasterizado pelo HW). Escalo o backing PRESERVANDO a
          // proporção do poster até a densidade da tela → o upscale 1080p→tela é
          // feito 1× AQUI com imageSmoothingQuality 'high'; o object-fit do elemento
          // segue cuidando do enquadramento (sem distorção). Fallback = res do poster.
          // (Se a TV renderizar em 1920 lógico c/ dpr=1, o scale dá ~1 e o painel faz
          // o upscale igual pros dois — aí o resíduo é o realce de imagem que a TV
          // aplica só no vídeo HW, não no DOM/canvas: limite físico.)
          const dpr = window.devicePixelRatio || 1;
          const targetH = (canvas.clientHeight || bitmap.height) * dpr;
          const scale =
            bitmap.height > 0 ? Math.max(1, targetH / bitmap.height) : 1;
          canvas.width = Math.round(bitmap.width * scale);
          canvas.height = Math.round(bitmap.height * scale);
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
          heldCanvasSrcRef.current = null; // canvas agora segura frame de VÍDEO
          setFreezeCoverIsVideo(true); // capa = frame de vídeo → filtro de match aplica
          showFreeze();
        }
      }
      return;
    }

    // CORTE no meio do vídeo (hop do pulso): o frame que ESTAVA na tela no instante do
    // corte, capturado em `captureHopFrame`. Desenha e mostra — zero preto no hop.
    if (candidate === 'hopFrame') {
      const src = hopFrameCanvasRef.current;
      const canvas = freezeFrameCanvasRef.current;
      if (canvas && src && src.width > 0) {
        const ctx = canvas.getContext('2d');
        if (ctx) {
          canvas.width = src.width;
          canvas.height = src.height;
          ctx.drawImage(src, 0, 0, canvas.width, canvas.height);
          heldCanvasSrcRef.current = null;
          setFreezeCoverIsVideo(true);
          showFreeze();
        }
      }
      return;
    }

    // Saindo de IMAGEM: o canvas já tem a imagem anterior (capturada no load).
    if (candidate === 'heldCanvas') {
      setFreezeCoverIsVideo(false); // capa = IMAGEM → sem filtro (casa com a <img>)
      showFreeze();
      return;
    }

    // 'nextImage' (imagem de DESTINO pré-carregada — troca INVISÍVEL) ou
    // 'lastFrame' (último frame do vídeo que saiu, do cache — Etapa B). Ambos
    // desenham um <img> pronto no canvas. NÃO toca o decoder (drawImage de <img>).
    const img =
      candidate === 'nextImage'
        ? nextImageElRef.current
        : prev
          ? lastFrameCache.get(prev.id)
          : null;
    const canvas = freezeFrameCanvasRef.current;
    let drew = false;
    if (canvas && img && img.complete && img.naturalWidth > 0) {
      const ctx = canvas.getContext('2d');
      if (ctx) {
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        ctx.drawImage(img, 0, 0, img.naturalWidth, img.naturalHeight);
        heldCanvasSrcRef.current = null; // capa = nextImage/lastFrame, não a <img> atual
        // nextImage = IMAGEM (sem filtro); lastFrame = frame de vídeo (Android, onde o
        // filtro já é 'none' de qualquer forma). Base: só vídeo em Tizen recebe realce.
        setFreezeCoverIsVideo(candidate === 'lastFrame');
        showFreeze();
        drew = true;
      }
    }
    // FALLBACK (T-2, 2026-07-21): o candidato primário foi escolhido (ex.: nextImage,
    // prioridade > lastFrameWc) mas NÃO estava desenhável (o <img> de destino ainda
    // não estava `complete` — race na ENTRADA de zona vindo de vídeo). Sem fallback
    // ficava BLACK mesmo com o último frame do vídeo que SAIU (lastFrameWc) pronto.
    // Agora cai pra ele → cobre em vez de preto. Android/browser: lastFrameWcCache
    // vazio (só Tizen/webOS alimenta) → no-op, sem regressão.
    if (!drew && prev) {
      const fbBmp = lastFrameWcCache.get(prev.id);
      if (fbBmp && fbBmp.width > 0 && drawLastFrameWcToCanvas(fbBmp)) {
        setFreezeCoverIsVideo(true); // capa = frame de vídeo → filtro de match aplica
        showFreeze();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streamStore.currentMedia?.id]);

  const handleImageError = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      const media = streamStore.currentMedia;
      const target = e.currentTarget;
      const expectedSrc = playableUrl || media?.url;

      // Guard contra "phantom error": quando o src do <img> muda durante
      // um load pendente (ex: usePlayableUrl resolvendo do cache logo
      // depois do mount), o Chrome aborta a request anterior e dispara
      // onError com target.src diferente do src atual. Sem esse guard,
      // a UI mostra "Erro ao carregar imagem" mesmo a foto carregando OK
      // logo em seguida.
      if (target.src && expectedSrc && target.src !== expectedSrc) {
        console.warn(
          '[Stream] Ignoring image error from stale src (URL changed mid-load):',
          { stale: target.src, current: expectedSrc },
        );
        return;
      }

      // Cold-start / warm-up de cache (2026-06-30): a imagem resolveu REMOTE
      // (cache miss — o índice de cache ainda está subindo no boot/pós-OTA/reboot)
      // e a URL presigned do R2 pode estar apenas vencida. NÃO mostrar o overlay
      // vermelho de erro nem tratar como falha real: avança SILENCIOSAMENTE
      // (handleMediaEnd, mesmo caminho gracioso do safety-timeout de 10s). O
      // download em background popula o cache; nos próximos loops a imagem
      // resolve do arquivo LOCAL e toca normal. Antes, o primeiro item após um
      // update OTA/reboot mostrava "Erro ao carregar imagem" + pulava no susto —
      // a primeira coisa que o cliente via. Só tratamos como erro de verdade
      // quando a falha vem de um arquivo de CACHE LOCAL (file://, corrompido).
      if (playbackSource === 'remote') {
        // Cache HTTP envenenado (2026-10-19): a mesma URL já baixada SEM CORS deixa no
        // Chrome uma cópia sem `Access-Control-Allow-Origin` (o R2 não manda
        // `Vary: Origin`) e o <img crossOrigin> falha em ~4 ms. Num gatilho de 1 imagem
        // o skip abaixo encerrava o gatilho na hora — nada aparecia. Antes de pular,
        // rebaixa a URL com CORS ignorando o cache e tenta UMA vez de novo.
        if (shouldRetryImageOnce(expectedSrc, imageRetriedSrcRef.current)) {
          imageRetriedSrcRef.current = expectedSrc;
          const mediaId = media?.id;
          console.warn(
            '[Stream] Image remote load failed — renovando o cache HTTP com CORS e tentando de novo:',
            media?.url,
          );
          void refreshCorsCacheEntry(expectedSrc).finally(() => {
            if (streamStore.currentMedia?.id === mediaId) target.src = expectedSrc;
          });
          return;
        }
        console.warn(
          '[Stream] Image remote load failed during cache warm-up — advancing silently (no error overlay):',
          media?.url,
        );
        setIsImageLoading(false);
        // Falha isolada pula; se NENHUM item carrega, o freio segura em vez de rodar a
        // playlist em cascata (vide StreamStore.handleMediaFailure).
        streamStore.handleMediaFailure('image-remote-error');
        return;
      }

      // PHANTOM ERROR do abort de src (2026-09-22). O guard do topo NÃO cobre este
      // caso: quando o evento é despachado, `target.src` JÁ foi atualizado para a URL
      // nova, então ele bate com `expectedSrc` e o erro passa. Acontece quando o
      // `usePlayableUrl` resolve o cache logo depois do mount — o Chrome aborta o load
      // remoto pendente e dispara `onError` para um arquivo que está perfeito.
      //
      // Medido no tablet (Tab S10 FE, 2026-09-22): o arquivo tinha MD5 IDÊNTICO ao do
      // servidor, respondia 200 pelo `_capacitor_file_` e decodificava 1920x1080 dentro
      // do próprio WebView — e mesmo assim caía aqui, pintando o overlay vermelho e
      // ABANDONANDO o slot. Uma nova tentativa no MESMO src resolve.
      //
      // Só tratamos como erro real quando a SEGUNDA tentativa também falha, então
      // arquivo de cache corrompido continua sendo reportado (era o motivo deste ramo).
      if (shouldRetryImageOnce(expectedSrc, imageRetriedSrcRef.current)) {
        imageRetriedSrcRef.current = expectedSrc;
        console.warn(
          '[Stream] Image error possivelmente fantasma (abort de src) — recarregando uma vez:',
          expectedSrc,
        );
        target.src = expectedSrc;
        return;
      }

      console.error(
        '[Stream] Image load error (local cache file):',
        media?.url,
      );
      setIsImageLoading(false);
      streamStore.handleError('Erro ao carregar imagem');
    },
    [streamStore, playableUrl, playbackSource],
  );

  // 2026-06-25 (Renato): re-enfileira download do item que acabou de tocar
  // se ele estava em status='error' no cacheStatus (i.e. tocou via streaming
  // HTTPS porque a tentativa anterior falhou). queueDownload tem TODOS os
  // gates internos (isCached/isInQueue/status='downloading'|'saving'/cooldown
  // ativo → skip), então é no-op pra cacheados. Trigger natural: a rede
  // acabou de provar que serve o item (vídeo tocou inteiro), boa hora pra
  // tentar baixar de novo. queueDownload é síncrono (só seta status +
  // enqueue na DownloadQueue); o download real roda async em paralelo ao
  // próximo vídeo — 100% transparente ao usuário.
  //
  // StreamMediaItem (currentMedia) não carrega fileSize/checksum — pegamos
  // do cacheStatus (que sempre tem entrada pra items errored porque ele
  // foi tentado a baixar antes). Se não houver entrada, item nunca foi
  // enfileirado (caso raro: 'stream' type ou primeiro toque pré-prefetchAll)
  // → no-op silencioso.
  const retryCacheIfErrored = useCallback(
    (media: StreamMediaItem | null): void => {
      if (!media) return;
      const cached = mediaStore.cacheStatus.get(media.id);
      if (!cached) return;
      mediaStore.queueDownload({
        id: media.id,
        type: (media.type === 'stream'
          ? 'video'
          : media.type) as PlaylistItem['type'],
        name: media.name,
        url: media.url,
        duration: media.duration ?? 0,
        fileSize: cached.fileSize,
        checksum: cached.checksum,
        mimeType: media.mimeType ?? '',
        campanhaId: media.campanhaId,
        hasDurationOverride: media.hasDurationOverride,
      });
    },
    [mediaStore],
  );

  const handleVideoEnded = useCallback((): void => {
    const media = streamStore.currentMedia;
    const video = videoRef.current;
    const { shouldManualLoop, shouldCutShort, scheduledDuration } =
      videoLoopControlRef.current;

    // PHASE 1.1 — se já avançamos via EARLY HOLD (SSSP video→img), o `<video>`
    // segurado (pausado) pode disparar um `ended` tardio; ignora (já avançamos, o
    // currentMedia agora é a imagem). No-op no fluxo normal: no `ended` regular o
    // hold ainda não foi ligado (heldVideoForImage=false), então isto não dispara —
    // Android/browser/consumer ficam byte-idênticos.
    if (heldVideoForImageRef.current && media?.type === 'image') {
      return;
    }

    // Vídeo ACABOU (troca de src iminente): libera o hide do vídeo → o freeze (já
    // pronto atrás desde o early-show) é revelado quando o overlay cai. Fim da
    // janela "vídeo visível por cima" → sem preto (freeze já lá) e sem volta.
    setIsEarlyFreeze(false);

    // If we're in manual loop mode, restart the video
    if (shouldManualLoop && video) {
      console.log('[Stream] Video ended in manual loop mode, restarting');
      video.currentTime = 0;
      video.play().catch((err) => {
        console.error('[Stream] Error replaying video:', err);
      });
      return;
    }

    // 2026-05-25 — bypass do detector de "onEnded prematuro" quando o
    // sistema acaba de fazer Cut Short intencional. Cenário:
    //   - Slot da playlist tem duration_seconds=10
    //   - Vídeo real tem 51s → ratio 0.2
    //   - handleTimeUpdate detecta currentTime>=scheduledDuration → loga
    //     "Cut short" + chama handleVideoEnded sincronamente
    //   - handleVideoEnded caía no detector de prematuro (currentTime/duration
    //     = 20% < 80%) → logava warning falso positivo "prematuro detectado"
    //     + tentava retomar play() (em vão, slot já avançou)
    //
    // Fix: se shouldCutShort=true E currentTime >= scheduledDuration (com
    // tolerância de 0.5s pra arredondamento de timeupdate), o end NÃO é
    // prematuro — é o cut intencional. Pula detector e segue pro avanço
    // normal.
    //
    // Casos NÃO bypassados (ainda passam pelo detector — corretos):
    //   - shouldCutShort=false (sem override) → vídeo terminou natural,
    //     detector verifica se foi cedo
    //   - shouldCutShort=true MAS currentTime < scheduledDuration → onEnded
    //     disparou ANTES do cut acontecer = prematuro de verdade
    if (
      media &&
      video &&
      shouldCutShort &&
      scheduledDuration > 0 &&
      video.currentTime >= scheduledDuration - 0.5
    ) {
      // Cai pra lógica de avanço normal abaixo (sem warning de "prematuro").
    } else if (media && video) {
      const currentTime = video.currentTime;
      const apiDuration = media.duration ?? 0;
      const trustedDuration = resolveTrustedNaturalDuration(
        apiDuration,
        video.duration,
      );
      const duration = trustedDuration > 0 ? trustedDuration : video.duration;
      const tracker = prematureEndedRetryRef.current;

      // Reseta tracker se mídia mudou
      if (tracker.mediaId !== media.id) {
        tracker.mediaId = media.id;
        tracker.attempted = false;
      }

      const isPremature =
        duration > 0 &&
        duration !== Infinity &&
        currentTime > 0 &&
        currentTime < duration * 0.8;

      if (isPremature && !tracker.attempted) {
        tracker.attempted = true;
        otaLog.warn('Stream onEnded prematuro — retomando playback', {
          mediaName: media.name?.slice(0, 40),
          currentTime: currentTime.toFixed(2),
          videoDuration: video.duration.toFixed(2),
          apiDuration,
          trustedDuration: duration.toFixed(2),
        });
        console.warn(
          '[Stream] onEnded prematuro detectado — tentando retomar 1×',
          {
            mediaName: media.name,
            currentTime: currentTime.toFixed(2),
            duration: duration.toFixed(2),
            ratio: `${((currentTime / duration) * 100).toFixed(0)}%`,
            readyState: video.readyState,
            networkState: video.networkState,
            buffered:
              video.buffered.length > 0
                ? `${video.buffered.start(0).toFixed(2)}-${video.buffered
                    .end(video.buffered.length - 1)
                    .toFixed(2)}`
                : 'empty',
            mediaError: video.error?.message ?? null,
          },
        );
        // Tenta continuar do ponto atual (NÃO reset pra 0 — perderia
        // o progresso já tocado e poderia loop infinito).
        video.play().catch((err) => {
          console.warn(
            '[Stream] Retry play() falhou após onEnded prematuro — avançando',
            err?.message ?? err,
          );
          streamStore.handleMediaEnd(
            mediaVersionRef.current,
            playingMediaIdRef.current,
          );
        });
        return;
      }

      if (isPremature && tracker.attempted) {
        // 2ª vez prematuro na mesma mídia — aceita e avança
        console.warn(
          '[Stream] onEnded prematuro 2ª vez — avançando pro próximo',
          {
            mediaName: media.name,
            currentTime: currentTime.toFixed(2),
            duration: duration.toFixed(2),
          },
        );

        // Telemetria aditiva (não altera fluxo): emite playback_error
        // SÓ no premature aceito (mirror do tracker.attempted dedupe — uma
        // vez por mídia, não em cada retry). NUNCA emite em cut-short
        // intencional do operador: re-checa o MESMO guard inline antes.
        const isOverrideCutShort =
          videoLoopControlRef.current.shouldCutShort === true &&
          videoLoopControlRef.current.scheduledDuration > 0 &&
          currentTime >= videoLoopControlRef.current.scheduledDuration - 0.5;
        if (!isOverrideCutShort) {
          analyticsService.trackPlaybackError({
            mediaId: media.id,
            mediaName: media.name,
            reason: 'PREMATURE_END',
            durationActual: currentTime,
            durationNatural: duration,
            ratio: `${((currentTime / duration) * 100).toFixed(0)}%`,
          });
        }
      }
    }

    const jaReportadoPeloSwap =
      !!media && scheduledCompleteEmittedForRef.current === media.id;
    if (jaReportadoPeloSwap) {
      // O swap agendado já emitiu a conclusão deste item. O elemento antigo só chega aqui se
      // terminar na fresta antes do React tirar os handlers dele — não emitir de novo.
      scheduledCompleteEmittedForRef.current = null;
    }

    if (media && video && !jaReportadoPeloSwap) {
      // Track playback completion
      const reportDuration = Math.round(video.duration || 0);
      // Use campanhaId from the media item if available (prefer number from item)
      const campanhaId = media.campanhaId ?? undefined;
      // CP10 — Snapshot do slot context ANTES do setTimeout pra capturar
      // o slot que acabou de tocar (não o próximo).
      const slotCtx = getSlotContextForAnalytics();

      setTimeout(() => {
        analyticsService.trackPlaybackComplete(
          media.id,
          reportDuration,
          media.name,
          campanhaId,
          slotCtx ?? undefined,
        );
        if (slotCtx) {
          clearSlotStartedDedup(); // exibição terminou → o mesmo slot pode reemitir na próxima volta
          analyticsService.trackSlotCompleted({
            ...slotCtx,
            durationActual: reportDuration,
            completedNaturally: true,
          });
        }
      }, 0);
    }

    // SEAMLESS v2 (Renato 2026-07-04/05): handleVideoEnded NÃO mexe mais no
    // canvas. O antigo "Canvas Bridge de vídeo" (screenshot do frame ~250ms
    // antes do fim + Promise.race(250ms) que protegia a race do decoder OMX)
    // foi REMOVIDO — vivia atrás de `enableCanvasBridge=false` (código morto
    // desde a seamless-v2). A cobertura de preto saindo de vídeo agora é feita
    // no useLayoutEffect (Etapa B: último frame do vídeo, do lastFrameCache,
    // desenhado no canvas ANTES do paint). Aqui só avança de forma instantânea
    // (o release do decoder não colide com nada — não há mais readback em voo).
    // LAST-FRAME (Renato 2026-07-16): captura o ÚLTIMO frame REAL 1× por vídeo,
    // AQUI (no fim natural E no cut-short/override — que roteia pra cá), quando o
    // <video> ainda segura o frame do término. O frame de um vídeo é INVARIÁVEL →
    // captura 1× (guard `!has`) e REUSA nas próximas exibições (sem loop contínuo,
    // sem recaptura, sem CPU à toa). Elimina o "rewind": não pega mais um frame de
    // ANTES do fim. Só Android/browser (Tizen/webOS = WebCodecs lastFrameWc; readback
    // de <video> lá volta preto). SÍNCRONO (drawImage) antes do avanço → sem 2º
    // consumidor do decoder OMX (ordem de release intacta). O `img.onload` (decode
    // da dataURL, fora do <video>) grava pra PRÓXIMA exibição; não bloqueia nada.
    if (
      video &&
      media &&
      media.type === 'video' &&
      platform.platform !== 'tizen' &&
      platform.platform !== 'webos' &&
      video.videoWidth > 0 &&
      !lastFrameCache.hasStored(media.id)
    ) {
      try {
        const c = document.createElement('canvas');
        c.width = video.videoWidth;
        c.height = video.videoHeight;
        const cx = c.getContext('2d');
        if (cx) {
          cx.drawImage(video, 0, 0, c.width, c.height);
          const capId = media.id;
          // JPEG comprimido (não o bitmap) — o decode só acontece no `prepare`.
          c.toBlob(
            (jpeg) => {
              if (jpeg) lastFrameCache.store(capId, jpeg);
            },
            'image/jpeg',
            0.9,
          );
        }
      } catch {
        // canvas tainted (cross-origin) / SecurityError → ignora; re-tenta na
        // próxima exibição. NUNCA toca no decoder.
      }
    }

    // PHASE 1 — HOLD-FRAME (Tizen/webOS, video→img). Em vez de deixar o `<video>`
    // que SAI ser desmontado no avanço (o tear-down do HW overlay PISCA PRETO na
    // Tizen, ACIMA do canvas de freeze — overlays de HW ignoram z-index), o mantemos
    // MONTADO + VISÍVEL, pausado no último frame (o HW overlay segura o frame —
    // confirmado no SsspHoldProbe na SSSP 6.5 sem WebCodecs). A ImagePlayer de destino
    // pinta POR BAIXO; ao carregar (handleImageLoad) escondemos o vídeo (visibility:
    // hidden — limpo) revelando a imagem, e desmontamos logo depois → zero preto.
    // SÓ video→img (a imagem não usa o decoder → sem conflito, diferente de
    // video→video). Android/browser: shouldHoldVideoFrameForImage=false → intocado
    // (vídeo desmonta como hoje). NÃO lê frame do `<video>` (não reabre readback preto).
    if (
      shouldHoldVideoFrameForImage({
        platform: platform.platform,
        currentType: media?.type,
        nextType: streamStore.nextMedia?.type,
      })
    ) {
      video?.pause(); // congela no último frame (cobre fim natural E cut-short)
      setHeldVideoForImage(true);
      setHeldVideoHidden(false);
    }

    // MOTOR AGENDADO: a troca é pelo relógio, não pelo `ended`. O vídeo acabou um pouco antes
    // de T → segura o último frame (o <video> parado já o mostra) até o swap. A impressão já
    // foi contada acima, como no fim natural.
    const agendada = scheduledSwitchRef.current;
    if (
      agendada &&
      media &&
      agendada.mediaId === media.id &&
      !shouldManualLoop
    ) {
      video?.pause();
      return;
    }

    retryCacheIfErrored(media);
    streamStore.handleMediaEnd(
      mediaVersionRef.current,
      playingMediaIdRef.current,
    );
  }, [streamStore, retryCacheIfErrored, platform.platform]);

  const decodeErrorTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const lastVideoTimeRef = useRef(0);

  const handleVideoError = useCallback((): void => {
    const video = videoRef.current;
    const code = video?.error?.code;

    // DECODE errors (code 3) are typically audio-only — the video track was
    // fine but an incompatible audio packet crashes the whole Chromium
    // pipeline. Instead of skipping the content, freeze on the last good
    // frame and let interactive element timers (setTimeout-based) keep
    // running. Schedule advancement after the remaining duration so the
    // playlist loop continues normally.
    if (video && code === 3) {
      const duration = video.duration || 30;
      const current = video.currentTime || 0;
      const remaining = Math.max(duration - current, 1);
      console.warn(
        `[Stream] Audio decode error at ${current.toFixed(1)}s — freezing frame, advancing in ${remaining.toFixed(1)}s`,
      );
      // Telemetria aditiva (não altera o freeze/setTimeout/handleMediaEnd).
      if (streamStore.currentMedia) {
        analyticsService.trackPlaybackError({
          mediaId: streamStore.currentMedia.id,
          mediaName: streamStore.currentMedia.name,
          reason: 'AUDIO_DECODE_ERROR',
          errorCode: '3',
          errorMessage: video.error?.message ?? undefined,
          durationActual: current,
          durationNatural: duration,
          networkState: video.networkState,
          readyState: video.readyState,
        });
      }
      const version = streamStore.playlistVersion;
      decodeErrorTimerRef.current = setTimeout(() => {
        streamStore.handleMediaEnd(version);
      }, remaining * 1000);
      return;
    }

    // ESPERAR ANTES DE PULAR. Falha transitória ao abrir o arquivo (medida no tablet com
    // cache local, sempre no vídeo que vem após uma imagem): o hook re-resolve a URL e o
    // efeito reaplica o src; o segundo load dá canPlay ~190 ms depois. Pular na hora mostrava
    // o item seguinte por ~130 ms e o pulso de sincronismo trazia o player de volta — glitch
    // visível no videowall. Aqui só se dá carência: se nesse meio-tempo o elemento ficou
    // pronto (sem erro, com dados), não há o que pular; senão, pula como sempre.
    const mediaIdForRetry = streamStore.currentMedia?.id;
    const showingKey = mediaIdForRetry
      ? `${mediaIdForRetry}#${streamStore.currentItemStartedAt}`
      : undefined;
    if (
      video &&
      shouldRetryVideoOpen({
        errorCode: code,
        hasEverPlayed: hasEverPlayedRef.current,
        mediaId: showingKey,
        retriedForMediaId: videoOpenRetriedForRef.current,
      })
    ) {
      videoOpenRetriedForRef.current = showingKey ?? null;
      otaLog.warn('Stream video onError → carência antes de pular', {
        code: code ?? '?',
        msg: (video.error?.message ?? '?').slice(0, 80),
        contentId: mediaIdForRetry?.slice(0, 8) ?? '?',
        graceMs: VIDEO_OPEN_ERROR_GRACE_MS,
      });
      const version = streamStore.playlistVersion;
      setTimeout(() => {
        if (streamStore.currentMedia?.id !== mediaIdForRetry) return;
        if (streamStore.playlistVersion !== version) return;
        const v = videoRef.current;
        if (v && !v.error && v.readyState >= 2) {
          otaLog.info('Stream video onError → recuperou sozinho, sem pular', {
            contentId: mediaIdForRetry?.slice(0, 8) ?? '?',
            readyState: v.readyState,
          });
          return;
        }
        otaLog.warn(
          'Stream video onError → não recuperou na carência, pulando',
          {
            contentId: mediaIdForRetry?.slice(0, 8) ?? '?',
            readyState: v?.readyState ?? '?',
            err: v?.error?.code ?? '?',
          },
        );
        setIsVideoLoading(false);
        // Segurado pelo freio de cascata → mantém o último quadro bom na tela.
        if (!streamStore.handleMediaFailure('video-open-error', version)) {
          setIsFreezeFrameVisible(false);
        }
      }, VIDEO_OPEN_ERROR_GRACE_MS);
      return;
    }

    setIsVideoLoading(false);
    console.error('[Stream] Video error → skip:', {
      message: video?.error?.message,
      code,
      contentId: streamStore.currentMedia?.id,
    });
    // 2026-06-13 — Log nativo pra diagnosticar root cause em campo
    // (chrome://inspect nem sempre acessível). Aparece em `adb logcat | grep OtaDebug`.
    otaLog.error('Stream video onError', {
      code: code ?? '?',
      msg: (video?.error?.message ?? '?').slice(0, 80),
      currentSrc: (video?.currentSrc ?? '?').slice(0, 100),
      networkState: video?.networkState ?? '?',
      readyState: video?.readyState ?? '?',
      contentId: streamStore.currentMedia?.id?.slice(0, 8) ?? '?',
    });
    // Telemetria aditiva (não altera o skip): emite playback_error pra
    // load/codec errors (code != 3). Code 2 = LOAD_ERROR (rede);
    // code 4 ou mensagem "not supported"/"NotSupportedError" = CODEC_UNSUPPORTED.
    if (streamStore.currentMedia) {
      const errorMessage = video?.error?.message ?? '';
      const isCodecUnsupported =
        code === 4 || /not supported|NotSupportedError/i.test(errorMessage);
      analyticsService.trackPlaybackError({
        mediaId: streamStore.currentMedia.id,
        mediaName: streamStore.currentMedia.name,
        reason: isCodecUnsupported ? 'CODEC_UNSUPPORTED' : 'LOAD_ERROR',
        errorCode: code != null ? String(code) : undefined,
        errorMessage: errorMessage || undefined,
        networkState: video?.networkState,
        readyState: video?.readyState,
      });
    }
    if (
      !streamStore.handleMediaFailure(
        'video-error',
        streamStore.playlistVersion,
      )
    ) {
      setIsFreezeFrameVisible(false);
    }
  }, [streamStore, handlePlayFailure]);

  // Timer que delay a apresentação do overlay "Carregando vídeo..." em
  // transições rápidas. Se onCanPlay disparar dentro de LOADING_OVERLAY_DELAY_MS,
  // o overlay NUNCA aparece — caso típico: gapless transition do Tizen
  // (a partir do 2º ciclo) e cache local com decoder warm.
  const loadingOverlayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const LOADING_OVERLAY_DELAY_MS = 350;

  const cancelLoadingOverlayTimer = useCallback((): void => {
    if (loadingOverlayTimerRef.current) {
      clearTimeout(loadingOverlayTimerRef.current);
      loadingOverlayTimerRef.current = null;
    }
  }, []);

  const handleLoadStart = useCallback((): void => {
    // Não mostra overlay imediatamente. Agenda pra mostrar após 350ms se
    // o vídeo ainda não estiver pronto (readyState < 3). Em transições
    // rápidas (cache local, gapless Tizen no 2º+ ciclo), onCanPlay
    // dispara antes do timer → overlay nunca aparece.
    streamStore.setPlaying(false);
    cancelLoadingOverlayTimer();
    loadingOverlayTimerRef.current = setTimeout(() => {
      const video = videoRef.current;
      if (video && video.readyState < 3) {
        setIsVideoLoading(true);
      }
      loadingOverlayTimerRef.current = null;
    }, LOADING_OVERLAY_DELAY_MS);
  }, [streamStore, cancelLoadingOverlayTimer]);

  // FIX B — loadedmetadata/loadeddata → o vídeo tem dados (readyState subiu) →
  // conteúdo válido, apenas carregando. Cancela o watchdog de avanço ANTES do
  // canplay/playing (cancelamento o mais cedo possível → zero risco de pular
  // conteúdo lento-mas-válido).
  const handleLoadedData = useCallback((): void => {
    clearAdvanceWatchdog();
  }, [clearAdvanceWatchdog]);

  // ─── BACKEND AVPLAY (Fase 1) — callbacks que o <AvplayVideo> chama ───
  // 1º frame real do AVPlay (currentTime avançou): espelha o essencial do handleCanPlay/
  // handlePlaying — limpa loading, cancela watchdog, marca tocando, fixa a versão do slot.
  const handleAvplayFirstFrame = useCallback((): void => {
    cancelLoadingOverlayTimer();
    clearAdvanceWatchdog();
    clearBufferingWatchdog();
    setIsVideoLoading(false);

    // SYNC NA ENTRADA DO VÍDEO (TV). Equivalente ao seek que o `<video>` do DOM faz no
    // `canplay`. Sem isto o AVPlay começa do ZERO enquanto o grupo já está N segundos
    // dentro do slot — e como abrir/preparar o plano HW leva ~1s, o vídeo herdava esse
    // atraso pelo slot inteiro. Medido na TV: resíduo de ~1,4 a 1,9s SÓ nos slots de
    // vídeo, enquanto as imagens ficavam dentro de 0,6s. Corrigir aqui, na origem, é
    // mais preciso que esperar o pulso (que só agiria 2s depois e acima de 1,5s).
    const mediaAv = streamStore.currentMedia;
    const handleAv = avplaySyncHandleRef.current;
    if (AVPLAY_ENTRY_SEEK_ENABLED && mediaAv?.playbackStartedAt && handleAv) {
      const alvo = resolveLoopTarget({
        playbackStartedAt: mediaAv.playbackStartedAt,
        durations: streamStore.playlist.map((it) => it.duration || 0),
        // ⚠️ Aqui NÃO vale o `paintLeadMs` inteiro. Ele compensa o caminho decisão→pixel,
        // e neste ponto o pipeline JÁ FOI PAGO — estamos no 1º quadro. Somar tudo de novo
        // adianta o seek pelo tempo que já passou: foi o que aconteceu na 1.0.451, que
        // levou a TV de 2,09s ATRASADA para 1,29s ADIANTADA contra o tablet. Sobra só a
        // latência do painel, que o pipeline não cobre.
        nowMs: serverNow() + avplayEntryLeadMs(platform.platform),
      });
      const atualAv = handleAv.currentSeconds() ?? 0;
      if (
        alvo &&
        alvo.index === streamStore.currentIndex &&
        alvo.offsetSeconds - atualAv > AVPLAY_ENTRY_SYNC_TOLERANCE_S
      ) {
        lastPulseActionRef.current = Date.now();
        console.log(
          `[Stream] Sync AVPlay na entrada: ${atualAv.toFixed(1)}s → ${alvo.offsetSeconds.toFixed(1)}s`,
        );
        handleAv.seekTo(alvo.offsetSeconds);
      }
    }
    // SEAMLESS + AVPLAY (2026-07-21): o vídeo AVPlay pinta no PLANO HW (abaixo do DOM),
    // então NUNCA dispara o `onPlaying`/`onLoad` de um elemento DOM que esconderia o
    // freeze cover. Sem isto, o canvas de freeze (z-5, opacity 1) segurando o ÚLTIMO
    // frame do slot anterior (uma IMAGEM) fica COBRINDO o vídeo pelo slot inteiro → o
    // operador vê a imagem congelada repetida enquanto o vídeo toca invisível embaixo
    // (t conta, st=PLAYING). O 1º frame do AVPlay É o "paint real" deste caminho →
    // esconde o cover aqui (o hold até aqui, ~505ms, mascara o load = gapless).
    setIsFreezeFrameVisible(false);
    streamStore.setPlaying(true);
    streamStore.notePlaybackStarted();
    hasEverPlayedRef.current = true;
    // BLK: no caminho AVPlay o `handlePlaying` (do `<video>` DOM) NUNCA roda — o vídeo pinta no
    // plano HW. Sem isto o recycle-boot só saía pelo CAP de 20s: medido na SSSP, o espião gravou
    // `cap expirado → UI normal` nos dois reloads, ou seja 20s de tela apagada por reciclagem em
    // vez dos ~2-4s do boot. Este É o "1º frame real" deste caminho.
    finishRecycleBoot();
    // Fecha o orçamento de tempo do boot: este é o instante em que o preto acabou de verdade.
    // Uma escrita só de localStorage, aqui, no fim — nada durante o boot (vide bootTiming).
    markBoot('frame');
    flushBootTiming();
    mediaVersionRef.current = streamStore.playlistVersion;
    playingMediaIdRef.current = streamStore.currentMedia?.id;
  }, [
    cancelLoadingOverlayTimer,
    clearAdvanceWatchdog,
    clearBufferingWatchdog,
    streamStore,
  ]);

  // Fim do vídeo AVPlay (natural OU cut-short — o AvplayVideo resolve loop/corte internamente):
  // avança pro próximo slot, version-gated. CP10: fecha o slot do VÍDEO (o handleVideoEnded
  // do <video> NÃO roda neste caminho → sem isto a impressão de vídeo não fechava sob AVPlay).
  const handleAvplayEnded = useCallback((): void => {
    const media = streamStore.currentMedia;
    if (media) {
      const campanhaId = media.campanhaId ?? undefined;
      const reportDuration = Math.round(media.duration || 0);
      const slotCtx = getSlotContextForAnalytics();
      analyticsService.trackPlaybackComplete(
        media.id,
        reportDuration,
        media.name,
        campanhaId,
        slotCtx ?? undefined,
      );
      if (slotCtx) {
        clearSlotStartedDedup(); // exibição terminou → o mesmo slot pode reemitir na próxima volta
        analyticsService.trackSlotCompleted({
          ...slotCtx,
          durationActual: reportDuration,
          completedNaturally: true,
        });
      }
    }
    streamStore.handleMediaEnd(
      mediaVersionRef.current,
      playingMediaIdRef.current,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streamStore]);

  // Falha do AVPlay (open/prepare/play/API ausente) → cai pro <video> (fallback 1ª classe).
  const handleAvplayFallback = useCallback((reason: string): void => {
    console.warn('[Stream][AVPlay] fallback pro <video>:', reason);
    // Erro REAL do AVPlay (open/prepare/play) vem prefixado "avplay:". O skip de vídeo
    // remoto ("non-file-url:") é SOFT → não trava o re-attempt no próximo vídeo cacheado.
    avplayHardErrorRef.current = reason.startsWith('avplay:');
    setAvplayFallback(true);
  }, []);

  /**
   * A URL do slot não é mídia — é PÁGINA (ex.: `youtube.com/watch?v=…`). PULA o slot.
   *
   * <p>🔴 POR QUE NÃO É `handleAvplayFallback` (bug medido em campo, 2026-07-30). Cair pro
   * `<video>` aqui era o que matava: o Chromium 85 da TV não toca página nem HLS, então o
   * `<video>` ficava preso em `readyState=0, 0x0` — preto PERMANENTE. E como o AVPlay já tinha
   * aberto a URL e errado (`errors:1, plays:0`), o pipeline morria e **a campanha INTEIRA parava de
   * tocar**, não só o item ruim. Assinatura na TV: 2 `<video>` em readyState 0 e ZERO `<object>`
   * do AVPlay.
   *
   * <p>Aqui a sessão do AVPlay está INTACTA — a guarda recusou antes do `open()`, sem stop e sem
   * close (vide `playableUrl` + `tizenAvplay.load`). O plano de HW segue com o frame anterior, então
   * pular é visualmente contínuo: nem preto, nem fallback quebrado.
   *
   * <p>NÃO emite `slot_started`: o conteúdo não tocou, e contar exibição de algo que não apareceu
   * inflaria os mínimos contratuais (R1). O `handleMediaEnd` só avança o índice.
   *
   * <p>Fica registrado no `otaLog` (e no breadcrumb `load-skip:unplayable`) porque o sintoma para o
   * operador é "meu conteúdo não aparece e ninguém avisa" — sem log, isso é indiagnosticável.
   */
  const handleAvplayUnplayable = useCallback(
    (reason: string): void => {
      const media = streamStore.currentMedia;
      otaLog.warn('Stream: slot PULADO — URL não é mídia tocável', {
        reason,
        contentId: media?.id?.slice(0, 8),
        contentName: media?.name?.slice(0, 40),
        tipo: media?.type,
      });
      // Mesmo caminho gracioso do safety-timeout: avança sem overlay de erro na tela da loja
      // (ou segura, se a playlist inteira está falhando — freio de cascata).
      streamStore.handleMediaFailure(
        `avplay-unplayable:${reason}`,
        streamStore.playlistVersion,
        media?.id,
      );
    },
    [streamStore],
  );

  const handleCanPlay = useCallback((): void => {
    const video = videoRef.current;
    const media = streamStore.currentMedia;
    // Cancela timer pendente — vídeo carregou rápido o suficiente
    cancelLoadingOverlayTimer();
    clearAdvanceWatchdog(); // FIX B — readyState>=HAVE_CURRENT_DATA → tocável.
    setIsVideoLoading(false);

    // Capture playlist version at the time this video STARTS playing
    mediaVersionRef.current = streamStore.playlistVersion;
    playingMediaIdRef.current = streamStore.currentMedia?.id;

    // Set duration for CSS animation
    // Priority: API scheduled duration > actual video duration > default
    const scheduledDuration = media?.duration;
    const actualDuration = video?.duration;
    const hasOverride = media?.hasDurationOverride === true;

    let shouldManualLoop = false;
    let shouldCutShort = false;
    let effectiveDuration = scheduledDuration || actualDuration || 10;

    if (!hasOverride) {
      // Sem override do operador → NUNCA cortar/loopar por ratio scheduled/actual.
      // WebView Samsung reporta duration provisória (~4–13s); durationchange corrige.
      shouldManualLoop = false;
      shouldCutShort = false;
      const trusted = resolveTrustedNaturalDuration(
        scheduledDuration,
        actualDuration,
      );
      if (trusted > 0) {
        effectiveDuration = trusted;
      } else if (scheduledDuration) {
        effectiveDuration = scheduledDuration;
      } else if (actualDuration) {
        effectiveDuration = actualDuration;
      }
      otaLog.info('Stream canPlay: natural duration', {
        name: media?.name?.slice(0, 40),
        contentId: media?.id?.slice(0, 8),
        scheduled: scheduledDuration,
        actual: actualDuration?.toFixed?.(2) ?? actualDuration,
        trusted,
        tier: playbackStore.currentTier,
      });
    } else if (
      scheduledDuration &&
      actualDuration &&
      actualDuration !== Infinity
    ) {
      if (looksProvisionalWebViewDuration(actualDuration, scheduledDuration)) {
        // Não decidir cut/loop com metadata provisória — durationchange
        // reavalia quando actual estabilizar (~102s no dia das mulheres).
        shouldManualLoop = false;
        shouldCutShort = false;
        effectiveDuration = scheduledDuration;
        otaLog.warn(
          'Stream canPlay: override deferred (provisional duration)',
          {
            name: media?.name?.slice(0, 40),
            contentId: media?.id?.slice(0, 8),
            scheduled: scheduledDuration,
            actual: actualDuration.toFixed(2),
          },
        );
      } else {
        const decision = resolveOverrideLoopDecision(
          scheduledDuration,
          actualDuration,
        );
        shouldManualLoop = decision.shouldManualLoop;
        shouldCutShort = decision.shouldCutShort;
        effectiveDuration = decision.effectiveDuration;
        if (decision.shouldManualLoop) {
          otaLog.warn('Stream canPlay: manual loop (override)', {
            name: media?.name?.slice(0, 40),
            scheduled: scheduledDuration,
            actual: actualDuration.toFixed(2),
            ratio: (scheduledDuration / actualDuration).toFixed(2),
          });
        }
      }
    }

    // Store loop control info
    videoLoopControlRef.current = {
      shouldManualLoop,
      shouldCutShort,
      scheduledDuration: scheduledDuration || 0,
      actualDuration: actualDuration || 0,
      playbackStartTime: Date.now(),
    };

    setMediaDuration(effectiveDuration);
    canPlayReachedRef.current = true;
    earlyFreezeDoneRef.current = false; // arma o early-freeze pra ESTA exibição
    earlyHoldDoneRef.current = false; // PHASE 1.1 — arma o early-hold pra ESTA exibição

    if (video) {
      // `canplay` dispara de novo depois de CADA seek. Zerar a velocidade aqui anulava a
      // correção fina por velocidade em curso toda vez que o pulso dava um seek — a
      // velocidade só se aplica quando a MÍDIA é nova.
      if (syncedMediaIdRef.current !== streamStore.currentMedia?.id) {
        video.playbackRate = playbackSpeed;
      }

      // Synchronized playback for videowall (decision: all players start
      // together and stay aligned with the loop's canonical clock).
      //
      // CONTRATO COM SERVER (a partir de 2026-05):
      //   - O server SÓ seta `playbackStartedAt` quando consegue garantir que
      //     todos os "peers" do player veem a mesma playlist (modos
      //     OVERRIDE_GROUP / OVERRIDE_PLAYLIST exclusivos, sem nenhum filtro
      //     per-player tendo removido conteúdo). Quando setado, o valor é
      //     uma EPOCH FIXA — igual pra todos os items da playlist E pra
      //     todos os peers — então a fórmula `(now - epoch) % totalDuration`
      //     dá loopPos deterministicamente.
      //   - Quando `playbackStartedAt` vem null/undefined (modo MERGE,
      //     FALLBACK, ou override com filtros): NÃO sincroniza. O player
      //     toca em ordem natural usando `onEnded` pra avançar.
      //
      // Bug histórico (resolvido 2026-05): o server setava timestamp DIFERENTE
      // por programação (campanha) — fazendo cada item da playlist mesclada
      // ter um "zero" do loop diferente, causando hopping rápido entre items
      // quando havia >1 campanha. Hoje o server entrega 1 timestamp único
      // (epoch fixa) ou nenhum.
      //
      // Guard against infinite loop: canplay fires multiple times (including
      // after the seek itself), so we record the synced media id and skip.
      const media = streamStore.currentMedia;
      if (
        media?.playbackStartedAt &&
        video.duration &&
        video.duration !== Infinity &&
        syncedMediaIdRef.current !== media.id
      ) {
        const playlist = streamStore.playlist;
        const target = resolveLoopTarget({
          playbackStartedAt: media.playbackStartedAt,
          durations: playlist.map((it) => it.duration || 0),
          nowMs: serverNow() + videoPaintLeadMs,
        });

        if (target) {
          const { index: targetIndex, offsetSeconds: targetOffset } = target;
          const loopPosition = target.loopPosition;
          const totalLoopDuration = target.loopTotal;

          if (targetIndex !== streamStore.currentIndex) {
            // We're on the wrong item — hop. Stream component will reload
            // the correct video with its own canplay, which will then seek
            // to the right offset.
            console.log(
              `[Stream] Sync: hopping to item ${targetIndex} (offset ${targetOffset.toFixed(1)}s, loopPos ${loopPosition.toFixed(1)}s of ${totalLoopDuration.toFixed(0)}s)`,
            );
            streamStore.setCurrentIndex(targetIndex);
          } else if (targetOffset > VIDEO_ENTRY_SEEK_MIN_S) {
            // Same item — seek inside it (latência do último seek somada, igual ao pulso)
            seekIssuedAtRef.current = Date.now();
            video.currentTime = Math.min(
              targetOffset + Math.max(0, seekLatencyMsRef.current) / 1000,
              video.duration - 0.5,
            );
            console.log(
              `[Stream] Sync: seeking to ${targetOffset.toFixed(1)}s (loopPos ${loopPosition.toFixed(1)}s, item ${targetIndex})`,
            );
          }
        }

        syncedMediaIdRef.current = media.id;
      }
    }

    streamStore.setPlaying(true);
  }, [streamStore, playbackSpeed, clearAdvanceWatchdog, videoPaintLeadMs]);

  /** WebView Samsung reporta duration provisória no canplay — atualiza quando estabiliza. */
  const handleDurationChange = useCallback((): void => {
    const video = videoRef.current;
    const media = streamStore.currentMedia;
    if (!video || !media || media.type !== 'video') return;

    const actual = video.duration;
    if (!actual || !Number.isFinite(actual)) return;

    const prev = videoLoopControlRef.current.actualDuration;
    if (prev > 0 && Math.abs(actual - prev) < 1) return;

    if (media.hasDurationOverride === true) {
      const scheduled = media.duration || actual;
      if (looksProvisionalWebViewDuration(actual, scheduled)) return;

      const decision = resolveOverrideLoopDecision(scheduled, actual);
      otaLog.info('Stream durationchange: override re-evaluated', {
        name: media?.name?.slice(0, 40),
        scheduled,
        actual: actual.toFixed(2),
        manualLoop: decision.shouldManualLoop,
        cutShort: decision.shouldCutShort,
      });
      videoLoopControlRef.current = {
        shouldManualLoop: decision.shouldManualLoop,
        shouldCutShort: decision.shouldCutShort,
        scheduledDuration: scheduled,
        actualDuration: actual,
        playbackStartTime:
          videoLoopControlRef.current.playbackStartTime || Date.now(),
      };
      setMediaDuration(decision.effectiveDuration);
      return;
    }

    otaLog.info('Stream durationchange: natural updated', {
      name: media?.name?.slice(0, 40),
      actual: actual.toFixed(2),
      prev: prev > 0 ? prev.toFixed(2) : '?',
      api: media.duration ?? '?',
    });
    const trusted = resolveTrustedNaturalDuration(media.duration, actual);
    videoLoopControlRef.current = {
      shouldManualLoop: false,
      shouldCutShort: false,
      scheduledDuration: media.duration || actual,
      actualDuration: actual,
      playbackStartTime:
        videoLoopControlRef.current.playbackStartTime || Date.now(),
    };
    setMediaDuration(trusted > 0 ? trusted : actual);
  }, [streamStore]);

  const attemptDecoderRecovery = useCallback(
    (reason: string, forceReload = false): void => {
      const video = videoRef.current;
      const media = streamStore.currentMedia;
      if (!video || !media || media.type !== 'video') return;
      if (!canPlayReachedRef.current && !hasEverPlayedRef.current) return;
      // Seek em voo NÃO é decoder travado: recarregar o src aqui jogava o vídeo pro preto
      // e reiniciava o buffer justamente no meio da correção de sync.
      if (video.seeking) return;

      const ct = video.currentTime;
      otaLog.warn('Stream decoder recovery', {
        reason,
        name: media.name?.slice(0, 40),
        contentId: media.id?.slice(0, 8),
        currentTime: ct.toFixed(2),
        paused: video.paused,
        readyState: video.readyState,
        forceReload,
      });

      if (forceReload || isAndroidWebView(platform.platform)) {
        const src = video.currentSrc || video.src || playableUrl || '';
        if (!src) return;
        applyMediaCrossOrigin(video, playbackSource);
        video.pause();
        video.src = src;
        video.load();
        if (ct > 0) {
          video.currentTime = ct;
        }
      } else {
        const dur = video.duration;
        const bump =
          dur && Number.isFinite(dur)
            ? Math.min(ct + 0.25, dur - 0.5)
            : ct + 0.25;
        video.currentTime = bump;
      }

      void video.play().catch((err) => {
        console.warn('[Stream] decoder recovery play() failed:', err);
      });
    },
    [streamStore, platform.platform, playableUrl, playbackSource],
  );

  // Lógica do buffering watchdog — refs/constants declarados no topo do
  // componente (perto dos useEffects de cleanup). Aqui só os handlers.
  const handleWaiting = useCallback((): void => {
    streamStore.setPlaying(false);
    // Tab A7 (2026-06-23): após ~7s o WebView faz relayout (keepScreenOn) e
    // libera o OMX decoder — vídeo congela no frame sem recovery automático.
    if (
      isAndroidWebView(platform.platform) &&
      (hasEverPlayedRef.current || canPlayReachedRef.current) &&
      !waitingRecoveryTimerRef.current
    ) {
      waitingRecoveryTimerRef.current = setTimeout(() => {
        waitingRecoveryTimerRef.current = null;
        const video = videoRef.current;
        if (!video || video.ended || video.seeking) return;
        const stall = playbackStallRef.current;
        const media = streamStore.currentMedia;
        if (
          media &&
          stall.mediaId === media.id &&
          Math.abs(video.currentTime - stall.lastTime) > 0.2 &&
          !video.paused
        ) {
          return;
        }
        attemptDecoderRecovery('waiting-android', true);
      }, ANDROID_SURFACE_RECOVERY_MS);
    }
    // CRÍTICO (Renato 2026-06-13): só agenda watchdog se o item AINDA NÃO
    // tocou nenhuma vez. Re-buffer no meio do vídeo (rede ruim em loja
    // online, congestão no streaming) NÃO deve pular — operador prometeu
    // N exibições e o vídeo precisa terminar mesmo com gagueira. Sem este
    // guard, watchdog dispararia se o re-buffer durasse >30s.
    if (hasEverPlayedRef.current) {
      return; // já tocou pelo menos 1x → re-buffer é problema de rede,
      //          deixa o <video> resolver sozinho (não pula playlist)
    }
    // Agenda watchdog se ainda não tiver um agendado
    if (!bufferingWatchdogRef.current) {
      bufferingWatchdogRef.current = setTimeout(() => {
        bufferingWatchdogRef.current = null;
        const video = videoRef.current;
        // Confirma que ainda está travado (não chegou a tocar nesse intervalo)
        // E que ainda não tocou (defesa em profundidade — pode ter mudado state)
        if (
          video &&
          video.paused &&
          video.readyState < 3 &&
          !hasEverPlayedRef.current
        ) {
          console.warn(
            `[Stream] ⏱️ Buffering watchdog (${BUFFERING_WATCHDOG_MS}ms) — vídeo travado na carga inicial, avançando playlist`,
            {
              src: video.currentSrc,
              readyState: video.readyState,
              networkState: video.networkState,
              contentId: streamStore.currentMedia?.id,
            },
          );
          // 2026-06-13 — Log nativo pra diagnosticar boot offline
          otaLog.warn('Stream buffering watchdog DISPAROU', {
            src: (video.currentSrc ?? '?').slice(0, 120),
            readyState: video.readyState,
            networkState: video.networkState,
            contentId: streamStore.currentMedia?.id?.slice(0, 8) ?? '?',
          });
          streamStore.handleMediaFailure(
            'buffering-watchdog',
            streamStore.playlistVersion,
          );
        }
      }, BUFFERING_WATCHDOG_MS);
    }
  }, [streamStore, platform.platform, attemptDecoderRecovery]);

  const handleStalled = useCallback((): void => {
    if (!canPlayReachedRef.current && !hasEverPlayedRef.current) return;
    if (platform.platform !== 'android') return;
    attemptDecoderRecovery('stalled-android', true);
  }, [platform.platform, attemptDecoderRecovery]);

  const handlePlaying = useCallback((): void => {
    clearBufferingWatchdog();
    clearAdvanceWatchdog(); // FIX B — mídia ficou tocável → cancela o watchdog.
    if (waitingRecoveryTimerRef.current) {
      clearTimeout(waitingRecoveryTimerRef.current);
      waitingRecoveryTimerRef.current = null;
    }
    hasEverPlayedRef.current = true; // marca que o item conseguiu tocar
    deferCacheUpgradeRef.current = true; // não hot-swap remote→cache mid-playback
    streamStore.setPlaying(true);
    streamStore.notePlaybackStarted();
    // BLK: o playback começou → consome a flag do recycle-boot. Sem isso, um boot seguinte que
    // NÃO foi pedido por nós herdaria a janela de preto.
    finishRecycleBoot();
    // SEAMLESS→VÍDEO (2026-07-16) — vídeo novo começou a tocar, mas 'playing'
    // dispara ANTES do 1º frame ser COMPOSTO → esconder o freeze aqui deixava ~1
    // frame preto na borda "entra o vídeo real". Agora agenda o hide pra quando o
    // frame for REALMENTE apresentado (requestVideoFrameCallback). Fallbacks
    // seguros embutidos: sem rVFC → esconde já (comportamento antigo do 'playing');
    // timeout de segurança → nunca prende o freeze. HUD.end é chamado no hide.
    scheduleFreezeHideOnFramePresented();
  }, [
    streamStore,
    clearBufferingWatchdog,
    clearAdvanceWatchdog,
    scheduleFreezeHideOnFramePresented,
  ]);

  /**
   * Seek TERMINOU: o cooldown do pulso e a histerese contam DAQUI, não do pedido — e a
   * latência medida (pedido → `seeked`) passa a compensar o alvo do próximo seek.
   */
  const handleSeeked = useCallback((): void => {
    const now = Date.now();
    lastSeekedAtRef.current = now;
    lastPulseActionRef.current = now;
    if (seekIssuedAtRef.current > 0) {
      const latency = Math.max(0, now - seekIssuedAtRef.current);
      seekIssuedAtRef.current = 0;
      // Alisado: um seek lento isolado (cache frio) não vira compensação permanente.
      seekLatencyMsRef.current =
        seekLatencyMsRef.current > 0
          ? seekLatencyMsRef.current * 0.7 + latency * 0.3
          : latency;
    }
  }, []);

  const handleTimeUpdate = useCallback((): void => {
    const video = videoRef.current;
    if (!video) return;

    // FIX B — currentTime avançou → conteúdo válido tocando; cancela o watchdog
    // de avanço (nunca pular conteúdo lento-mas-válido).
    if (video.currentTime > 0) clearAdvanceWatchdog();

    // COVER NA 1ª EXIBIÇÃO (vide FIRST_PLAY_FRAME_CAPTURE_LEAD_S). Só Android/browser/
    // desktop (readback permitido), só enquanto NÃO há frame definitivo, 1× por exibição.
    {
      const media = streamStore.currentMedia;
      const ctl = videoLoopControlRef.current;
      const fim =
        ctl.shouldCutShort && ctl.scheduledDuration > 0
          ? ctl.scheduledDuration
          : video.duration && Number.isFinite(video.duration)
            ? video.duration
            : 0;
      if (
        media &&
        media.type === 'video' &&
        fim > 0 &&
        firstPlayCaptureDoneRef.current !== media.id &&
        !lastFrameCache.hasStored(media.id) &&
        isVideoReadbackAllowedForPlatform(platform.platform) &&
        video.currentTime >= fim - FIRST_PLAY_FRAME_CAPTURE_LEAD_S
      ) {
        firstPlayCaptureDoneRef.current = media.id;
        const capId = media.id;
        void captureVideoFrameJpeg(video).then((jpeg) => {
          if (!jpeg) return;
          lastFrameCache.storeProvisional(capId, jpeg);
          lastFrameCache.prepare(capId); // decodifica já: a troca é daqui a ~0,4s
        });
      }
    }

    // LAST-FRAME (Renato 2026-07-16): a captura do último frame foi movida pro
    // handleVideoEnded — captura 1× no fim REAL (fim natural + cut-short) e REUSA
    // (o frame de um vídeo é invariável). Removidos daqui: a captura 1× por limiar
    // (pegava um frame ANTES do fim → "rewind") e a captura CONTÍNUA via rVFC
    // (recapturava todo frame na janela final → CPU à toa). Vide handleVideoEnded.

    const {
      shouldManualLoop,
      shouldCutShort,
      scheduledDuration,
      actualDuration,
      playbackStartTime,
    } = videoLoopControlRef.current;

    // EARLY FREEZE (Renato 2026-07-16): zera a piscada preta entre o fim do vídeo e a
    // entrada do freeze. Em Tizen/webOS o <video> é HW overlay: ao terminar, o overlay
    // é DERRUBADO antes do freeze (plano gráfico) ficar visível → preto vaza no gap.
    // Mostrar o freeze ~earlyFreezeLeadS ANTES do fim pinta o canvas POR BAIXO do
    // overlay (o usuário continua vendo o vídeo, que está por cima no plano de HW);
    // quando o overlay some, revela o freeze SEM preto. NÃO toca no <video> (≠ 35e5232
    // revertido, que escondia o vídeo síncrono e PIOROU o tear-down). 1× por exibição,
    // só quando VAI avançar (não loop/single) e o lastFrameWc já está cacheado.
    if (
      isTizenLike &&
      earlyFreezeLeadS > 0 &&
      !earlyFreezeDoneRef.current &&
      endProbeRafRef.current == null
    ) {
      const media = streamStore.currentMedia;
      const end =
        shouldCutShort && scheduledDuration > 0
          ? scheduledDuration
          : video.duration && isFinite(video.duration)
            ? video.duration
            : 0;
      const willAdvance = !shouldManualLoop && streamStore.playlist.length > 1;
      // Ao entrar na janela final (~0.5s), ARMA o probe rAF (disparo fino). O
      // timeupdate (~234ms) só serve pra armar; quem dispara no lead exato é o rAF.
      // UNIFICAÇÃO (Renato 2026-07-20): o poster WebCodecs (lastFrameWc) é reservado
      // pro `video→VIDEO`. Pro `video→img`, TODA a família Tizen usa o HOLD-FRAME
      // (estende o último frame do <video> vivo — bloco abaixo), inclusive consumer.
      // → não arma o freeze-canvas quando o próximo é imagem (evita competir com o hold).
      if (
        media &&
        media.type === 'video' &&
        streamStore.nextMedia?.type !== 'image' &&
        willAdvance &&
        end > 0 &&
        video.currentTime >= end - 0.5 &&
        lastFrameWcCache.has(media.id)
      ) {
        startEndFreezeProbe(end, media.id);
      }
    }

    // PHASE 1.1 — EARLY HOLD (video→img, TODA a família Tizen/webOS — SSSP + Consumer,
    // decisão de unificação Renato 2026-07-20). Pausar o <video> só no `ended` é tarde
    // (o overlay de HW já apagou → flash preto residual, intermitente). Aqui,
    // ~earlyHoldLeadS ANTES do fim, chamamos o handleVideoEnded (que pausa o vídeo +
    // liga o hold + avança pra imagem): o frame MID-STREAM fica segurado pelo overlay e
    // o vídeo NUNCA chega no `ended`. O timeupdate (~234ms) é fino o bastante pro lead
    // de 400ms. Dispara 1× por exibição (earlyHoldDoneRef). Consumer TAMBÉM segura o
    // frame (não usa mais o poster WebCodecs pro video→img — só pro video→video).
    // Android/browser nem são tizen/webos → NÃO entram (gate `shouldHoldVideoFrameForImage`).
    if (
      !earlyHoldDoneRef.current &&
      shouldHoldVideoFrameForImage({
        platform: platform.platform,
        currentType: streamStore.currentMedia?.type,
        nextType: streamStore.nextMedia?.type,
      })
    ) {
      const holdEnd =
        shouldCutShort && scheduledDuration > 0
          ? scheduledDuration
          : video.duration && isFinite(video.duration)
            ? video.duration
            : 0;
      const willAdvance = !shouldManualLoop && streamStore.playlist.length > 1;
      // Cap do lead a 15% da duração: garante que o pause caia sempre ≥85% do
      // vídeo. Assim o handleVideoEnded NUNCA interpreta como "fim prematuro"
      // (< 80% → tentaria retomar em vez de avançar) em vídeos curtos, e o bypass
      // de cut-short (currentTime ≥ scheduledDuration − 0.5) sempre casa (lead ≤ 0.4).
      const effHoldLead = Math.min(earlyHoldLeadS, holdEnd * 0.15);
      if (
        willAdvance &&
        holdEnd > 0 &&
        video.currentTime >= holdEnd - effHoldLead
      ) {
        earlyHoldDoneRef.current = true;
        handleVideoEnded(); // pausa (segura frame mid-stream) + hold + avança
        return;
      }
    }

    // Case 1: Cut video short (duration_seconds < video length)
    if (
      shouldCutShort &&
      streamStore.currentMedia?.hasDurationOverride === true &&
      scheduledDuration > 0
    ) {
      if (video.currentTime >= scheduledDuration) {
        // BUGFIX (Renato 2026-06-24) — playlist de item ÚNICO.
        // handleVideoEnded → handleMediaEnd → playNext faz
        // currentIndex = (0+1)%1 = 0, então currentMedia NÃO muda → os
        // useEffects que recarregam o <video> (keyed em currentMedia) não
        // re-disparam → o vídeo IGNORA o corte e roda até o fim natural
        // (ex: 30s com override de 15s), e handleTimeUpdate re-chama
        // handleVideoEnded a cada frame após o corte → Canvas Bridge pisca
        // ("saltos de zoom"). Como não há "próxima" mídia distinta pra onde
        // avançar, o correto é reiniciar o vídeo NO LUGAR (mesmo padrão do
        // manual loop, abaixo) respeitando o corte. Seek in-place → SEM
        // remount → não toca no race do decoder OMX (v1.0.162).
        if (streamStore.playlist.length <= 1) {
          console.debug(
            '[Stream] Cut short (item único): reiniciando vídeo no lugar na borda do corte',
          );
          // Re-arma o fallback wall-clock pra este novo ciclo de 0→corte.
          videoLoopControlRef.current.playbackStartTime = Date.now();
          cutShortStuckRef.current = { mediaId: null, triggered: false };
          video.currentTime = 0;
          video.play().catch((err) => {
            console.error(
              '[Stream] Erro ao reiniciar vídeo cut-short de item único:',
              err,
            );
          });
          return;
        }
        console.debug(
          '[Stream] Cut short: scheduled duration reached, advancing to next media',
        );
        handleVideoEnded();
        return;
      }

      // Wall-clock fallback — Tizen 9.0 pode congelar currentTime/timeupdate
      // no último frame (Prada 15s: admin mostra 15/15, tela preta, sem ended).
      // Só dispara após scheduledDuration + 1.5s (mesma tolerância do Case 3).
      if (
        playbackStartTime > 0 &&
        videoPlayedEnoughForCutShortAdvance(video, scheduledDuration)
      ) {
        const wallElapsed = (Date.now() - playbackStartTime) / 1000;
        if (wallElapsed >= scheduledDuration + 1.5) {
          const media = streamStore.currentMedia;
          const tracker = cutShortStuckRef.current;
          if (media && tracker.mediaId !== media.id) {
            tracker.mediaId = media.id;
            tracker.triggered = false;
          }
          if (!tracker.triggered) {
            tracker.triggered = true;
            console.warn(
              '[Stream] 🚨 Cut-short wall-clock polyfill — timeupdate/ended stalled',
              {
                mediaName: media?.name,
                scheduledDuration,
                wallElapsedSec: wallElapsed.toFixed(2),
                currentTime: video.currentTime.toFixed(3),
                paused: video.paused,
                ended: video.ended,
                readyState: video.readyState,
              },
            );
            // Item único: avançar é no-op (currentMedia não muda) e deixaria
            // o vídeo congelado. Reinicia no lugar (mesma lógica do Case 1).
            if (streamStore.playlist.length <= 1) {
              videoLoopControlRef.current.playbackStartTime = Date.now();
              cutShortStuckRef.current = { mediaId: null, triggered: false };
              video.currentTime = 0;
              video.play().catch((err) => {
                console.error(
                  '[Stream] Erro ao reiniciar vídeo cut-short de item único (polyfill):',
                  err,
                );
              });
              return;
            }
            handleVideoEnded();
            return;
          }
        }
      }
    }

    // Case 2: Manual loop (duration_seconds >= 2x video length)
    if (
      shouldManualLoop &&
      streamStore.currentMedia?.hasDurationOverride === true
    ) {
      // Check if we've reached the scheduled duration
      const elapsedTime = (Date.now() - playbackStartTime) / 1000;

      if (elapsedTime >= scheduledDuration) {
        // Scheduled duration reached - advance to next media
        console.log(
          '[Stream] Manual loop: scheduled duration reached, advancing to next media',
        );
        handleVideoEnded();
        return;
      }

      // Check if video reached its end (need to loop)
      if (video.currentTime >= actualDuration - 0.1) {
        console.log('[Stream] Manual loop: restarting video at 0');
        video.currentTime = 0;
        video.play().catch((err) => {
          console.error('[Stream] Error replaying video:', err);
        });
      }
    }

    // Case 4 — decoder stall mid-playback (Tab A7 / HEVC). currentTime
    // congela sem paused/ended — distinto de onEnded prematuro (~13s).
    // ⚠️ Embedded: quando o host escondeu a View (isHostHidden), o currentTime
    // congelado é INTENCIONAL (freeze D2) — pular o stall-recovery pra ele não
    // ficar dando seek/reload e causar o vídeo "de soco". Default false → frota intacta.
    if (
      shouldRunStallRecovery({
        hostHidden: streamStore.isHostHidden,
        cutShort: shouldCutShort,
        manualLoop: shouldManualLoop,
        hasPlayedOrCanPlay:
          hasEverPlayedRef.current || canPlayReachedRef.current,
        ended: video.ended,
        // Vídeo pausado NÃO é stall de decoder: pode ser o Chrome pausando mídia muda
        // de aba em background (power-save) → rodar recovery vira loop de AbortError.
        paused: video.paused,
      }) &&
      streamStore.currentMedia
    ) {
      const media = streamStore.currentMedia;
      const trustedDur = resolveTrustedNaturalDuration(
        media.duration ?? 0,
        video.duration,
      );
      const ct = video.currentTime;
      const stillPlaying = trustedDur > 0 && ct < trustedDur * 0.92;
      const stall = playbackStallRef.current;

      if (stall.mediaId !== media.id) {
        stall.mediaId = media.id;
        stall.lastTime = ct;
        stall.sinceMs = 0;
        stall.recovery = 'none';
      } else if (stillPlaying) {
        if (Math.abs(ct - stall.lastTime) < 0.08) {
          if (stall.sinceMs === 0) {
            stall.sinceMs = Date.now();
          }
          const stalledMs = Date.now() - stall.sinceMs;
          if (stall.recovery === 'none' && stalledMs > 1500) {
            if (isAndroidWebView(platform.platform)) {
              stall.recovery = 'reload';
              attemptDecoderRecovery('stall-android', true);
            } else {
              stall.recovery = 'seek';
              const bump = Math.min(ct + 0.25, trustedDur - 0.5);
              otaLog.warn('Stream playback stall — seek+play recovery', {
                name: media.name?.slice(0, 40),
                contentId: media.id?.slice(0, 8),
                currentTime: ct.toFixed(2),
                trustedDuration: trustedDur.toFixed(2),
                stalledMs,
                source: playbackSource,
              });
              video.currentTime = bump;
              void video.play().catch((err) => {
                console.warn('[Stream] Stall recovery play() failed:', err);
              });
            }
          } else if (stall.recovery === 'seek' && stalledMs > 6000) {
            stall.recovery = 'reload';
            const src = video.currentSrc || video.src;
            otaLog.warn('Stream playback stall — video.load() recovery', {
              name: media.name?.slice(0, 40),
              contentId: media.id?.slice(0, 8),
              currentTime: ct.toFixed(2),
              stalledMs,
              srcSuffix: src.slice(-40),
            });
            video.load();
            video.currentTime = ct;
            void video.play().catch((err) => {
              console.warn('[Stream] Stall reload play() failed:', err);
            });
          }
        } else {
          stall.lastTime = ct;
          stall.sinceMs = 0;
          stall.recovery = 'none';
        }
      }
    }

    // Case 3 — polyfill pro evento `ended` silenciado pelo WebKit Tizen 9.0.
    // Bug Renato 2026-06-06: "Pepsi Best Moment" 30s travou em currentTime
    // = 30 / duration = 30 sem disparar onEnded → playlist nunca avançou.
    //
    // Diferente de Case 1/2 (que dependem de duration override), aqui
    // tratamos o vídeo natural (sem override) que confia 100% no evento
    // `ended` do <video>. Quando WebKit silencia esse evento (bug
    // específico Tizen com vídeos terminando em currentTime exato), nada
    // detecta — handleVideoEnded nunca é chamado.
    //
    // NÃO é watchdog genérico: só ativa quando há evidência específica
    // (chegou ao fim + não pausado + ended não foi disparado por >1.5s).
    // Vídeo travado no MEIO continua mostrando o sintoma — não mascarado.
    //
    // Telemetria embutida (console.warn com estado completo do <video>)
    // permite quantificar a ocorrência em prod e reportar pra Samsung.
    if (!shouldCutShort && !shouldManualLoop && streamStore.currentMedia) {
      const apiDuration = streamStore.currentMedia.duration ?? 0;
      const metaDuration = resolveTrustedNaturalDuration(
        apiDuration,
        video.duration,
      );
      if (!metaDuration || metaDuration === Infinity) {
        // skip case 3
      } else {
        const remaining = metaDuration - video.currentTime;
        const reachedEnd = remaining <= 0.1;
        const eventSilenced = !video.paused && !video.ended;

        if (reachedEnd && eventSilenced) {
          const stuck = endStuckRef.current;
          const now = Date.now();

          if (stuck.videoSrc !== video.src) {
            // Primeira vez detectando "no fim" pra este src — começa contagem.
            stuck.videoSrc = video.src;
            stuck.firstSeenAt = now;
          } else if (now - stuck.firstSeenAt > 1500) {
            // Vídeo no fim há >1.5s sem disparar `ended` — WebKit silenciou.
            // Simula o evento que o browser deveria ter disparado.
            console.warn(
              '[Stream] 🚨 onEnded silenciado pelo WebKit — polyfill advance',
              {
                currentTime: video.currentTime.toFixed(3),
                duration: video.duration.toFixed(3),
                paused: video.paused,
                ended: video.ended,
                readyState: video.readyState,
                networkState: video.networkState,
                stuckForMs: now - stuck.firstSeenAt,
                videoSrcSuffix: video.src.slice(-40),
              },
            );
            // Reseta tracker pra próxima mídia + simula o evento.
            stuck.videoSrc = null;
            stuck.firstSeenAt = 0;
            handleVideoEnded();
          }
        } else if (endStuckRef.current.videoSrc === video.src) {
          // Saiu do estado "no fim" antes do timeout (ex: vídeo voltou a
          // tocar por algum motivo) — reseta tracker pra evitar trigger
          // falso depois.
          endStuckRef.current.videoSrc = null;
          endStuckRef.current.firstSeenAt = 0;
        }
      }
    }
  }, [
    handleVideoEnded,
    streamStore,
    playbackSource,
    platform.platform,
    attemptDecoderRecovery,
    isTizenLike,
    earlyFreezeLeadS,
    earlyHoldLeadS,
    startEndFreezeProbe,
    clearAdvanceWatchdog,
  ]);

  // Cut-short: interval independente do timeupdate — se o WebKit parar de
  // disparar timeupdate por completo, handleTimeUpdate acima nunca roda.
  useEffect(() => {
    if (streamStore.currentMedia?.type !== 'video') return;

    const tick = () => {
      const video = videoRef.current;
      if (!video) return;

      const { shouldCutShort, scheduledDuration, playbackStartTime } =
        videoLoopControlRef.current;
      if (!shouldCutShort || scheduledDuration <= 0 || playbackStartTime <= 0) {
        return;
      }

      if (!videoPlayedEnoughForCutShortAdvance(video, scheduledDuration)) {
        return;
      }

      const wallElapsed = (Date.now() - playbackStartTime) / 1000;
      if (wallElapsed < scheduledDuration + 1.5) return;

      const media = streamStore.currentMedia;
      const tracker = cutShortStuckRef.current;
      if (media && tracker.mediaId !== media.id) {
        tracker.mediaId = media.id;
        tracker.triggered = false;
      }
      if (tracker.triggered) return;

      tracker.triggered = true;
      console.warn(
        '[Stream] 🚨 Cut-short interval polyfill — timeupdate parou de disparar',
        {
          mediaName: media?.name,
          scheduledDuration,
          wallElapsedSec: wallElapsed.toFixed(2),
          currentTime: video.currentTime.toFixed(3),
          paused: video.paused,
          ended: video.ended,
        },
      );
      handleVideoEnded();
    };

    const interval = window.setInterval(tick, 5000);
    return () => clearInterval(interval);
  }, [streamStore.currentMedia?.id, handleVideoEnded]);

  // Poll 1s — Case 4 stall recovery quando WebView para de emitir timeupdate.
  useEffect(() => {
    if (streamStore.currentMedia?.type !== 'video') return;
    const id = window.setInterval(() => {
      handleTimeUpdate();
    }, 1000);
    return () => clearInterval(id);
  }, [streamStore.currentMedia?.id, handleTimeUpdate]);

  // Reseta debounce cut-short ao trocar de mídia.
  useEffect(() => {
    cutShortStuckRef.current = { mediaId: null, triggered: false };
  }, [streamStore.currentMedia?.id]);

  // TV Remote handlers
  useTVRemote({
    onEnter: () => {
      if (showMediaInfo && focusedButton) {
        // v1.0.108 — Reativar Kiosk também é alvo de Enter
        if (focusedButton === 'reactivate-kiosk') {
          kioskStore.lock();
          setShowMediaInfo(false);
          return;
        }
        // landscape/portrait → handler de orientação
        handleOrientationChange(focusedButton);
      } else {
        // Default behavior: Toggle mute com Enter
        streamStore.toggleMute();
        // Mostra indicador temporariamente
        if (muteTimeoutRef.current) {
          clearTimeout(muteTimeoutRef.current);
        }
        muteTimeoutRef.current = setTimeout(() => {
          // Força re-render para esconder indicador
        }, 2000);
      }
    },
    onRight: () => {
      if (showMediaInfo) {
        // v1.0.108 — Navegação 3-way: Paisagem → Retrato → Reativar Kiosk
        // (este último só visível quando !isLocked). Antes ficava preso
        // em Paisagem↔Retrato.
        if (focusedButton === 'landscape') {
          setFocusedButton('portrait');
          portraitButtonRef.current?.focus();
        } else if (focusedButton === 'portrait' && !kioskStore.isLocked) {
          setFocusedButton('reactivate-kiosk');
          reactivateKioskButtonRef.current?.focus();
        } else {
          // Default: começa em portrait (entrada inicial no painel)
          setFocusedButton('portrait');
          portraitButtonRef.current?.focus();
        }
      } else {
        // Default behavior: Próximo vídeo
        streamStore.playNext();
      }
    },
    onLeft: () => {
      if (showMediaInfo) {
        // v1.0.108 — Navegação 3-way inverso: Reativar Kiosk → Retrato → Paisagem
        if (focusedButton === 'reactivate-kiosk') {
          setFocusedButton('portrait');
          portraitButtonRef.current?.focus();
        } else if (focusedButton === 'portrait') {
          setFocusedButton('landscape');
          landscapeButtonRef.current?.focus();
        } else {
          // Default: começa em landscape
          setFocusedButton('landscape');
          landscapeButtonRef.current?.focus();
        }
      } else {
        // Default behavior: Vídeo anterior
        streamStore.playPrevious();
      }
    },
    onDown: () => {
      // v1.0.218 (Renato 2026-07-17): gate por kiosk. Painel de debug/settings
      // do player NÃO pode abrir com kiosk ativo — cliente em loja não deveria
      // conseguir acessar via controle remoto. Sintoma reportado: TV Tizen
      // SSSP com kiosk habilitado, ao apertar seta pra baixo aparecia o
      // "painelzinho de controle" (mesmo o kiosk mantendo isLocked=true no
      // resto do sistema).
      if (kioskStore.isLocked) return;
      // Toggle debug media info overlay (simple press, no need to hold)
      setShowMediaInfo((prev) => !prev);
      if (mediaInfoTimeoutRef.current) {
        clearTimeout(mediaInfoTimeoutRef.current);
      }
    },
    onUp: () => {
      // v1.0.50 — Atalho pro botão "Limpar" da caixa de crashes que não é
      // alcançável pelo controle remoto (overlay não tem foco navegável
      // arrow-up/down em TV consumer).
      //
      // v1.0.51 — Renato reportou que apertou ↑ e crashes não limparam
      // (print mostrou 11 crashes após). Causa: closure stale —
      // `showMediaInfo` e `cacheInfo.crashCount` capturados no momento do
      // render, e useTVRemote re-attach pode não pegar o estado mais novo
      // em tempo. Fix: usar refs (showMediaInfoRef) e sempre limpar
      // (idempotente) — se não tem crash, clearCrashes() é no-op de
      // localStorage. Só checa overlay visibility (não checa count).
      if (!showMediaInfoRef.current) {
        return; // overlay fechado — não vai mexer em nada
      }
      console.log('[Stream] ↑ pressed with overlay visible — clearing crashes');
      clearCrashes();
      // Força refresh imediato do cacheInfo pra UI atualizar — o
      // interval normal de 1s parece atraso pro usuário.
      setCacheInfo((prev) => ({
        ...prev,
        crashCount: 0,
        lastCrashMessage: null,
        lastCrashAt: null,
        lastCrashStack: null,
      }));
    },
    onInfo: () => {
      // v1.0.218 (Renato 2026-07-17): mesmo gate do onDown — painel só abre
      // fora de kiosk. Se kiosk locked e técnico precisa do painel, primeiro
      // desbloqueia (7-clicks + PIN opcional), depois INFO abre.
      if (kioskStore.isLocked) return;
      // Samsung/LG "i" (INFO) button on the remote — dedicated debug toggle.
      // Same effect as onDown but discoverable: technicians on-site can press the
      // labeled INFO button to inspect cache state, sync time, version, etc.
      setShowMediaInfo((prev) => !prev);
      if (mediaInfoTimeoutRef.current) {
        clearTimeout(mediaInfoTimeoutRef.current);
      }
    },
    onReturn: () => {
      // Não faz nada - player fullscreen não tem "voltar"
    },
  });

  // CP3.5 (2026-05-16) — Estatísticas do padrão do loop pro painel de debug.
  // Detecta o menor padrão repetitivo na playlist atual e calcula contagens
  // por campanha (1/loop · N/h). Reflete o modelo "1 padrão repetido N×"
  // do refactor de Loop de Reprodução (vide LOOP_REPRODUCAO_PLAYER.md).
  //
  // ⚠️ DEVE estar ANTES dos early returns (loading/no-content) pra não
  // violar Rules of Hooks — hooks têm que ser chamados na mesma ordem
  // em todo render.
  const loopStats = useMemo(() => {
    const items = streamStore.playlist.map((m) => ({
      id: m.id,
      duration: m.duration ?? 10,
      campanhaId: m.campanhaId,
      campanhaName: m.campanhaId
        ? schedulerStore.campaigns.find((c) => Number(c.id) === m.campanhaId)
            ?.name
        : undefined,
    }));
    const pattern = detectLoopPattern(items);
    const position = computeLoopPosition(streamStore.currentIndex, pattern);
    const campaigns = computeCampaignStats(items, pattern);
    return { pattern, position, campaigns };
  }, [
    streamStore.playlist,
    streamStore.currentIndex,
    schedulerStore.campaigns,
  ]);

  // V059 — Items que vieram no último sync mas estão filtrados POR JANELA
  // PRÓPRIA DO CONTEÚDO (scheduleMode/weeklySchedule/daysOfWeek/startAt/endAt).
  // Composição AND no player: estes itens não tocam, mas a associação no DB
  // existe — útil pro operador entender "cadê meu vídeo?" abrindo o painel
  // e vendo a lista em cinza com motivo.
  //
  // Items filtrados por campanha/playlist (datas, status) NÃO aparecem aqui
  // — esses já são responsabilidade de outras camadas.
  const contentWindowFilteredOut = useMemo(() => {
    const now = new Date();
    const seen = new Set<string>();
    const blocked: Array<{
      id: string;
      name: string;
      reason: string;
      blockedBy: 'content' | 'group';
    }> = [];
    for (const campaign of schedulerStore.campaigns) {
      for (const item of campaign.playlist) {
        if (seen.has(item.id)) continue;
        seen.add(item.id);

        // V060 — verifica primeiro janela do Grupo (4ª camada AND).
        // viaDireto=true bypassa; sem viaGrupos não filtra; com viaGrupos
        // faz UNIÃO. Diagnóstico antes do filtro de Conteúdo pra que a
        // razão "raiz" apareça quando ambas bloqueariam.
        const groupDecision = isGroupSchedulePlayable(
          item,
          schedulerStore.playerGroups,
          now,
        );
        if (!groupDecision.isPlayable) {
          blocked.push({
            id: item.id,
            name: item.name,
            reason: groupDecision.reason || 'fora da janela do grupo',
            blockedBy: 'group',
          });
          continue;
        }

        // Só interessa itens COM schedule configurado (scheduleMode='ADVANCED'
        // ou weeklySchedule presente). Itens legacy (sem schedule) NÃO entram
        // nessa lista — eles tocam sempre conforme back-compat absoluta.
        const hasOwnSchedule =
          item.scheduleMode === 'ADVANCED' ||
          (item.weeklySchedule && Object.keys(item.weeklySchedule).length > 0);
        if (!hasOwnSchedule) continue;
        const decision = isContentPlayable(item, now);
        if (!decision.isPlayable) {
          blocked.push({
            id: item.id,
            name: item.name,
            reason: decision.reason || 'fora da janela',
            blockedBy: 'content',
          });
        }
      }
    }
    return blocked;
  }, [schedulerStore.campaigns, schedulerStore.playerGroups]);

  // Formata segundos como "M:SS" pra exibir duração do padrão no painel.
  // Helper puro (não-hook) — pode ficar antes ou depois de returns sem afetar.
  const formatLoopTime = (s: number): string => {
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  // CP10 — Resolve contexto do slot atual pra eventos slot_started/completed.
  // Retorna null se player está em Tier 4 (legado, sem slot_sequence) — caso
  // em que só trackPlayback* roda. Helper puro, fire-and-forget no caller.
  const getSlotContextForAnalytics = (): {
    slotIndex: number;
    scheduledOffsetS: number;
    playlistId?: number;
    programacaoId?: number;
    tier: number;
    contentId: string;
    contentName?: string;
  } | null => {
    const tier = playbackStore.currentTier;
    if (tier === 4) return null;
    const seq = playbackStore.currentSlotSequence;
    if (!seq) return null;
    const idx = streamStore.currentIndex;
    const slot = seq.slots[idx];
    if (!slot) return null;
    return {
      slotIndex: idx,
      scheduledOffsetS: slot.scheduledOffsetS,
      playlistId: seq.playlistId,
      programacaoId: slot.programacaoId,
      tier,
      contentId: slot.contentId,
      contentName: slot.contentName,
    };
  };

  // Loading state - waiting for first sync.
  //
  // 2026-06-13 (Renato) — CRÍTICO: se já temos cache disponível (hasContent
  // true via initializeFromCache no boot), NÃO mostrar "Sincronizando com
  // servidor" enquanto sync inicial roda. Antes deste guard:
  //
  //   Boot offline → cache-first popula playlist → syncStore.hasContent=true
  //                → sync() roda em paralelo → status='syncing', syncCount=0
  //                → Stream renderizava "Sincronizando..." NUNCA chegando no
  //                  <video> apesar da playlist estar carregada e pronta!
  //   Sync demora 30s pra timeout sem rede → tela preta era essa janela inteira.
  //
  // Agora: hasContent já presente = render Stream direto + sync em background.
  // Online normal não é afetado: sync inicial é <1s, cache vazio no boot, então
  // hasContent=false até sync completar.
  if (
    !syncStore.hasContent &&
    (syncStore.status === 'idle' ||
      (syncStore.status === 'syncing' && syncStore.syncCount === 0))
  ) {
    // Helper function to get API URL for troubleshooting.
    // Mesma fonte que o sync usa (inclui a URL que o host passou no embedded).
    const getApiUrl = (): string => resolveApiBaseUrl();

    // BLK: recycle-boot → preto puro, sem spinner/texto/URL de servidor.
    if (isRecycleBoot) return <Container />;
    return (
      <Container>
        <LoadingOverlay>
          <LoadingSpinner />
          <LoadingText>Sincronizando com servidor...</LoadingText>
          <ServerUrlInfo>Server: {getApiUrl()}</ServerUrlInfo>
        </LoadingOverlay>
      </Container>
    );
  }

  // Boot grace (Renato 2026-07-05) — evita o FLASH de "Aguardando conteúdo" (~2-3s)
  // na janela entre "1º sync OK" e "currentMedia setado" (scheduler/cache
  // settling). Mostra "Carregando conteúdo..." por até BOOT_GRACE_MS; só cai pro
  // NoContent na hora se for DEFINITIVO (player bloqueado, auth/registro pendente,
  // ou servidor confirmou biblioteca VAZIA = admin realmente sem nada). Preserva
  // o NoContent legítimo (após o grace, ex.: tudo filtrado por horário/dia).
  // Decisão pura + testada em playbackHelpers (shouldDeferNoContentForBoot).
  const BOOT_GRACE_MS = 5000;
  const authOrRegistrationPending =
    authStore.registrationStatus === 'registering' ||
    authStore.registrationStatus === 'initializing' ||
    authStore.registrationStatus === 'pending_approval' ||
    syncStore.status === 'auth_error';
  if (
    shouldDeferNoContentForBoot({
      hasCurrentMedia: !!streamStore.currentMedia,
      isPlayerBlocked: syncStore.isPlayerBlocked,
      authOrRegistrationPending,
      serverConfirmedEmpty:
        syncStore.status === 'success' && !syncStore.hasContent,
      withinBootGrace: Date.now() - bootStartRef.current < BOOT_GRACE_MS,
    })
  ) {
    if (!bootGraceLoggedRef.current) {
      bootGraceLoggedRef.current = true;
      otaLog.info('Stream boot-grace: Carregando (adia flash de NoContent)', {
        status: syncStore.status,
        hasContent: syncStore.hasContent,
        syncCount: syncStore.syncCount,
      });
    }
    // BLK: recycle-boot → preto puro. Esta é a tela MEDIDA em ~2,4s no restart real — a
    // principal que o cliente via.
    if (isRecycleBoot) return <Container />;
    return (
      <Container>
        <LoadingOverlay>
          <LoadingSpinner />
          <LoadingText>Carregando conteúdo...</LoadingText>
        </LoadingOverlay>
      </Container>
    );
  }

  // No content state - show NoContent with countdown
  // O bloqueio administrativo tem prioridade: mesmo que tenha cache, o player
  // deve exibir a tela de bloqueado quando syncStore.isPlayerBlocked.
  if (
    syncStore.isPlayerBlocked ||
    !syncStore.hasContent ||
    !streamStore.currentMedia
  ) {
    // Determine the reason for showing NoContent.
    //
    // 2026-05-24 — UX refactor: 3 cenários distintos pra TI em campo.
    //
    //   A) offline_no_internet : sem internet de verdade. Detecção
    //      conservadora: precisa que (i) navigator.onLine reporte false
    //      [hint] E (ii) o erro do sync seja consistente com fail-fast de
    //      rede (Network Error / Failed to fetch). Combinado evita o false
    //      negative que o navigator.onLine sozinho causava (Android WebView
    //      mente — INTERNET+VALIDATED no OS, mas navigator.onLine=false).
    //
    //   B) server_unreachable  : wifi OK, mas server inacessível
    //      (firewall, DNS, server fora do ar, Railway 502...). Mensagem
    //      direciona a TI do cliente pra onde olhar.
    //
    //   C) caminho feliz       : sem NoContent.
    //
    // Para qualquer erro de sync que NÃO bate em (A), assumimos (B) — é
    // honesto com o usuário: o player TENTOU falar com o server e falhou,
    // independentemente de o Wi-Fi local estar OK ou não. Melhor mandar a
    // TI olhar o server do que culpar o Wi-Fi do cliente errado.
    const hasCachedContent = mediaStore.hasCachedContent;
    const errMsg = (syncStore.error ?? '').toLowerCase();
    const looksLikeNetworkLevelFailure =
      errMsg.includes('network error') ||
      errMsg.includes('failed to fetch') ||
      errMsg.includes('err_internet_disconnected');
    const navigatorReportsOffline = !syncStore.isOnline;

    // 2026-06-12 — Sinal AUTORITATIVO vem do ConnectionMonitorService
    // (probes reais em gstatic + server). Quando esse já rodou pelo menos
    // um ciclo (~2s após boot), `syncStore.isInternetReachable` reflete
    // realidade — não a mentira do navigator.onLine no Android WebView.
    //
    // Fallback pra heurística antiga apenas se o monitor ainda não rodou:
    // identificamos isso checando se isInternetReachable=true E
    // navigator.onLine=false (estado inconsistente = monitor não rodou
    // ainda OU acabou de iniciar).
    const monitorSaysNoInternet = !syncStore.isInternetReachable;
    const isLikelyTrulyOffline =
      monitorSaysNoInternet ||
      // Fallback antigo: só usado se monitor não contradisse explicitamente
      (syncStore.isInternetReachable === true &&
        navigatorReportsOffline &&
        looksLikeNetworkLevelFailure);
    const hasSyncError = syncStore.status === 'error' || !!syncStore.error;

    let reason: NoContentReason = 'no_content';
    if (syncStore.isPlayerBlocked) {
      reason = 'player_blocked';
    } else if (
      // 2026-06-07 — Bug reportado por Renato (v1.0.155):
      // Device se cadastrou OK no server → registrationStatus='pending_approval',
      // mas sync continua tentando e toma 401/403 → cai em 'server_unreachable'
      // ("Sem acesso ao servidor Efflux") quando o estado correto é "Aguardando
      // aprovação". Se o registrationStatus está em fluxo de registro/aprovação,
      // ESSE é o status real do device — tem prioridade sobre erros transient
      // de sync.
      authStore.registrationStatus === 'registering' ||
      authStore.registrationStatus === 'initializing' ||
      authStore.registrationStatus === 'pending_approval' ||
      syncStore.status === 'auth_error'
    ) {
      // NoContent distingue via prop `registrationStatus`:
      //   'registering'/'initializing' → "Registrando dispositivo..."
      //   'pending_approval'           → "Aguardando aprovação"
      //   default                      → "Dispositivo não autorizado"
      reason = 'auth_error';
    } else if (isLikelyTrulyOffline) {
      // Cenário A — provavelmente offline real.
      reason = 'offline_no_internet';
    } else if (hasSyncError && !hasCachedContent) {
      // Cenário B sem cache — server inacessível e nada pra mostrar.
      reason = 'server_unreachable';
    } else if (hasSyncError && hasCachedContent) {
      // Cenário B com cache — server inacessível mas reaproveita cache se
      // a playlist permitir. (Quando entra aqui é porque a playlist atual
      // não está utilizável; mantém server_unreachable pro diagnóstico.)
      reason = 'server_unreachable';
    } else if (syncStore.status === 'error') {
      reason = 'sync_error';
    }

    // BLK: suprime só o FLASH de NoContent dentro da janela do recycle-boot. Passado o teto, o
    // NoContent legítimo volta normalmente — é ele que explica em campo por que nada está tocando.
    if (isRecycleBoot) return <Container />;

    // Read cached background based on orientation
    const isLandscape = window.innerWidth > window.innerHeight;
    const bgKey = isLandscape ? 'efflux_bg_landscape' : 'efflux_bg_portrait';
    const cachedBackground = localStorage.getItem(bgKey) || undefined;

    return (
      <NoContent
        countdown={syncStore.retryCountdown}
        retryTargetAt={syncStore.retryTargetAt}
        isOnline={syncStore.isOnline}
        lastSyncTime={syncStore.lastSyncTime}
        error={syncStore.error}
        deviceId={authStore.deviceId}
        tenantName={authStore.tenantSlug}
        reason={reason}
        serverUrl={resolveApiBaseUrl()}
        registrationStatus={authStore.registrationStatus}
        backgroundUrl={cachedBackground}
      />
    );
  }

  const { currentMedia, isMuted, progress, error } = streamStore;

  // FIX 2 (2026-07-16) — esconde o <video> (visibility:hidden) enquanto o freeze
  // cobre a transição, SÓ em Tizen/webOS: lá o HW overlay do vídeo pisca preto ao
  // trocar de src e renderiza ACIMA do canvas de freeze (overlay HW ignora
  // z-index). Em Android/browser o z-index funciona e esconder poderia introduzir
  // flash → NUNCA esconde (gate tizen/webos). Revela junto com o hide do freeze
  // (mesmo commit React) → sem gap. visibility:hidden mantém decode/rVFC vivos.
  // EARLY FREEZE (Renato 2026-07-16): durante a janela do early-freeze (freeze já
  // pronto ATRÁS, vídeo ainda tocando seus últimos ~130ms), NÃO esconde o vídeo —
  // ele fica visível por cima (overlay HW) tocando até o fim → sem "retorno em
  // movimento". Só quando o vídeo ACABA (isEarlyFreeze → false, em handleVideoEnded)
  // o hide entra e revela o freeze na troca de src. Fora do early-freeze, comportamento
  // inalterado (esconde quando o freeze cobre a transição reativa).
  // PHASE 1 — durante o HOLD-FRAME (Tizen video→img) o `<video>` que SAI fica
  // VISÍVEL (segura o último frame no HW overlay) até a imagem pintar; então
  // `heldVideoHidden` o esconde (visibility:hidden, limpo). Isso SOBREPÕE o
  // `hideVideoForFreeze` normal (que poderia esconder o vídeo cedo demais se o canvas
  // de freeze estivesse visível). Fora do hold, comportamento inalterado — e como
  // Android/browser nunca ligam `heldVideoForImage`, lá é byte-idêntico ao de antes.
  const hideVideoForFreeze = heldVideoForImage
    ? heldVideoHidden
    : shouldHideVideoForFreeze(platform.platform, isFreezeFrameVisible) &&
      !isEarlyFreeze;
  /** Handlers do <video> ATIVO (o standby do motor agendado não recebe nenhum). */
  const activeVideoHandlers = {
    onLoadStart: handleLoadStart,
    onLoadedMetadata: handleLoadedData,
    onLoadedData: handleLoadedData,
    onEnded: handleVideoEnded,
    onError: handleVideoError,
    onCanPlay: handleCanPlay,
    onDurationChange: handleDurationChange,
    onWaiting: handleWaiting,
    onStalled: handleStalled,
    onPlaying: handlePlaying,
    onTimeUpdate: handleTimeUpdate,
    onSeeked: handleSeeked,
  };

  // Loop video when there's only 1 item in playlist
  const shouldLoop = deveRepetirNativamente({
    totalDeItens: progress.total,
    tipo: currentMedia.type,
    emGatilho: streamStore.isTriggerPlayback,
    modoDoGatilho: triggerStore.activeTrigger?.mode ?? null,
  });

  // URL resolvida (cache local ou, em miss, remoto) — o fallback remoto vive
  // DENTRO do `usePlayableUrl`, que sempre termina em cache-URL ou `media.url`.
  //
  // ⚠️ NÃO reintroduzir `|| currentMedia.url`: antes da 1ª resolução o hook
  // devolve `null`, e esse `||` pintava o `<img>` com a presigned CRUA do R2.
  // Como a assinatura rotaciona a cada sync, toda montagem virava uma URL
  // INÉDITA → fetch novo + mais uma cópia decodificada retida no renderer
  // (VA 32-bit sem teto no embedded). Sem o `||`, o gate `!isUrlLoading &&
  // mediaUrl` simplesmente não pinta até resolver. Vide cache-offline-first.md.
  const mediaUrl = playableUrl;

  return (
    <Container ref={containerRef} id="stream-container">
      {/* Touch interceptor - captures all touches/clicks to toggle MediaInfo */}
      <TouchInterceptor onClick={handleContainerClick} />

      {/* Interactive elements (buttons + QR codes) over content */}
      <InteractiveElementLayer />

      {/* Barra de progresso / countdown de kiosk — componente-folha isolado que
          roda o próprio rAF e escreve no DOM via ref, SEM re-renderizar o Stream
          (Renato 2026-07-05, gargalo do A7). Kiosk locked → badge discreto;
          unlocked → barra. Ele decide internamente qual renderizar. */}
      <PlaybackProgressBar
        mediaType={currentMedia.type}
        mediaId={currentMedia.id}
        imageDurationSec={mediaDuration}
        badgeTotalSec={currentMedia.duration ?? 0}
        startedAtMs={streamStore.currentItemStartedAt}
        videoRef={videoRef}
        loopControlRef={videoLoopControlRef}
        kioskLocked={kioskStore.isLocked}
      />

      {/* Offline badge when playing from cache while offline */}
      {syncStore.hasContent && !syncStore.isOnline && (
        <OfflineBadge>
          <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M23.64 7c-.45-.34-4.93-4-11.64-4-1.5 0-2.89.19-4.15.48L18.18 13.8 23.64 7zm-6.6 8.22L3.27 1.44 2 2.72l2.05 2.06C1.91 5.76.59 6.82.36 7l11.63 14.49.01.01.01-.01 3.9-4.86 3.32 3.32 1.27-1.27-3.46-3.46z" />
          </svg>
          OFFLINE
        </OfflineBadge>
      )}

      {/* Loading overlay while resolving URL — só mostra se o Canvas Bridge
          NÃO estiver ativo (freeze frame do vídeo anterior já cobre a tela).
          v1.0.71 — Trocado pra BlackTransition (preto puro, sem spinner/texto,
          sem fade). Renato pediu tela 100% preta entre mídias pra não parecer
          que o vídeo travou. */}
      {isUrlLoading && !isFreezeFrameVisible && !avplayActive && (
        <BlackTransition />
      )}

      {/* Multi-zone rendering: when current media has a zonaId AND zones exist,
          render N independent ContentViewports — one per zone. Otherwise single viewport. */}
      {syncStore.hasMultipleZones &&
      streamStore.currentMedia?.zonaId != null ? (
        (() => {
          const defaultLayoutW = syncStore.orientationWidth || 1920;
          const defaultLayoutH = syncStore.orientationHeight || 1080;

          const currentCampaignId = streamStore.currentMedia?.campanhaId;
          const activeZones = activeZonesForCampaign(
            syncStore.zones,
            streamStore.playlist,
            currentCampaignId,
          );

          const globalMaxOut = zoneGroupMaxDurationSec(
            streamStore.playlist,
            activeZones,
          );
          const globalLoopDuration =
            globalMaxOut > 0 ? globalMaxOut : undefined;

          return activeZones.map((zone) => {
            const zoneItems = streamStore.playlist.filter(
              (item) => item.zonaId === zone.id,
            );
            return (
              <ContentViewport
                key={zone.id}
                style={(() => {
                  const lw = zone.layoutWidth || defaultLayoutW;
                  const lh = zone.layoutHeight || defaultLayoutH;
                  const cw =
                    containerRef.current?.clientWidth || window.innerWidth;
                  const ch =
                    containerRef.current?.clientHeight || window.innerHeight;
                  // FIX (mesma regra do conteúdo normal): multi-zona usava
                  // Math.min hardcoded (contain) → letterbox preto quando o
                  // aspecto do canvas difere da tela, DIFERENTE do viewport
                  // único que segue o effectiveFitMode (default stretch =
                  // preenche). Agora escala X/Y independentes (stretch): o
                  // canvas do layout preenche a tela inteira e cada zona
                  // estica proporcionalmente junto — zero borda preta,
                  // consistente com as campanhas normais.
                  return {
                    ...computeStretchRect({
                      x: zone.posX,
                      y: zone.posY,
                      w: zone.width,
                      h: zone.height,
                      layoutWidth: lw,
                      layoutHeight: lh,
                      containerWidth: cw,
                      containerHeight: ch,
                    }),
                    zIndex: syncStore.zones.length - zone.zOrder,
                  };
                })()}
              >
                <ZonePlayer
                  playlist={zoneItems}
                  globalLoopDuration={globalLoopDuration}
                  onReady={() => handleZoneReady(activeZones.length)}
                />
              </ContentViewport>
            );
          });
        })()
      ) : (
        <ContentViewport
          style={
            syncStore.hasCustomOrientation
              ? (() => {
                  // FIX (fullscreen-crop-custom-orientation): mesma regra do
                  // conteúdo normal. ANTES usava orientationWidth/Height em
                  // px CRUS (ex.: 1440×2304) numa tela de 823×1317 CSS px
                  // (density 1.75) → transbordava e CORTAVA o fullscreen
                  // quando havia orientação customizada ativa. Agora escala
                  // X/Y independentes (stretch, igual ao viewport único e ao
                  // multi-zona pós-fix): preenche a tela inteira, sem corte
                  // e sem letterbox. screenOffset (LED) escala junto.
                  const lw = syncStore.orientationWidth!;
                  const lh = syncStore.orientationHeight!;
                  const cw =
                    containerRef.current?.clientWidth || window.innerWidth;
                  const ch =
                    containerRef.current?.clientHeight || window.innerHeight;
                  return computeStretchRect({
                    x: syncStore.screenOffsetX,
                    y: syncStore.screenOffsetY,
                    w: lw,
                    h: lh,
                    layoutWidth: lw,
                    layoutHeight: lh,
                    containerWidth: cw,
                    containerHeight: ch,
                  });
                })()
              : {
                  left: 0,
                  top: 0,
                  width: '100%',
                  height: '100%',
                }
          }
        >
          {currentMedia.type === 'stream' &&
          currentMedia.streamUrl &&
          !avplayActive ? (
            // Tablet/browser (NÃO-AVPlay): YouTube via iframe, HLS via <video>.
            // (TESTE 2026-07-31 confirmou: iframe do YouTube dá Error 153 na TV Tizen também na 9.0,
            //  não só na 6.5 → embed bloqueado por origem/webview. Na TV, YouTube só via AVPlay+HLS.)
            // Na TV (avplayActive) o stream é tocado pelo <AvplayVideo> UNIFICADO no ramo `else`
            // abaixo — MESMA key (`avplay-${gen}`) do vídeo. Assim o controlador AVPlay PERSISTE
            // através de vídeo↔stream↔imagem → toda troca vira SWITCH com StillMode (segura o
            // último frame durante o load) → ZERO preto na entrada/saída do stream. Antes a key
            // própria `avplay-stream-` remontava o componente → controlador novo → load 'init'
            // still=false → ~1.9s de preto (medido na bancada).
            (() => {
              const streamUrl = currentMedia.streamUrl!;
              const isYouTube = /(?:youtube\.com|youtu\.be)/.test(streamUrl);
              const videoId = isYouTube
                ? streamUrl.match(/(?:v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/)?.[1]
                : null;
              return (
                <>
                  {isYouTube && videoId ? (
                    <iframe
                      src={`https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&controls=0&showinfo=0&rel=0&iv_load_policy=3&modestbranding=1&playsinline=1`}
                      style={{
                        width: '100%',
                        height: '100%',
                        border: 'none',
                        position: 'absolute',
                        inset: 0,
                      }}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      onLoad={() => {
                        setIsImageLoading(false);
                        streamStore.setPlaying(true);
                        mediaVersionRef.current = streamStore.playlistVersion;
                        playingMediaIdRef.current =
                          streamStore.currentMedia?.id;
                        const baseDurationSeconds = Math.max(
                          1,
                          Math.round(currentMedia.duration || 30),
                        );
                        setMediaDuration(baseDurationSeconds);
                        const adjustedDuration =
                          baseDurationSeconds / playbackSpeed;
                        if (imageTimerRef.current)
                          clearTimeout(imageTimerRef.current);
                        imageTimerRef.current = setTimeout(() => {
                          streamStore.handleMediaEnd();
                        }, adjustedDuration * 1000);
                      }}
                    />
                  ) : (
                    <VideoPlayer
                      autoPlay
                      playsInline
                      loop={false}
                      muted
                      controls={false}
                      disablePictureInPicture
                      controlsList="nodownload nofullscreen noremoteplayback"
                      $objectFit={effectiveFitMode}
                      $hidden={hideVideoForFreeze}
                      style={{
                        width: '100%',
                        height: '100%',
                        position: 'absolute',
                        inset: 0,
                      }}
                      onEnded={handleVideoEnded}
                      onError={handleVideoError}
                      onCanPlay={handleCanPlay}
                    >
                      <source src={streamUrl} />
                    </VideoPlayer>
                  )}
                </>
              );
            })()
          ) : (
            <>
              {/* PHASE 1 — vídeo e imagem viram condicionais IRMÃOS (antes: dois ramos
                de um ternário). O `<video>` renderiza no slot de vídeo E, no HOLD-FRAME
                (Tizen/webOS video→img), também no slot de imagem, pra SEGURAR o último
                frame do vídeo que saiu. Ficando SEMPRE no mesmo índice de filho, React
                PRESERVA o nó (e o frame no HW overlay) através da troca. Fora do hold,
                `heldVideoForImage` é false (SEMPRE em Android/browser) → a condição do
                vídeo vira idêntica a `type === 'video'` e a de imagem a `type !== 'video'`
                (cobre image + audio, igual ao else antigo) → comportamento inalterado. */}
              {/* BACKEND AVPLAY (Fase 1): slot fullscreen de VÍDEO via AVPlay (só tizen +
                flag + sem fallback). Máscaras/QR/imagem DOM ficam por cima (S2). Erro →
                onFallback liga avplayFallback → cai pro <VideoPlayer> abaixo. */}
              {/* AVPlay UNIFICADO: MESMO plano/controlador pra VÍDEO + STREAM (HLS/RTSP) +
                image-hold (url=null). Key estável → o controlador PERSISTE através de
                vídeo↔stream↔imagem → toda troca vira SWITCH com StillMode (segura o frame no
                load) → ZERO preto na entrada/saída do stream. Stream YouTube (URL de página) →
                o guard do AvplayVideo dispara onUnplayable → pula o slot. */}
              {avplayActive && (
                <AvplayVideo
                  onSyncHandle={(h) => {
                    avplaySyncHandleRef.current = h;
                  }}
                  key={`avplay-${avplayGeneration}`}
                  mediaId={
                    currentMedia.type === 'video' ||
                    currentMedia.type === 'stream'
                      ? currentMedia.id
                      : null
                  }
                  url={
                    currentMedia.type === 'stream'
                      ? (currentMedia.streamUrl ?? null)
                      : currentMedia.type === 'video'
                        ? (playableUrl ?? '')
                        : null
                  }
                  durationOverrideSec={
                    currentMedia.type === 'stream'
                      ? (currentMedia.duration ?? null)
                      : currentMedia.type === 'video' &&
                          currentMedia.hasDurationOverride === true
                        ? (currentMedia.duration ?? null)
                        : null
                  }
                  loop={shouldLoop && currentMedia.type !== 'stream'}
                  isStream={currentMedia.type === 'stream'}
                  slotKey={streamStore.currentIndex}
                  onFirstFrame={handleAvplayFirstFrame}
                  onEnded={handleAvplayEnded}
                  onFallback={handleAvplayFallback}
                  onUnplayable={handleAvplayUnplayable}
                />
              )}
              {!avplayActive &&
                (currentMedia.type === 'video' || heldVideoForImage) && (
                  <>
                    {/* Elemento(s) de vídeo. Motor agendado = A/B: o ativo tem ref e handlers;
                        o standby carrega o próximo vídeo em silêncio e os papéis trocam em T. */}
                    {(abDisponivel
                      ? (['A', 'B'] as const)
                      : (['A'] as const)
                    ).map((slot) => {
                      const active =
                        !abDisponivel || slot === activeVideoSlot;
                      return (
                        <VideoPlayer
                          key={`video-${slot}`}
                          ref={active ? videoRef : standbyVideoRef}
                          $objectFit={effectiveFitMode}
                          $hidden={active ? hideVideoForFreeze : true}
                          data-content-id={active ? currentMedia.id : undefined}
                          data-video-slot={slot}
                          style={{
                            transform: 'none',
                            WebkitTransform: 'none',
                            zoom: 1,
                            touchAction: 'none',
                            maxWidth: '100%',
                            maxHeight: '100%',
                          }}
                          autoPlay={active}
                          playsInline
                          loop={active ? shouldLoop : false}
                          muted={active ? isMuted : true}
                          preload={active ? undefined : 'auto'}
                          controls={false}
                          disablePictureInPicture
                          controlsList="nodownload nofullscreen noremoteplayback"
                          crossOrigin={mediaCrossOriginProp(playbackSource)}
                          poster="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
                          {...(active ? activeVideoHandlers : {})}
                        >
                          {/* Source is set programmatically via video.src */}
                        </VideoPlayer>
                      );
                    })}
                    {isVideoLoading && !isFreezeFrameVisible && (
                      <BlackTransition />
                    )}
                    <VideoPreloader url={nextPreloadUrl} />
                  </>
                )}
              {/* Imagem / demais tipos não-vídeo (mesmo ramo `else` de antes:
                `type !== 'video'` cobre image + audio). No HOLD-FRAME pinta POR BAIXO
                do `<video>` segurado; ao carregar (handleImageLoad) o vídeo é escondido
                (visibility:hidden) e a imagem revelada → sem preto. */}
              {currentMedia.type !== 'video' &&
                currentMedia.type !== 'stream' && (
                  <>
                    {!isUrlLoading && mediaUrl && (
                      <ImagePlayer
                        ref={(img) => {
                          imgRef.current = img;
                          // BUGFIX (2026-06-26): slots consecutivos com a MESMA imagem
                          // (ex.: CENTRUM no wrap do loop 25→1, pois Bresenham a coloca
                          // em 1/13/25 e 25→1 ficam coladas) NÃO disparam `onLoad`: o
                          // `currentMedia` troca de objeto mas o `id` é o mesmo, então
                          // `usePlayableUrl` (keyed no id) devolve o MESMO blob → o
                          // `<img src>` fica idêntico → o browser não recarrega → onLoad
                          // não roda → `isImageLoading` trava em true → `BlackTransition`
                          // cobre a tela por 10s até o safety-net (tela preta em prod).
                          // Se o elemento já está `complete` (cacheado/src idêntico) e
                          // ainda estamos em loading, roda o handler manualmente. Guarda
                          // por `isImageLoading` → idempotente (só 1× por ciclo; src novo
                          // tem complete=false no commit → segue pelo onLoad normal).
                          if (
                            img &&
                            img.complete &&
                            img.naturalWidth > 0 &&
                            isImageLoading
                          ) {
                            handleImageLoad();
                          }
                        }}
                        $objectFit={effectiveFitMode}
                        $overHwPlane={avplayActive}
                        src={mediaUrl}
                        alt={currentMedia.name}
                        crossOrigin={mediaCrossOriginProp(playbackSource)}
                        onLoad={handleImageLoad}
                        onError={handleImageError}
                      />
                    )}
                    {(isImageLoading || isUrlLoading) &&
                      !isFreezeFrameVisible &&
                      !avplayActive && <BlackTransition />}
                  </>
                )}
            </>
          )}
        </ContentViewport>
      )}

      {/* SEAMLESS (2026-07-03, movido 2026-08-24): canvas de "último frame"
          PERSISTENTE — agora irmão dos DOIS ramos (multi-zona E viewport único),
          direto no Container. ANTES vivia DENTRO do ContentViewport do viewport
          único → durante um slot de ZONA o canvas ficava DESMONTADO
          (freezeFrameCanvasRef=null): a captura do composite no fim do slot
          falhava em silêncio e, na troca zona→fullscreen, o canvas remontava EM
          BRANCO → capa transparente → preto vazava por baixo = piscada que NÃO
          existia nas trocas comuns. Persistindo no Container, a MESMA regra de
          capa cobre todas as trocas (comum↔comum, zona↔comum). Segura o último
          frame do conteúdo anterior até o novo pintar de verdade
          (onPlaying/onLoad/handleZoneReady) → zero preto na transição. */}
      <canvas
        ref={freezeFrameCanvasRef}
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          objectFit: effectiveFitMode === 'stretch' ? 'fill' : effectiveFitMode,
          // Match do plano de vídeo Samsung (Tizen/webOS); 'none' em Android/browser.
          filter: freezeCanvasFilter,
          opacity: isFreezeFrameVisible ? 1 : 0,
          // SEAMLESS (2026-07-03) — MOSTRAR é INSTANTÂNEO (transition none): o
          // freeze precisa cobrir na hora, senão o fade-in de 100ms deixa o
          // conteúdo novo (preto carregando) vazar por baixo do canvas
          // semi-transparente = "piscada" no fim da imagem (bug reportado).
          // ESCONDER mantém fade de 100ms (crossfade suave revelando o novo).
          transition: isFreezeFrameVisible ? 'none' : 'opacity 100ms ease-out',
          // z50: acima de QUALQUER zona (zIndex das zonas = zones.length -
          // zOrder, que passaria de 5 com 6+ zonas) e abaixo do
          // BlackTransition (z100) — a capa nunca fica atrás do layout.
          zIndex: 50,
        }}
      />

      {/* MuteIndicator removido — em Android (kiosk retail) o autoplay com
          som é permitido via setMediaPlaybackRequiresUserGesture(false) no
          MainActivity, e volume é gerenciado server-side. O overlay "Mudo -
          Pressione OK" só fazia sentido em browser/web; em kiosk era ruído. */}

      {/* Fullscreen button — só em browser/web. Em Android o app já roda
          fullscreen via immersive mode (MainActivity), botão é desnecessário
          e visualmente intrusivo pro cliente final em loja. */}
      {platform.supportsFullscreen() && platform.platform !== 'android' && (
        <FullscreenButton
          $visible={showFullscreenButton}
          onClick={handleToggleFullscreen}
          aria-label={
            isFullscreen ? 'Sair do fullscreen' : 'Entrar em fullscreen'
          }
        >
          {isFullscreen ? <FullscreenExitIcon /> : <FullscreenEnterIcon />}
        </FullscreenButton>
      )}

      {/* Media info overlay - Toggle com H (browser), auto-hide 4s em release */}
      <MediaInfo $visible={showMediaInfo}>
        <p>
          Mídia:{' '}
          {loopStats.pattern.repetitions > 1 ? (
            <>
              <span>
                {loopStats.position.positionInPattern}/
                {loopStats.pattern.patternLength}
              </span>
              {' no padrão · Loop '}
              <span>
                {loopStats.position.currentLoopIter}/
                {loopStats.pattern.repetitions}
              </span>
              {' da hora'}
            </>
          ) : (
            <span>
              {progress.current}/{progress.total}
            </span>
          )}
          {' — '}Triggers{' '}
          <span>
            {Array.from(triggerStore.triggerDefinitions.values()).reduce(
              (acc, t) => acc + t.playlist.length,
              0,
            )}
          </span>
          {' — '}
          {triggerStore.isPlaying && triggerStore.activeTrigger ? (
            <>
              Ativo:{' '}
              <span style={{ color: '#f44336' }}>
                {triggerStore.activeTrigger.triggerName}
              </span>
            </>
          ) : (
            // 2026-05-29 — antes: "Desativado: 0" (0 hardcoded sem
            // semântica, confundia o operador). Agora: "Aguardando" em
            // cinza pra indicar que tem trigger configurado mas não está
            // disparado nesse momento. Quando dispara, vira "Ativo: nome".
            <span style={{ color: '#888' }}>Aguardando</span>
          )}
          {syncStore.hasContent &&
            schedulerStore.campaigns.length > 0 &&
            schedulerStore.campaigns.every((c) => c.playlist.length === 0) && (
              <span
                style={{
                  marginLeft: '8px',
                  color: '#ffa726',
                  fontSize: '0.9em',
                }}
              >
                (0 prog., última válida)
              </span>
            )}
        </p>
        <p>
          Título: <span>{currentMedia.name}</span>
        </p>
        {syncStore.hasMultipleZones &&
          currentMedia?.zonaId != null &&
          (() => {
            const activeZones = activeZonesForCampaign(
              syncStore.zones,
              streamStore.playlist,
              currentMedia.campanhaId,
            );
            const gMax = zoneGroupMaxDurationSec(
              streamStore.playlist,
              activeZones,
            );
            return (
              <p>
                Timeline:{' '}
                <span
                  style={{
                    color: '#4FC3F7',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {Math.floor(zoneTimelineSec / 60)}:
                  {String(zoneTimelineSec % 60).padStart(2, '0')}
                </span>
                {' / '}
                <span>
                  {`${Math.floor(gMax / 60)}:${String(Math.floor(gMax) % 60).padStart(2, '0')}`}
                </span>
                {' — '}
                <span>{activeZones.length} zonas</span>
              </p>
            );
          })()}
        <p>
          Tipo:{' '}
          <span>
            {currentMedia.type} ({orientationLabel})
          </span>
        </p>
        <p>
          Duração:{' '}
          <span>
            slot {currentMedia.duration}s
            {currentMedia.hasDurationOverride ? ' · override' : ' · natural'}
          </span>
          {' — Tier '}
          <span>{playbackStore.currentTier}</span>
        </p>
        <p>
          Fonte: <span>{getSourceLabel(playbackSource)}</span>
        </p>
        <p>
          Status:{' '}
          <span>
            {/*
              2026-06-13 — usar connectionState (probe gstatic.com via
              ConnectionMonitorService) em vez de isOnline (navigator.onLine).
              navigator.onLine mente no Android WebView — reportava "online"
              mesmo com WiFi desligado, fazendo o painel mostrar
              "Reproduzindo online" quando o tablet tá totalmente isolado.
              Estados de ConnectionMonitor:
                - 'online'             → internet + server OK
                - 'server_unreachable' → internet OK, server fora
                - 'degraded'           → conexão instável (lentidão/erros)
                - 'offline'            → sem internet de verdade
            */}
            {streamStore.isPlaying
              ? syncStore.connectionState === 'online'
                ? `Reproduzindo (online${
                    syncStore.lastHeartbeatTime
                      ? ' ' +
                        new Date(
                          syncStore.lastHeartbeatTime,
                        ).toLocaleTimeString()
                      : ''
                  })`
                : syncStore.connectionState === 'server_unreachable'
                  ? 'Reproduzindo (sem comunicação com servidor)'
                  : syncStore.connectionState === 'degraded'
                    ? 'Reproduzindo (rede degradada)'
                    : 'Reproduzindo (offline)'
              : 'Buffering...'}
          </span>
        </p>
        <p>
          Rede:{' '}
          <span>
            {deviceInfo?.networkName || 'N/A'} ({deviceInfo?.ipAddress || 'N/A'}
            )
          </span>
        </p>
        <p>
          ID: <span>{authStore.deviceId}</span>
        </p>
        <p>
          Server: <span>{resolveApiBaseUrl('N/A')}</span>
        </p>
        <p>
          Versão: <span>v{otaStore.currentVersion}</span>
          {' — Sync: '}
          <span>{syncStore.lastSyncTimeFormatted}</span>
          {' — Modo: '}
          <span>
            {ENV_FIT_MODE}
            {effectiveFitMode !== ENV_FIT_MODE &&
              ` (ativo: ${effectiveFitMode})`}
          </span>
        </p>
        {/* 2026-05-31 (v1.0.103) — Estado do kiosk REMOVIDO daqui: o
            painelzinho só abre quando kiosk está unlocked, então a linha só
            mostraria UNLOCKED — inútil. Movido pra admin via comando
            kiosk:get-state (RemoteOps). */}
        <p>
          Bat.:{' '}
          <span>
            {!isBatterySupported
              ? 'Não'
              : batteryStatus
                ? `${Math.round(batteryStatus.level * 100)}% ${
                    batteryStatus.charging ? 'Carregando' : 'Descarregando'
                  }`
                : 'N/A'}
          </span>
          {' Vol.: '}
          <span>
            {volumeLabel}
            {' (Def.: '}
            {defaultVolumeLabel}
            {')'}
          </span>
          {' Brilho: '}
          <span>{brightnessLabel}</span>
        </p>
        <p>
          Cache:{' '}
          <span>
            {cacheInfo.cached} cached, {cacheInfo.downloading} baixando,{' '}
            {cacheInfo.saving > 0 && `${cacheInfo.saving} salvando, `}
            {cacheInfo.pending} pendente
            {cacheInfo.errors > 0 && `, ${cacheInfo.errors} erros`}
          </span>
        </p>
        {cacheInfo.lastError && (
          <p
            style={{
              color: '#ff6b6b',
              fontSize: '0.85em',
              wordBreak: 'break-all',
              marginTop: '4px',
            }}
          >
            ⚠️ Último erro: {cacheInfo.lastError}
          </p>
        )}
        {/* ════════════════════════════════════════════════════════════════
           🔬 CACHE DIAG + 🗑️ REMOVIDOS — DESATIVADO em v1.0.83
           ════════════════════════════════════════════════════════════════
           Renato pediu pra esconder do painel após estabilização do download
           de cache em produção (2026-05-30). O bloco abaixo continua no
           código pra reativar facilmente em situação de troubleshooting:

           PARA REATIVAR:
             Troque o `{false &&` da linha abaixo por `{true &&` (ou remova
             a wrapper inteira mantendo só a condição original). Versão de
             diagnóstico vira a próxima minor — bumpar `package.json` +
             `config.xml` + `gradle.properties` + `tenants/efflux/config.json`,
             buildar, subir via OTA + criar rollout pro device-alvo (ou pra
             todos, dependendo da extensão do problema).

           O QUE VOLTA A APARECER quando reativado:
             1. 🔬 Cache Diag · backend: <indexeddb|filesystem|noop>
                - Cache size atual (X.XMB de YMB, %)
                - IDB quota nativa (se backend = indexeddb)
                - Attempts: X | ✅ Y | ❌ Z | avg Ts | reconnects: N
                - Últimas 5 ops (sucesso/timeout/erro com tempo)
             2. 🗑️ Removidos: contador de remoções de cache + última fonte

           Nada do código de telemetria (mediaStore.cacheStoreAttempts,
           recentIdbOps, etc) foi removido — só a renderização visual está
           comentada. Não há custo de runtime perceptível em manter o
           painel desligado.

           Vide v1.0.61 (onde foi adicionado) pra histórico.
           ════════════════════════════════════════════════════════════════ */}
        {SHOW_CACHE_DIAG && mediaStore.cacheStoreAttempts > 0 && (
          <div
            style={{
              backgroundColor: 'rgba(255, 193, 7, 0.08)',
              border: '1px solid rgba(255, 193, 7, 0.4)',
              padding: '4px 6px',
              borderRadius: '3px',
              marginTop: '6px',
              fontSize: '0.78em',
              color: '#ffd54f',
              fontFamily: 'monospace',
            }}
          >
            <p style={{ margin: 0, fontWeight: 'bold' }}>
              🔬 Cache Diag · backend:{' '}
              <span style={{ color: '#81c784' }}>{getCurrentCacheType()}</span>
            </p>
            {/* Tamanho REAL do cache vs quota configurada pelo app (não a
                quota mentirosa do navegador). 2026-05-28 task #17:
                navigator.storage.estimate em Tizen/Capacitor retorna
                ~166MB falso mesmo com unlimited storage privilege. A
                quota configurada (default 1GB, env VITE_MAX_CACHE_MB)
                reflete o limite real que o app se impõe. */}
            <p style={{ margin: '2px 0 0 0' }}>
              Cache:{' '}
              <span
                style={{
                  color:
                    mediaStore.configuredCachePercent > 80
                      ? '#ff5252'
                      : '#81c784',
                }}
              >
                {(mediaStore.totalCacheSize / (1024 * 1024)).toFixed(1)}MB de{' '}
                {mediaStore.configuredCacheQuotaMB}MB (
                {mediaStore.configuredCachePercent}%)
              </span>
              <span style={{ opacity: 0.6, marginLeft: '4px' }}>
                · {mediaStore.cachedItems.length} items
              </span>
            </p>
            {mediaStore.lastStorageEstimate &&
              getCurrentCacheType() === 'indexeddb' && (
                <p
                  style={{
                    margin: '2px 0 0 0',
                    opacity: 0.5,
                    fontSize: '0.85em',
                  }}
                >
                  IDB quota nativa:{' '}
                  <span>
                    {mediaStore.lastStorageEstimate.usageMB}MB /{' '}
                    {mediaStore.lastStorageEstimate.quotaMB}MB (
                    {mediaStore.lastStorageEstimate.percent}%)
                  </span>
                </p>
              )}
            <p style={{ margin: '2px 0 0 0' }}>
              Attempts: {mediaStore.cacheStoreAttempts} | ✅{' '}
              {mediaStore.cacheStoreSuccesses} | ❌{' '}
              {mediaStore.cacheStoreFailures}
              {mediaStore.cacheStoreSuccesses > 0 &&
                ` | avg ${Math.round(mediaStore.cacheStoreAvgSuccessDurationMs / 1000)}s`}
              {mediaStore.forceReconnectCount > 0 &&
                ` | reconnects: ${mediaStore.forceReconnectCount}`}
            </p>
            {mediaStore.recentIdbOps.length > 0 && (
              <div style={{ marginTop: '2px' }}>
                <p style={{ margin: 0, fontSize: '0.95em' }}>
                  Últimas ops (mais recente primeiro):
                </p>
                {mediaStore.recentIdbOps.slice(0, 5).map((op, idx) => (
                  <p
                    key={`${op.timestamp}-${idx}`}
                    style={{
                      margin: 0,
                      fontSize: '0.9em',
                      marginLeft: '8px',
                      color:
                        op.result === 'success'
                          ? '#81c784'
                          : op.result === 'timeout'
                            ? '#ff9800'
                            : '#ff5252',
                    }}
                  >
                    {op.result === 'success'
                      ? '✅'
                      : op.result === 'timeout'
                        ? '⏱️'
                        : '❌'}{' '}
                    {op.itemName} · {op.sizeMB}MB ·{' '}
                    {Math.round(op.durationMs / 1000)}s
                  </p>
                ))}
              </div>
            )}
          </div>
        )}
        {SHOW_CACHE_DIAG && cacheInfo.totalRemovals > 0 && (
          <p
            style={{
              color: '#ffa726',
              fontSize: '0.85em',
              wordBreak: 'break-all',
              marginTop: '4px',
            }}
          >
            🗑️ Removidos: {cacheInfo.totalRemovals} (último:{' '}
            {cacheInfo.lastRemovalCount} via{' '}
            <strong>{cacheInfo.lastRemovalSource}</strong>
            {cacheInfo.lastRemovalAt &&
              ` há ${Math.round((Date.now() - cacheInfo.lastRemovalAt) / 1000)}s`}
            )
          </p>
        )}
        {cacheInfo.checksumMismatchCount > 0 && (
          <p
            style={{
              color: '#ffd54f',
              fontSize: '0.85em',
              wordBreak: 'break-all',
              marginTop: '4px',
            }}
          >
            🔐 Checksums divergentes (servidor):{' '}
            {cacheInfo.checksumMismatchCount}
            {cacheInfo.lastChecksumMismatch &&
              ` — ${cacheInfo.lastChecksumMismatch}`}
          </p>
        )}
        {/* Caixa vermelha de crashes — movida em 2026-06-23 pra
            <CrashErrorBanner> separado (top-right, z-index 10003), pra
            ficar SEMPRE clicável por cima do LoopInfoPanel (Renato:
            botão Limpar ficava coberto pelo painel das campanhas). */}
        {cacheInfo.downloads.length > 0 && (
          <div style={{ marginTop: '8px', fontSize: '0.9em' }}>
            <p style={{ marginBottom: '4px', color: '#4fc3f7' }}>Baixando:</p>
            {cacheInfo.downloads.map((download) => {
              // Download terminou (100%) mas cache.store() ainda gravando.
              // Distinguir do "100% baixando" antigo (que parecia travado).
              const isSaving = download.progress >= 100;
              return (
                <p
                  key={download.id}
                  style={{ marginLeft: '16px', marginBottom: '2px' }}
                >
                  {isSaving
                    ? `💾 ${download.name}: salvando…`
                    : `📥 ${download.name}: ${download.progress}%`}
                </p>
              );
            })}
          </div>
        )}
        {cacheInfo.cachedFiles.length > 0 && (
          <div style={{ marginTop: '8px', fontSize: '0.9em' }}>
            <p style={{ marginBottom: '4px', color: '#4fc3f7' }}>
              Arquivos em cache:{' '}
              {(() => {
                // v1.0.67 (task #17 reabriu) — Renato reportou 28/05 que
                // mesmo após o fix anterior, o painel ainda mostrava "166
                // de 166 (100%)" em tablet e TV. Causa: este header usava
                // playlist total (soma de fileSize), não quota disponível
                // do player. Quando a playlist toda está cacheada,
                // cached === playlistTotal → 100% (correto mas inútil pro
                // operador que quer saber "quanto espaço o player ainda
                // tem pra salvar arquivos").
                //
                // Agora usa configuredCacheQuotaMB (default 1024MB, env
                // VITE_MAX_CACHE_MB) — mesma fonte do "Cache Diag" abaixo.
                // Operador vê de bate-pronto se cache está cheio ou
                // folgado (ex: "166MB de 1024MB (16%)" → 858MB livres).
                const cachedMB = (
                  mediaStore.totalCacheSize /
                  (1024 * 1024)
                ).toFixed(1);
                const quotaMB = mediaStore.configuredCacheQuotaMB;
                const percent = mediaStore.configuredCachePercent;
                const color = percent > 80 ? '#ff5252' : '#81c784';
                return (
                  <span style={{ color, fontWeight: 'normal' }}>
                    {cachedMB}MB de {quotaMB}MB ({percent}%)
                  </span>
                );
              })()}
            </p>
            {(() => {
              // 2026-06-23 (Renato): quando passa de 15 items, divide em 2
              // colunas pra não esticar verticalmente e empurrar o resto da
              // UI (controles de orientação, kiosk) pra fora da tela.
              const files = cacheInfo.cachedFiles;
              if (files.length <= 15) {
                return files.map((file) => (
                  <p
                    key={file.id}
                    style={{ marginLeft: '16px', marginBottom: '2px' }}
                  >
                    ✅ {file.name}
                    {lastFrameCache.hasStored(file.id) ? ' 🎞️' : ''}
                    {lastFrameWcCache.has(file.id) ? ' 🖼️' : ''}
                  </p>
                ));
              }
              const half = Math.ceil(files.length / 2);
              const left = files.slice(0, half);
              const right = files.slice(half);
              return (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '0 16px',
                    marginLeft: '16px',
                  }}
                >
                  <div>
                    {left.map((file) => (
                      <p key={file.id} style={{ marginBottom: '2px' }}>
                        ✅ {file.name}
                        {lastFrameCache.hasStored(file.id) ? ' 🎞️' : ''}
                        {lastFrameWcCache.has(file.id) ? ' 🖼️' : ''}
                      </p>
                    ))}
                  </div>
                  <div>
                    {right.map((file) => (
                      <p key={file.id} style={{ marginBottom: '2px' }}>
                        ✅ {file.name}
                        {lastFrameCache.hasStored(file.id) ? ' 🎞️' : ''}
                        {lastFrameWcCache.has(file.id) ? ' 🖼️' : ''}
                      </p>
                    ))}
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* CP3.5 (2026-05-16) — Info do loop pattern pra debug.
            Movido em 2026-06-23 pro <LoopInfoPanel> separado no canto
            superior direito (Renato — o bloco ficava muito grande dentro
            do MediaInfo e rolava a tela com kiosk OFF). */}

        {/* Speed control - Click to cycle, hold for 4x */}
        <SpeedControls>
          <p>Velocidade</p>
          <SpeedSliderContainer>
            <SpeedSlider
              type="range"
              min={1}
              max={4}
              step={1}
              value={playbackSpeed}
              onChange={handleSpeedChange}
              tabIndex={showMediaInfo ? 0 : -1}
              className="focusable"
            />
            <SpeedValue $active={playbackSpeed > 1}>
              {playbackSpeed}x
            </SpeedValue>
          </SpeedSliderContainer>
        </SpeedControls>

        {/* V059/V060 — Items filtrados pela JANELA PRÓPRIA DO CONTEÚDO
            (scheduleMode/weeklySchedule) OU pela JANELA DO GRUPO de
            players. Em cinza (disabled) com tooltip explicando o motivo.
            Operador abre o painel e entende imediatamente "cadê meu
            vídeo?". Só renderiza se houver items filtrados — não polui
            quando tudo OK. */}
        {contentWindowFilteredOut.length > 0 && (
          <div
            style={{
              marginTop: '12px',
              padding: '8px 10px',
              background: 'rgba(255, 167, 38, 0.08)',
              border: '1px solid rgba(255, 167, 38, 0.3)',
              borderRadius: 6,
              fontSize: '0.85em',
            }}
            title="Items presentes no sync mas filtrados por janela própria do conteúdo (V059) ou do grupo do player (V060)"
          >
            <p style={{ margin: '0 0 6px', color: '#ffa726', fontWeight: 600 }}>
              🕒 Fora da janela ({contentWindowFilteredOut.length})
            </p>
            <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
              {contentWindowFilteredOut.map((item) => (
                <li
                  key={item.id}
                  style={{
                    padding: '4px 0',
                    color: '#888',
                    opacity: 0.7,
                    textDecoration: 'line-through',
                    fontSize: '0.92em',
                  }}
                  title={item.reason}
                >
                  <span
                    style={{
                      marginRight: 6,
                      fontSize: '0.78em',
                      padding: '1px 5px',
                      borderRadius: 4,
                      background:
                        item.blockedBy === 'group'
                          ? 'rgba(100, 181, 246, 0.18)'
                          : 'rgba(255, 167, 38, 0.15)',
                      color: item.blockedBy === 'group' ? '#64b5f6' : '#ffa726',
                      textDecoration: 'none',
                      verticalAlign: 'middle',
                    }}
                  >
                    {item.blockedBy === 'group' ? 'GRUPO' : 'CONTEÚDO'}
                  </span>
                  <span style={{ color: '#aaa' }}>{item.name}</span>
                  <span
                    style={{
                      marginLeft: 8,
                      color: '#777',
                      fontSize: '0.85em',
                      fontStyle: 'italic',
                      textDecoration: 'none',
                      display: 'inline-block',
                    }}
                  >
                    — {item.reason}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Orientation selector buttons + Reativar Kiosk (inline compacto)
            2026-05-29 — antes o Reativar Kiosk vinha em bloco separado abaixo,
            ocupando largura total. Renato pediu pra encaixar inline depois do
            Retrato, com tamanho menor — ação rara, não merece prominent CTA. */}
        <OrientationButtons>
          <OrientationButton
            ref={landscapeButtonRef}
            $selected={effectiveOrientation === 'landscape'}
            onClick={() => handleOrientationChange('landscape')}
            tabIndex={showMediaInfo ? 0 : -1}
            className="focusable"
          >
            <LandscapeIcon />
            Paisagem
            {detectedOrientation === 'landscape' && <AutoBadge>Auto</AutoBadge>}
          </OrientationButton>
          <OrientationButton
            ref={portraitButtonRef}
            $selected={effectiveOrientation === 'portrait'}
            onClick={() => handleOrientationChange('portrait')}
            tabIndex={showMediaInfo ? 0 : -1}
            className="focusable"
          >
            <PortraitIcon />
            Retrato
            {detectedOrientation === 'portrait' && <AutoBadge>Auto</AutoBadge>}
          </OrientationButton>
          {customOrientations.length > 0 && (
            <div style={{ position: 'relative' }}>
              <OrientationButton
                $selected={false}
                onClick={() => setShowCustomSelector(!showCustomSelector)}
                tabIndex={showMediaInfo ? 0 : -1}
                className="focusable"
              >
                <svg
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ width: 20, height: 20, fill: 'currentColor' }}
                >
                  <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14zm-7-2h2v-4h4v-2h-4V7h-2v4H7v2h4v4z" />
                </svg>
                Custom
              </OrientationButton>
              {showCustomSelector && (
                <div
                  style={{
                    position: 'absolute',
                    bottom: '100%',
                    left: 0,
                    marginBottom: '8px',
                    background: 'rgba(30, 30, 50, 0.95)',
                    border: '2px solid rgba(255, 255, 255, 0.15)',
                    borderRadius: '8px',
                    padding: '6px 0',
                    minWidth: '220px',
                    zIndex: 10002,
                    backdropFilter: 'blur(10px)',
                  }}
                >
                  {customOrientations.map((o) => (
                    <button
                      key={o.id}
                      onClick={() => handleCustomOrientationSelect(o.id)}
                      style={{
                        display: 'block',
                        width: '100%',
                        padding: '8px 16px',
                        background: 'transparent',
                        border: 'none',
                        color: '#e0e0e0',
                        fontSize: '14px',
                        textAlign: 'left',
                        cursor: 'pointer',
                        transition: 'background 150ms',
                      }}
                      onMouseEnter={(e) => {
                        (e.target as HTMLButtonElement).style.background =
                          'rgba(33, 150, 243, 0.2)';
                      }}
                      onMouseLeave={(e) => {
                        (e.target as HTMLButtonElement).style.background =
                          'transparent';
                      }}
                      tabIndex={showMediaInfo ? 0 : -1}
                      className="focusable"
                    >
                      {o.name}
                      {o.width && o.height ? (
                        <span
                          style={{
                            color: '#888',
                            fontSize: '12px',
                            marginLeft: '6px',
                          }}
                        >
                          ({o.width}&times;{o.height})
                        </span>
                      ) : null}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          {/* 2026-05-31 (v1.0.101) — Botão "Reativar Kiosk" agora aparece
              sempre que NÃO está locked. Antes exigia `isServerEnabled=true`
              também, o que escondia o botão quando admin desativava kiosk
              remotamente (impedindo recovery local pela equipe de campo).
              O lock() agora força serverEnabled=true se necessário. */}
          {!kioskStore.isLocked && (
            <OrientationButton
              ref={reactivateKioskButtonRef}
              $selected={focusedButton === 'reactivate-kiosk'}
              onClick={() => {
                kioskStore.lock();
                setShowMediaInfo(false);
              }}
              style={{
                padding: '8px 12px',
                fontSize: '13px',
                gap: '4px',
                background: 'rgba(244, 67, 54, 0.15)',
                borderColor: '#f44336',
                color: '#f44336',
              }}
              tabIndex={showMediaInfo ? 0 : -1}
              className="focusable"
              title="Reativar modo kiosk (bloquear app)"
            >
              🔒 Reativar Kiosk
            </OrientationButton>
          )}
        </OrientationButtons>
      </MediaInfo>

      {/* Loop & Impressões — painel separado no canto superior direito.
          Extraído do MediaInfo em 2026-06-23 (Renato): com playlists grandes
          o bloco esticava o MediaInfo verticalmente e empurrava controles
          (orientação + reativar kiosk) pra fora da tela com kiosk OFF. */}
      {loopStats.pattern.repetitions > 1 && (
        <LoopInfoPanel $visible={showMediaInfo}>
          <p style={{ marginBottom: '4px', color: '#4fc3f7' }}>
            Loop & Impressões:
          </p>
          <p style={{ marginLeft: '16px', marginBottom: '2px' }}>
            Padrão: <span>{loopStats.pattern.patternLength}</span> slots ·{' '}
            <span>{formatLoopTime(loopStats.pattern.patternDurationS)}</span>
            {' · repete '}
            <span>{loopStats.pattern.repetitions}×</span> na hora
          </p>
          <p style={{ marginLeft: '16px', marginBottom: '2px' }}>
            Hora atual:{' '}
            <span>
              {progress.current}/{progress.total}
            </span>{' '}
            absoluto ·{' '}
            <span>{formatLoopTime(loopStats.pattern.totalDurationS)}</span>
            {' total'}
          </p>
          {loopStats.campaigns.map((camp) => (
            <p
              key={camp.campanhaId}
              style={{ marginLeft: '16px', marginBottom: '2px' }}
            >
              · {camp.campanhaName || `Campanha #${camp.campanhaId}`}:{' '}
              <span>{camp.countPerLoop}</span>/loop ·{' '}
              <span>{camp.countTotal}</span>/h
            </p>
          ))}
        </LoopInfoPanel>
      )}

      {/* Crashes capturados — banner separado no canto superior direito,
          z-index 10003 (acima do LoopInfoPanel) pra garantir que o botão
          "↑ Limpar" sempre fique clicável, mesmo com playlists grandes. */}
      {cacheInfo.crashCount > 0 && (
        <CrashErrorBanner $visible={showMediaInfo}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '8px',
            }}
          >
            <p style={{ color: '#fff', fontWeight: 'bold' }}>
              💥 Crashes capturados (boots anteriores): {cacheInfo.crashCount}
            </p>
            <button
              type="button"
              onClick={() => {
                clearCrashes();
                setCacheInfo((prev) => ({
                  ...prev,
                  crashCount: 0,
                  lastCrashMessage: null,
                  lastCrashAt: null,
                  lastCrashStack: null,
                }));
              }}
              style={{
                background: 'transparent',
                color: '#fff',
                border: '1px solid #fff',
                borderRadius: '3px',
                padding: '4px 10px',
                fontSize: '0.9em',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
              title="Atalho: ↑ no controle remoto"
            >
              ↑ Limpar
            </button>
          </div>
          {cacheInfo.lastCrashMessage && (
            <p
              style={{
                color: '#fff',
                margin: '4px 0 0 0',
                wordBreak: 'break-all',
                fontSize: '0.85em',
              }}
            >
              Último: {cacheInfo.lastCrashMessage}
              {cacheInfo.lastCrashAt &&
                ` (há ${Math.round((Date.now() - cacheInfo.lastCrashAt) / 1000)}s)`}
            </p>
          )}
          {cacheInfo.lastCrashStack && (
            <pre
              style={{
                color: '#fff',
                margin: '4px 0 0 0',
                fontSize: '0.8em',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
                maxHeight: '120px',
                overflow: 'auto',
              }}
            >
              {cacheInfo.lastCrashStack}
            </pre>
          )}
        </CrashErrorBanner>
      )}

      {/* Error overlay */}
      {error && (
        <ErrorOverlay>
          <ErrorText>{error}</ErrorText>
          <LoadingText>Avançando para próxima mídia...</LoadingText>
        </ErrorOverlay>
      )}
    </Container>
  );
});

export default Stream;
